import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  IconButton,
  Checkbox,
  Menu,
  MenuItem,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  CircularProgress,
  LinearProgress,
  Drawer,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  InputAdornment,
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Restaurant,
  TableRestaurant,
  MenuBook,
  ShoppingCart,
  People,
  Settings,
  Notifications,
  AccountCircle,
  Logout,
  PointOfSale,
  Receipt,
  TrendingUp,
  Store,
  Business,
  Domain,
  Apartment,
  SwapHoriz,
  DarkMode,
  LightMode,
  ChevronLeft,
  ChevronRight,
  MenuOpen,
  Search,
  BarChart,
  CalendarMonth,
  Inventory,
  PersonAdd,
  Edit,
  Delete,
  ToggleOn,
  ToggleOff,
  AddBusiness,
  QrCode,
  Sell,
  Category,
  AddCircleOutline,
  DesignServices,
  Kitchen,
  ThumbUpAlt,
  CancelOutlined,
  Refresh,
  ManageAccounts,
  LocalOffer,
  AttachMoney,
  StarRate,
  FastfoodOutlined,
  PhoneAndroid,
  SmartToy,
  CreditCard,
  Close,
  DeliveryDining,
  ExpandMore,
  CalendarToday,
  Visibility,
  VisibilityOff,
  PhotoCamera,
  CloudUpload,
  AutoAwesome,
  CheckCircleOutline,
  ImageSearch,
  RecordVoiceOver,
} from '@mui/icons-material';

import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Bar, BarChart as RechartsBarChart, Cell, Legend as RechartsLegend } from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ChatbotFAB from './ChatbotFAB';
import { useThemeModeContext } from '../hooks/useThemeMode';
import { UserRole, UserStatus, Tenant, User, PricingPlan, Table, MenuItem as MenuItemType, MenuCategory, Restaurant as RestaurantType, TableShape, TableStatus, SubscriptionPayment } from '../types';
import apiService from '../services/api';
import { useNetwork } from '../contexts/NetworkContext';
import { localDb } from '../services/localDb';
import { CloudDone, CloudOff, Sync as SyncIcon } from '@mui/icons-material';
import { colors, shadows, neu, brand, food } from '../theme';
import FloatingShapes from './common/FloatingShapes';

// Lazy-loaded section components — code only downloads when user first opens that section
const PricingPlansContent = React.lazy(() => import('./PricingPlansContent'));
const TableDesigner = React.lazy(() => import('./TableDesigner'));
const ReportsSection = React.lazy(() => import('./ReportsSection'));
const KDSPage = React.lazy(() => import('./KDSPage'));
const StaffManagementSection = React.lazy(() => import('./StaffManagementSection'));
const CouponManagementSection = React.lazy(() => import('./CouponManagementSection'));
const GSTSection = React.lazy(() => import('./GSTSection'));
const ExpenseSection = React.lazy(() => import('./ExpenseSection'));
const InventorySection = React.lazy(() => import('./InventorySection'));
const PaymentGatewaySetup = React.lazy(() => import('./PaymentGatewaySetup'));
const FeedbackSection = React.lazy(() => import('./FeedbackSection'));
const ChatbotSection = React.lazy(() => import('./ChatbotSection'));
const OrderFlowSettings = React.lazy(() => import('./OrderFlowSettings'));
const ComboManagementSection = React.lazy(() => import('./ComboManagementSection'));
const SettingsSection = React.lazy(() => import('./SettingsSection'));
const DeliveryIntegrationSetup = React.lazy(() => import('./DeliveryIntegrationSetup'));
const TTSTemplateSection = React.lazy(() => import('./TTSTemplateSection'));

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;
const IMAGE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5134/api').replace('/api', '');

const Dashboard: React.FC = () => {
  const { user, logout, currentTenant, currentRestaurant, switchTenant, switchRestaurant } = useAuth();
  const { mode, toggleMode, isDark: globalIsDark } = useThemeModeContext();
  const { isOnline, forceOffline, toggleMode: toggleNetworkMode } = useNetwork();
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const checkUnsyncedOrders = async () => {
    try {
      const pending = await localDb.getPendingOrders();
      setUnsyncedCount(pending.length);
    } catch {
      setUnsyncedCount(0);
    }
  };

  useEffect(() => {
    checkUnsyncedOrders();
    const interval = setInterval(checkUnsyncedOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const runOfflineSync = async () => {
    if (!isOnline || syncing) return;
    setSyncing(true);
    try {
      const pending = await localDb.getPendingOrders();
      if (pending.length > 0) {
        setSnackbar({ open: true, message: `Syncing ${pending.length} offline orders...`, severity: 'info' as any });
        for (const order of pending) {
          try {
            const { isOffline, paymentStatus, paymentMethod, paidAmount, paymentTenderedAmount, paymentTransactionReference, ...cleanOrder } = order;
            const serverOrder = await apiService.createOrder(cleanOrder);
            const newOrderId = serverOrder?.id;

            if (paymentStatus === 'Paid' && newOrderId) {
              await apiService.processPayment(newOrderId, {
                method: paymentMethod,
                amount: paidAmount,
                tenderedAmount: paymentTenderedAmount,
                transactionId: paymentTransactionReference,
              });
            }

            await localDb.removePendingOrder(order.id);
          } catch (err) {
            console.error('Failed to sync offline order:', order.id, err);
          }
        }
        await checkUnsyncedOrders();
        setSnackbar({ open: true, message: 'Offline orders synchronized successfully!', severity: 'success' });
      }
    } catch (err) {
      console.error('Offline sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (isOnline && unsyncedCount > 0) {
      runOfflineSync();
    }
  }, [isOnline, unsyncedCount]);

  // ── Favorites state ──────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    const rId = currentRestaurant?.id || '';
    const stored = localStorage.getItem(`menumaster.favorites.${rId}`);
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse favorites:', e);
      }
    }
    return new Set<string>();
  });

  const toggleFavorite = (itemId: string) => {
    const rId = currentRestaurant?.id || '';
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      localStorage.setItem(`menumaster.favorites.${rId}`, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  useEffect(() => {
    const rId = currentRestaurant?.id || '';
    if (!rId) return;
    const stored = localStorage.getItem(`menumaster.favorites.${rId}`);
    if (stored) {
      try {
        setFavorites(new Set(JSON.parse(stored)));
      } catch (e) {
        console.error('Failed to parse favorites:', e);
        setFavorites(new Set());
      }
    } else {
      setFavorites(new Set());
    }
  }, [currentRestaurant]);
  const [dashboardTheme, setDashboardTheme] = useState<'dark' | 'forest' | 'glass'>(() => {
    return (localStorage.getItem('dashboard-theme') as 'dark' | 'forest' | 'glass') || 'dark';
  });

  const isDark = dashboardTheme === 'forest' ? false : true;

  useEffect(() => {
    localStorage.setItem('dashboard-theme', dashboardTheme);
    const root = document.documentElement;
    root.classList.remove('theme-dark', 'theme-forest', 'theme-glass');
    root.classList.add(`theme-${dashboardTheme}`);
    if (dashboardTheme === 'forest') {
      root.setAttribute('data-theme', 'light');
    } else {
      root.setAttribute('data-theme', 'dark');
    }
  }, [dashboardTheme]);

  const [themeAnchorEl, setThemeAnchorEl] = useState<null | HTMLElement>(null);
  const handleThemeMenuOpen = (event: React.MouseEvent<HTMLElement>) => setThemeAnchorEl(event.currentTarget);
  const handleThemeMenuClose = () => setThemeAnchorEl(null);
  const selectTheme = (themeName: 'dark' | 'forest' | 'glass') => {
    setDashboardTheme(themeName);
    handleThemeMenuClose();
  };

  const { lang: appLang, setLang: setAppLang, t } = useLanguage();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tenantAnchorEl, setTenantAnchorEl] = useState<null | HTMLElement>(null);
  const [restaurantAnchorEl, setRestaurantAnchorEl] = useState<null | HTMLElement>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [restaurants, setRestaurants] = useState<RestaurantType[]>([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(['operations', 'management', 'delivery', 'analytics'])
  );
  const toggleCategory = (catId: string) =>
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      next.has(catId) ? next.delete(catId) : next.add(catId);
      return next;
    });
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Dialog states
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserForm, setEditUserForm] = useState({ id: '', firstName: '', lastName: '', username: '', email: '', phone: '', password: '', role: 'Admin', tenantId: '' });
  const [createRestaurantDialogOpen, setCreateRestaurantDialogOpen] = useState(false);
  const [editTenantDialogOpen, setEditTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', firstName: '', lastName: '', role: 'Admin', tenantId: '' });
  const [newRestaurant, setNewRestaurant] = useState({ name: '', restaurantCode: '', domain: '', planId: '', planExpiry: '', adminEmail: '', adminUsername: '', adminPassword: '', adminFirstName: '', adminLastName: '' });
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [subscriptionPayments, setSubscriptionPayments] = useState<SubscriptionPayment[]>([]);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [globalGateways, setGlobalGateways] = useState<any[]>([]);
  const [loadingGlobalGateways, setLoadingGlobalGateways] = useState(false);
  const [globalGatewayDialogOpen, setGlobalGatewayDialogOpen] = useState(false);
  const [globalGatewayForm, setGlobalGatewayForm] = useState({
    gatewayName: '',
    displayName: '',
    isTestMode: true,
    apiKey: '',
    apiSecret: '',
    merchantId: '',
    saltIndex: '1'
  });
  const [renewPlanDialogOpen, setRenewPlanDialogOpen] = useState(false);
  const [renewingTenant, setRenewingTenant] = useState<Tenant | null>(null);
  const [payPlanStep, setPayPlanStep] = useState<1 | 2>(1);
  const [renewPlanForm, setRenewPlanForm] = useState({
    planId: '',
    billingCycle: 'monthly',
    paymentMethod: 'upi',
    amount: 0,
    transactionId: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [showEditUserPassword, setShowEditUserPassword] = useState(false);
  
  // Tables state
  const [tables, setTables] = useState<Table[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [createTableDialogOpen, setCreateTableDialogOpen] = useState(false);
  const [qrDialogTable, setQrDialogTable] = useState<Table | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const [editTableDialogOpen, setEditTableDialogOpen] = useState(false);
  const [visualDesignerDialogOpen, setVisualDesignerDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [newTable, setNewTable] = useState({
    tableNumber: '',
    capacity: 4,
    location: '',
    isActive: true,
    tableName: '',
    shape: TableShape.Rectangle,
    xPosition: 0,
    yPosition: 0,
    width: 100,
    height: 100,
    rotation: 0
  });
  
  // Menu state
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [loadingMenuCategories, setLoadingMenuCategories] = useState(false);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [createCategoryDialogOpen, setCreateCategoryDialogOpen] = useState(false);
  const [createItemDialogOpen, setCreateItemDialogOpen] = useState(false);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItemType | null>(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', displayOrder: 0, isActive: true });
  const [newItem, setNewItem] = useState({
    name: '', shortcutName: '', description: '', price: 0, categoryId: '', isAvailable: true, preparationTime: 15, isVegetarian: true, isSpicy: false, tags: [] as string[], imageFile: null as File | null,
    sizes: [] as { name: string; price: number }[],
    isHappyHourEnabled: false, happyHourPrice: 0, happyHourStart: '12:00', happyHourEnd: '15:00', happyHourDays: 'Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday'
  });
  const [editItemImageFile, setEditItemImageFile] = useState<File | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MenuItemType | null>(null);
  
  // OCR states
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [ocrFile, setOcrFile] = useState<File | null>(null);
  const [ocrImagePreview, setOcrImagePreview] = useState<string | null>(null);
  const [ocrScanning, setOcrScanning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStage, setOcrStage] = useState('');
  const [ocrResults, setOcrResults] = useState<{ id: string; category: string; name: string; price: number; description: string; isVegetarian: boolean; isSpicy: boolean; sizeName?: string | null; tag?: string | null; selected: boolean }[]>([]);
  const [ocrLanguage, setOcrLanguage] = useState<'en' | 'hi'>('en');
  // 'preview' renders the scanned items as a styled menu card (mirrors the
  // uploaded photo for easy visual verification); 'edit' is the field list.
  const [ocrViewMode, setOcrViewMode] = useState<'preview' | 'edit'>('preview');

  // Animated progress while OCR runs (backend gives no streaming progress, so we
  // ramp smoothly to ~92% and snap to 100% on completion).
  useEffect(() => {
    if (!ocrScanning) return;
    setOcrProgress(0);
    const stages = [
      { p: 15, label: 'Uploading image…' },
      { p: 35, label: 'Enhancing & cleaning image…' },
      { p: 60, label: 'Reading text (OCR)…' },
      { p: 80, label: 'Detecting items, prices & categories…' },
      { p: 92, label: 'Finalizing…' },
    ];
    let i = 0;
    setOcrStage(stages[0].label);
    const timer = setInterval(() => {
      setOcrProgress(prev => {
        const target = stages[Math.min(i, stages.length - 1)].p;
        setOcrStage(stages[Math.min(i, stages.length - 1)].label);
        if (prev >= target) { i = Math.min(i + 1, stages.length - 1); }
        const next = prev + Math.max(1, (target - prev) * 0.18);
        return next > 92 ? 92 : next;
      });
    }, 220);
    return () => clearInterval(timer);
  }, [ocrScanning]);

  // Excel states
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelResults, setExcelResults] = useState<{ id: string; category: string; name: string; price: number; description: string; selected: boolean }[]>([]);

  // Approval queue state
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [loadingPendingOrders, setLoadingPendingOrders] = useState(false);
  const [approvalBusy, setApprovalBusy] = useState<Set<string>>(new Set());

  // Real orders list states
  const [ordersList, setOrdersList] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Sales chart data for dashboard home
  const [salesChartData, setSalesChartData] = useState<{ day: string; revenue: number; orders: number }[]>([]);
  const [salesChartLoading, setSalesChartLoading] = useState(false);
  const [showSalesGraph, setShowSalesGraph] = useState(false);
  const [salesGraphTab, setSalesGraphTab] = useState<'daily' | 'items'>('daily');
  const [topItemsData, setTopItemsData] = useState<{ name: string; quantity: number; revenue: number }[]>([]);
  const [topItemsLoading, setTopItemsLoading] = useState(false);
  const [dailyItemsData, setDailyItemsData] = useState<any[]>([]);
  const [topItemNames, setTopItemNames] = useState<string[]>([]);
  const [dailyItemsLoading, setDailyItemsLoading] = useState(false);

  // Revenue section state (SuperAdmin)
  const [allRestaurants, setAllRestaurants] = useState<RestaurantType[]>([]);
  const [restaurantRevenues, setRestaurantRevenues] = useState<Record<string, { revenue: number; orderCount: number; loading: boolean }>>({});
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  // Plan features state for active restaurant
  const [planFeatures, setPlanFeatures] = useState<{
    HasPOS: boolean;
    HasInventory: boolean;
    HasCRM: boolean;
    HasOnlineOrdering: boolean;
    HasDelivery: boolean;
    HasKDS: boolean;
    HasReports: boolean;
    CanAdjustSales: boolean;
    MaxMenuItems: number;
    MaxTables: number;
    MaxUsers: number;
    PlanName?: string;
  }>(() => {
    const cached = localStorage.getItem('cached_plan_features');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        console.error('Failed to parse cached plan features:', e);
      }
    }
    return {
      HasPOS: false,
      HasInventory: false,
      HasCRM: false,
      HasOnlineOrdering: false,
      HasDelivery: false,
      HasKDS: false,
      HasReports: false,
      MaxMenuItems: 0,
      MaxTables: 0,
      MaxUsers: 0,
      PlanName: 'RESTAURANT PLATFORM'
    };
  });

  // Stable callback shared across all section components — avoids re-renders from inline arrow functions
  const handleNotify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  useEffect(() => {
    const loadPlanFeatures = async () => {
      if (user?.role === UserRole.SuperAdmin) {
        // SuperAdmin has access to all features bypass
        const adminFeatures = {
          HasPOS: true,
          HasInventory: true,
          HasCRM: true,
          HasOnlineOrdering: true,
          HasDelivery: true,
          HasKDS: true,
          HasReports: true,
          CanAdjustSales: true,
          MaxMenuItems: 999999,
          MaxTables: 999999,
          MaxUsers: 999999,
          PlanName: 'SUPER ADMIN'
        };
        setPlanFeatures(adminFeatures);
        localStorage.setItem('cached_plan_features', JSON.stringify(adminFeatures));
        return;
      }

      if (currentRestaurant?.id) {
        try {
          const accessData = await apiService.checkModuleAccess(currentRestaurant.id);
          if (accessData && accessData.features) {
            const features = {
              HasPOS: accessData.features.HasPOS ?? accessData.features.hasPOS ?? false,
              HasInventory: accessData.features.HasInventory ?? accessData.features.hasInventory ?? false,
              HasCRM: accessData.features.HasCRM ?? accessData.features.hasCRM ?? false,
              HasOnlineOrdering: accessData.features.HasOnlineOrdering ?? accessData.features.hasOnlineOrdering ?? false,
              HasDelivery: accessData.features.HasDelivery ?? accessData.features.hasDelivery ?? false,
              HasKDS: accessData.features.HasKDS ?? accessData.features.hasKDS ?? false,
              HasReports: accessData.features.HasReports ?? accessData.features.hasReports ?? false,
              CanAdjustSales: accessData.features.CanAdjustSales ?? accessData.features.canAdjustSales ?? false,
              MaxMenuItems: accessData.plan?.maxMenuItems ?? accessData.plan?.MaxMenuItems ?? 0,
              MaxTables: accessData.plan?.maxTables ?? accessData.plan?.MaxTables ?? 0,
              MaxUsers: accessData.plan?.maxUsers ?? accessData.plan?.MaxUsers ?? 0,
              PlanName: accessData.plan?.name ?? accessData.plan?.Name ?? 'RESTAURANT PLATFORM'
            };
            setPlanFeatures(features);
            localStorage.setItem('cached_plan_features', JSON.stringify(features));
          }
        } catch (error) {
          console.error('Failed to load plan features:', error);
          // Secure-by-default: keep the existing state (loaded from cache) on API failure,
          // avoiding wide-open feature leaks.
        }
      }
    };

    loadPlanFeatures();
  }, [currentRestaurant, user]);

  useEffect(() => {
    if (user?.role === UserRole.SuperAdmin) {
      loadTenants();
      loadUsers();
      loadPricingPlans();
    }
  }, [user]);

  useEffect(() => {
    if (activeSection === 'admins' && user?.role === UserRole.SuperAdmin) {
      loadUsers();
    }
  }, [activeSection]);

  // Reload tenants when the Create Admin dialog opens (in case they haven't loaded yet)
  useEffect(() => {
    if (createUserDialogOpen && user?.role === UserRole.SuperAdmin && tenants.length === 0) {
      loadTenants();
    }
  }, [createUserDialogOpen]);

  useEffect(() => {
    const section = searchParams.get('section');
    if (section) {
      setActiveSection(section);
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeSection === 'tenants') {
      loadTenants();
      // Also load pricing plans so plan names can be shown in the tenant grid
      if (pricingPlans.length === 0) loadPricingPlans();
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'tables' && currentRestaurant?.id) {
      loadTables();
    }
  }, [activeSection, currentRestaurant]);

  useEffect(() => {
    if (activeSection === 'approval' && currentRestaurant?.id) {
      loadPendingOrders();
    }
  }, [activeSection, currentRestaurant]);

  useEffect(() => {
    if (activeSection === 'orders' && currentRestaurant?.id) {
      loadRealOrders();
    }
  }, [activeSection, currentRestaurant]);

  useEffect(() => {
    if (activeSection === 'dashboard' && showSalesGraph && currentRestaurant?.id && user?.role !== UserRole.SuperAdmin) {
      (async () => {
        setSalesChartLoading(true);
        try {
          const orders = await apiService.getOrders(currentRestaurant.id);
          const today = new Date();
          const days: { day: string; revenue: number; orders: number }[] = [];
          for (let i = 6; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const label = d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit' });
            const dateStr = d.toISOString().slice(0, 10);
            const dayOrders = (orders || []).filter((o: any) => {
              const od = o.orderDate ?? o.createdAt ?? '';
              return od.slice(0, 10) === dateStr && o.status !== 'Cancelled';
            });
            days.push({
              day: label,
              revenue: dayOrders.reduce((sum: number, o: any) => sum + (o.totalAmount ?? o.finalAmount ?? 0), 0),
              orders: dayOrders.length,
            });
          }
          setSalesChartData(days);
        } catch {
          setSalesChartData([]);
        } finally {
          setSalesChartLoading(false);
        }
      })();
    }
  }, [activeSection, currentRestaurant, showSalesGraph]);

  useEffect(() => {
    if (activeSection === 'dashboard' && showSalesGraph && salesGraphTab === 'items' && currentRestaurant?.id && user?.role !== UserRole.SuperAdmin) {
      (async () => {
        setTopItemsLoading(true);
        try {
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - 29);
          const fmt = (d: Date) => d.toISOString().slice(0, 10);
          const data = await apiService.getTopSellingItems(currentRestaurant.id, fmt(start), fmt(end), 10);
          setTopItemsData(
            (data || []).map((d: any) => ({
              name: String(d.name ?? ''),
              quantity: Number(d.quantity ?? 0),
              revenue: Number(d.revenue ?? 0),
            }))
          );
        } catch {
          setTopItemsData([]);
        } finally {
          setTopItemsLoading(false);
        }
      })();
    }
  }, [activeSection, currentRestaurant, showSalesGraph, salesGraphTab]);

  // Daily breakdown per item — fires after 30-day top-items data is loaded
  useEffect(() => {
    if (
      activeSection === 'dashboard' && showSalesGraph && salesGraphTab === 'items' &&
      currentRestaurant?.id && user?.role !== UserRole.SuperAdmin && topItemsData.length > 0
    ) {
      (async () => {
        setDailyItemsLoading(true);
        try {
          const top5 = topItemsData.slice(0, 5).map((i) => i.name);
          setTopItemNames(top5);

          const today = new Date();
          const days: { date: string; label: string }[] = [];
          for (let i = 6; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            days.push({
              date: d.toISOString().slice(0, 10),
              label: d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit' }),
            });
          }

          const results = await Promise.all(
            days.map((d) => apiService.getTopSellingItems(currentRestaurant.id, d.date, d.date, 10))
          );

          const stacked = results.map((dayData, i) => {
            const entry: any = { day: days[i].label };
            top5.forEach((name) => {
              const found = (dayData || []).find((x: any) => x.name === name);
              entry[name] = found ? Number(found.quantity) : 0;
            });
            return entry;
          });

          setDailyItemsData(stacked);
        } catch {
          setDailyItemsData([]);
        } finally {
          setDailyItemsLoading(false);
        }
      })();
    }
  }, [activeSection, currentRestaurant, showSalesGraph, salesGraphTab, topItemsData]);

  useEffect(() => {
    if (activeSection === 'revenue' && user?.role === UserRole.SuperAdmin) {
      loadAllRestaurantsWithRevenue();
    }
  }, [activeSection, user]);

  useEffect(() => {
    if (activeSection === 'earnings' && user?.role === UserRole.SuperAdmin) {
      loadSubscriptionPayments();
    }
  }, [activeSection, user]);

  useEffect(() => {
    if (activeSection === 'gateways' && user?.role === UserRole.SuperAdmin) {
      loadGlobalGateways();
    }
  }, [activeSection, user]);

  useEffect(() => {
    if (activeSection === 'menu' && currentRestaurant?.id) {
      loadMenuCategories();
      loadMenuItems();
    }
  }, [activeSection, currentRestaurant]);

  useEffect(() => {
    if (currentTenant?.id) {
      loadRestaurants();
    }
  }, [currentTenant]);

  const loadPricingPlans = async () => {
    try {
      setLoadingPlans(true);
      const plans = await apiService.getPricingPlans();
      setPricingPlans(plans);
    } catch (error) {
      console.error('Failed to load pricing plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      // SuperAdmin views a global list of admins across all tenants, so do not filter by currentTenant?.id
      const usersData = await apiService.getUsers(user?.role === UserRole.SuperAdmin ? undefined : currentTenant?.id);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadSubscriptionPayments = async () => {
    try {
      setLoadingEarnings(true);
      const data = await apiService.getSubscriptionPayments();
      setSubscriptionPayments(data);
    } catch (error) {
      console.error('Failed to load subscription payments:', error);
      setSnackbar({ open: true, message: 'Failed to load subscription payments', severity: 'error' });
    } finally {
      setLoadingEarnings(false);
    }
  };

  const loadTables = async () => {
    if (!currentRestaurant?.id) return;
    try {
      setLoadingTables(true);
      const tablesData = await apiService.getTables(currentRestaurant.id);
      setTables(tablesData);
      await localDb.setCache(`tables_${currentRestaurant.id}`, tablesData);
    } catch (error) {
      console.error('Failed to load tables:', error);
      const cached = await localDb.getCache(`tables_${currentRestaurant.id}`);
      if (cached) setTables(cached);
    } finally {
      setLoadingTables(false);
    }
  };

  const loadMenuCategories = async () => {
    if (!currentRestaurant?.id) return;
    try {
      setLoadingMenuCategories(true);
      const categoriesData = await apiService.getMenuCategories(currentRestaurant.id);
      setMenuCategories(categoriesData);
      await localDb.setCache(`categories_${currentRestaurant.id}`, categoriesData);
    } catch (error) {
      console.error('Failed to load menu categories:', error);
      const cached = await localDb.getCache(`categories_${currentRestaurant.id}`);
      if (cached) setMenuCategories(cached);
    } finally {
      setLoadingMenuCategories(false);
    }
  };

  const loadMenuItems = async () => {
    if (!currentRestaurant?.id) return;
    try {
      setLoadingMenuItems(true);
      const itemsData = await apiService.getMenuItems(undefined, currentRestaurant.id);
      setMenuItems(itemsData);
      await localDb.setCache(`menu_items_${currentRestaurant.id}`, itemsData);
    } catch (error) {
      console.error('Failed to load menu items:', error);
      const cached = await localDb.getCache(`menu_items_${currentRestaurant.id}`);
      if (cached) setMenuItems(cached);
    } finally {
      setLoadingMenuItems(false);
    }
  };

  const loadPendingOrders = async () => {
    if (!currentRestaurant?.id) return;
    try {
      setLoadingPendingOrders(true);
      const orders = await apiService.getPendingOrders(currentRestaurant.id);
      setPendingOrders(orders);
    } catch (error) {
      console.error('Failed to load pending orders:', error);
    } finally {
      setLoadingPendingOrders(false);
    }
  };

  const loadRealOrders = async () => {
    if (!currentRestaurant?.id) return;
    try {
      setLoadingOrders(true);
      const orders = await apiService.getOrders(currentRestaurant.id);
      const sorted = (orders || []).sort((a: any, b: any) => new Date(b.orderDate || b.createdAt).getTime() - new Date(a.orderDate || a.createdAt).getTime());
      setOrdersList(sorted);
    } catch (error) {
      console.error('Failed to load real orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadAllRestaurantsWithRevenue = async () => {
    setLoadingRevenue(true);
    try {
      const rests = await apiService.getRestaurants();
      setAllRestaurants(rests);
      const revMap: Record<string, { revenue: number; orderCount: number; loading: boolean }> = {};
      rests.forEach(r => { revMap[r.id] = { revenue: 0, orderCount: 0, loading: true }; });
      setRestaurantRevenues({ ...revMap });
      await Promise.all(rests.map(async (r) => {
        try {
          const [rev, cnt] = await Promise.all([
            apiService.getRestaurantRevenue(r.id),
            apiService.getRestaurantOrderCount(r.id),
          ]);
          setRestaurantRevenues(prev => ({ ...prev, [r.id]: { revenue: rev, orderCount: cnt, loading: false } }));
        } catch {
          setRestaurantRevenues(prev => ({ ...prev, [r.id]: { revenue: 0, orderCount: 0, loading: false } }));
        }
      }));
    } catch (error) {
      console.error('Failed to load restaurants:', error);
    } finally {
      setLoadingRevenue(false);
    }
  };

  const loadGlobalGateways = async () => {
    try {
      setLoadingGlobalGateways(true);
      const data = await apiService.getRestaurantGateways('00000000-0000-0000-0000-000000000000');
      setGlobalGateways(data);
    } catch (error) {
      console.error('Failed to load global gateways:', error);
      setSnackbar({ open: true, message: 'Failed to load global payment gateways', severity: 'error' });
    } finally {
      setLoadingGlobalGateways(false);
    }
  };

  const approveOrder = async (orderId: string) => {
    setApprovalBusy(s => new Set(s).add(orderId));
    try {
      await apiService.approveOrder(orderId);
      setPendingOrders(prev => prev.filter(o => o.id !== orderId));
      setSnackbar({ open: true, message: 'Order approved and sent to kitchen!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to approve order', severity: 'error' });
    } finally {
      setApprovalBusy(s => { const n = new Set(s); n.delete(orderId); return n; });
    }
  };

  const cancelOrder = async (orderId: string) => {
    setApprovalBusy(s => new Set(s).add(orderId));
    try {
      await apiService.updateOrderStatus(orderId, 'Cancelled' as any);
      setPendingOrders(prev => prev.filter(o => o.id !== orderId));
      setSnackbar({ open: true, message: 'Order cancelled.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to cancel order', severity: 'error' });
    } finally {
      setApprovalBusy(s => { const n = new Set(s); n.delete(orderId); return n; });
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) {
      setSnackbar({ open: true, message: 'Please fill in all required fields (username, email, password)', severity: 'error' });
      return;
    }
    
    try {
      // SuperAdmin always creates Admin role users
      const roleToCreate = user?.role === UserRole.SuperAdmin ? 'Admin' : newUser.role;
      const createdUser = await apiService.createUser({
        tenantId: user?.role === UserRole.SuperAdmin ? newUser.tenantId : (currentTenant?.id || ''),
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        firstName: newUser.firstName || newUser.username.split('@')[0],
        lastName: newUser.lastName || '',
        role: roleToCreate,
      });
      
      setSnackbar({ 
        open: true, 
        message: `User ${newUser.firstName} ${newUser.lastName || newUser.username} created successfully!`, 
        severity: 'success' 
      });
      
      setCreateUserDialogOpen(false);
      setNewUser({ username: '', email: '', password: '', firstName: '', lastName: '', role: 'Admin', tenantId: '' });
      loadUsers();
    } catch (error: any) {
      console.error('Failed to create user:', error);
      
      // Extract specific error message from backend
      let errorMessage = 'Failed to create user';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Format error message for UI
      setSnackbar({ 
        open: true, 
        message: `${errorMessage}. Please check your input and try again.`, 
        severity: 'error' 
      });
      
      // Log backend response for debugging
      if (error?.response?.status) {
        console.log('Backend status:', error.response.status);
        console.log('Backend response:', error.response.data);
      }
    }
  };

  const handleEditUserClick = (selectedUser: User) => {
    setEditingUser(selectedUser);
    setEditUserForm({
      id: selectedUser.id,
      firstName: selectedUser.firstName || '',
      lastName: selectedUser.lastName || '',
      username: selectedUser.username,
      email: selectedUser.email,
      phone: selectedUser.phone || '',
      password: '',
      role: selectedUser.role || 'Admin',
      tenantId: selectedUser.tenantId || ''
    });
    setEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editUserForm.id) return;
    if (!editUserForm.username || !editUserForm.email) {
      setSnackbar({ open: true, message: 'Username and Email are required.', severity: 'error' });
      return;
    }

    try {
      await apiService.updateUser(editUserForm.id, {
        firstName: editUserForm.firstName,
        lastName: editUserForm.lastName,
        username: editUserForm.username,
        email: editUserForm.email,
        phone: editUserForm.phone,
        password: editUserForm.password || undefined,
        role: editUserForm.role,
        tenantId: editUserForm.tenantId || undefined
      });

      setSnackbar({
        open: true,
        message: 'Admin user updated successfully!',
        severity: 'success'
      });

      setEditUserDialogOpen(false);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      let errorMessage = 'Failed to update user';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleDeleteUser = async (userId: string, fullName: string) => {
    if (!window.confirm(`Are you sure you want to delete the admin user "${fullName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      setSnackbar({
        open: true,
        message: `Admin user "${fullName}" deleted successfully!`,
        severity: 'success'
      });
      loadUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      let errorMessage = 'Failed to delete user';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleCreateRestaurantWithAdmin = async () => {
    // Validate required fields and show explicit error messages
    if (!newRestaurant.name.trim()) {
      setSnackbar({ open: true, message: 'Please enter a Restaurant Name', severity: 'error' });
      return;
    }
    if (!newRestaurant.restaurantCode.trim()) {
      setSnackbar({ open: true, message: 'Please enter a Restaurant Code', severity: 'error' });
      return;
    }
    if (!newRestaurant.domain.trim()) {
      setSnackbar({ open: true, message: 'Please enter a Domain name', severity: 'error' });
      return;
    }
    if (!newRestaurant.planId) {
      setSnackbar({ open: true, message: 'Please select a Pricing Plan', severity: 'error' });
      return;
    }
    if (!newRestaurant.adminFirstName.trim()) {
      setSnackbar({ open: true, message: 'Please enter the Admin\'s First Name', severity: 'error' });
      return;
    }
    if (!newRestaurant.adminUsername.trim()) {
      setSnackbar({ open: true, message: 'Please enter the Admin\'s Username', severity: 'error' });
      return;
    }
    if (!newRestaurant.adminEmail.trim()) {
      setSnackbar({ open: true, message: 'Please enter the Admin\'s Email', severity: 'error' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newRestaurant.adminEmail.trim())) {
      setSnackbar({ open: true, message: 'Please enter a valid Admin Email address', severity: 'error' });
      return;
    }
    if (!newRestaurant.adminPassword) {
      setSnackbar({ open: true, message: 'Please enter the Admin\'s Password', severity: 'error' });
      return;
    }
    if (newRestaurant.adminPassword.length < 8) {
      setSnackbar({ open: true, message: 'Admin Password must be at least 8 characters long', severity: 'error' });
      return;
    }

    let tenantCreated = false;
    try {
      // First create the tenant (restaurant)
      // Compute planExpiry from selected plan's trialDays
      let planExpiry: string | undefined;
      if (newRestaurant.planId) {
        const selectedPlan = pricingPlans.find(p => p.id === newRestaurant.planId);
        if (selectedPlan && selectedPlan.trialDays > 0) {
          const expiry = new Date();
          expiry.setDate(expiry.getDate() + selectedPlan.trialDays);
          planExpiry = expiry.toISOString();
        }
      }
      const tenantResponse = await apiService.createTenant({
        name: newRestaurant.name,
        restaurantCode: newRestaurant.restaurantCode,
        subdomain: newRestaurant.domain,
        planId: newRestaurant.planId || undefined,
        planExpiry,
      } as any);
      tenantCreated = true;

      // Second, create the actual restaurant for this tenant
      await apiService.createRestaurant({
        tenantId: tenantResponse.id,
        name: newRestaurant.name,
        restaurantCode: newRestaurant.restaurantCode,
        address: 'Default Address',
        phone: '1234567890',
        email: newRestaurant.adminEmail,
        isActive: true,
        currency: 'INR',
        timeZone: 'Asia/Kolkata',
      } as any);

      // Then create the admin user for this tenant
      await apiService.createUser({
        tenantId: tenantResponse.id,
        username: newRestaurant.adminUsername,
        email: newRestaurant.adminEmail,
        password: newRestaurant.adminPassword,
        firstName: newRestaurant.adminFirstName,
        lastName: newRestaurant.adminLastName,
        role: 'Admin',
      });

      setSnackbar({
        open: true,
        message: `Restaurant and admin user created successfully!`,
        severity: 'success'
      });

      setCreateRestaurantDialogOpen(false);
      setNewRestaurant({ name: '', restaurantCode: '', domain: '', planId: '', planExpiry: '', adminEmail: '', adminUsername: '', adminPassword: '', adminFirstName: '', adminLastName: '' });
    } catch (error: any) {
      console.error('Failed to create restaurant:', error);
      if (error?.response?.status) {
        console.log('Backend status:', error.response.status);
        console.log('Backend response:', error.response.data);
      }
      if (tenantCreated) {
        // Tenant was created but admin user/restaurant creation failed — close dialog and show warning
        let errorDetails = '';
        if (error?.response?.data?.message) {
          errorDetails = ` Reason: ${error.response.data.message}`;
        } else if (error?.message) {
          errorDetails = ` Reason: ${error.message}`;
        }
        setSnackbar({ open: true, message: `Restaurant created but admin user or restaurant setup failed.${errorDetails} You can add them separately.`, severity: 'warning' });
        setCreateRestaurantDialogOpen(false);
      } else {
        let errorMessage = 'Failed to create restaurant';
        if (error?.response?.data?.message) errorMessage = error.response.data.message;
        else if (error?.message) errorMessage = error.message;
        setSnackbar({ open: true, message: `${errorMessage}. Please check your input and try again.`, severity: 'error' });
      }
    } finally {
      if (tenantCreated) loadTenants();
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === UserStatus.Active ? UserStatus.Inactive : UserStatus.Active;
    try {
      const updatedUser = await apiService.updateUserStatus(userId, newStatus);
      setSnackbar({ 
        open: true, 
        message: `User ${newStatus === UserStatus.Active ? 'activated' : 'deactivated'} successfully!`, 
        severity: 'success' 
      });
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      
      let errorMessage = 'Failed to update user status';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setSnackbar({ 
        open: true, 
        message: `${errorMessage}`, 
        severity: 'error' 
      });
    }
  };

  const handleToggleTenantStatus = async (tenantId: string, currentActive: boolean) => {
    try {
      await apiService.updateTenantStatus(tenantId, !currentActive);
      setSnackbar({ open: true, message: `Tenant ${!currentActive ? 'activated' : 'deactivated'} successfully!`, severity: 'success' });
      loadTenants();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update tenant status', severity: 'error' });
    }
  };

  const handleToggleCashAdjust = async (tenant: Tenant) => {
    try {
      await apiService.updateTenant(tenant.id, {
        name: tenant.name,
        subdomain: tenant.subdomain,
        isActive: tenant.isActive,
        planId: tenant.planId,
        planExpiry: tenant.planExpiry ? (typeof tenant.planExpiry === 'string' ? tenant.planExpiry : tenant.planExpiry.toISOString()) : undefined,
        canAdjustSales: !tenant.canAdjustSales
      });
      setSnackbar({ open: true, message: `Cash sales adjust ${!tenant.canAdjustSales ? 'enabled' : 'disabled'}!`, severity: 'success' });
      loadTenants();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update tenant feature', severity: 'error' });
    }
  };

  const handleOpenEditTenant = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setEditTenantDialogOpen(true);
  };

  const handleCloseEditTenant = () => {
    setEditTenantDialogOpen(false);
    setEditingTenant(null);
  };

  const handleUpdateTenant = async () => {
    if (!editingTenant) return;
    try {
      await apiService.updateTenant(editingTenant.id, {
        name: editingTenant.name,
        subdomain: editingTenant.subdomain,
        isActive: editingTenant.isActive,
        restaurantCode: editingTenant.restaurantCode,
        planId: editingTenant.planId,
        planExpiry: editingTenant.planExpiry
          ? new Date(editingTenant.planExpiry as string).toISOString()
          : undefined,
      });
      setSnackbar({ open: true, message: 'Tenant updated successfully!', severity: 'success' });
      handleCloseEditTenant();
      loadTenants();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update tenant', severity: 'error' });
    }
  };

  const handleOpenRenewPlanDialog = (tenant: Tenant) => {
    const currentPlan = pricingPlans.find(p => p.id === tenant.planId);
    const defaultPlanId = tenant.planId || (pricingPlans.length > 0 ? pricingPlans[0].id : '');
    const defaultPlan = pricingPlans.find(p => p.id === defaultPlanId);
    setRenewingTenant(tenant);
    setPayPlanStep(1);
    setRenewPlanForm({
      planId: defaultPlanId,
      billingCycle: 'monthly',
      paymentMethod: 'upi',
      amount: defaultPlan ? defaultPlan.priceMonthly : (currentPlan ? currentPlan.priceMonthly : 0),
      transactionId: `TXN-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
    });
    setRenewPlanDialogOpen(true);
  };

  const handleRenewPlanSubmit = async () => {
    if (!renewingTenant) return;
    try {
      const plan = pricingPlans.find(p => p.id === renewPlanForm.planId);
      await apiService.createSubscriptionPayment({
        tenantId: renewingTenant.id,
        pricingPlanId: renewPlanForm.planId,
        amount: renewPlanForm.amount,
        billingCycle: renewPlanForm.billingCycle,
        paymentMethod: renewPlanForm.paymentMethod,
        transactionId: renewPlanForm.transactionId
      });
      setSnackbar({ open: true, message: `Payment confirmed! ${renewingTenant.name} is now active on ${plan?.name || 'selected plan'}.`, severity: 'success' });
      setRenewPlanDialogOpen(false);
      loadTenants();
      loadSubscriptionPayments();
    } catch (error: any) {
      console.error('Failed to process payment:', error);
      setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to process payment', severity: 'error' });
    }
  };

  const loadTenants = async () => {
    try {
      setLoadingTenants(true);
      const tenantsData = await apiService.getTenants();
      setTenants(tenantsData);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleDeleteTenant = async (tenantId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the tenant "${name}"? This action cannot be undone and will delete all associated data.`)) {
      return;
    }

    try {
      await apiService.deleteTenant(tenantId);
      setSnackbar({
        open: true,
        message: `Tenant "${name}" deleted successfully!`,
        severity: 'success'
      });
      loadTenants();
    } catch (error: any) {
      console.error('Failed to delete tenant:', error);
      let errorMessage = 'Failed to delete tenant';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleTenantMenuOpen = (event: React.MouseEvent<HTMLElement>) => setTenantAnchorEl(event.currentTarget);
  const handleTenantMenuClose = () => setTenantAnchorEl(null);
  const handleRestaurantMenuOpen = (event: React.MouseEvent<HTMLElement>) => setRestaurantAnchorEl(event.currentTarget);
  const handleRestaurantMenuClose = () => setRestaurantAnchorEl(null);

  const loadRestaurants = async (tenantId?: string) => {
    const targetTenantId = tenantId || currentTenant?.id;
    if (!targetTenantId) {
      console.warn('No tenant ID available to load restaurants');
      setRestaurants([]);
      return;
    }
    try {
      setLoadingRestaurants(true);
      const restaurantsData = await apiService.getRestaurants(targetTenantId);
      setRestaurants(restaurantsData);
    } catch (error) {
      console.error('Failed to load restaurants:', error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const handleSwitchTenant = async (tenantId: string) => {
    try {
      await switchTenant(tenantId);
      handleTenantMenuClose();
      // Load restaurants when tenant is switched
      loadRestaurants(tenantId);
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  };

  const handleSwitchRestaurant = async (restaurantId: string) => {
    try {
      await switchRestaurant(restaurantId);
      handleRestaurantMenuClose();
    } catch (error) {
      console.error('Failed to switch restaurant:', error);
    }
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  // Table handlers
  const handleCreateTable = async () => {
    if (!currentRestaurant?.id) {
      setSnackbar({ open: true, message: 'No restaurant selected. Please select a restaurant first.', severity: 'error' });
      return;
    }
    try {
      await apiService.createTable({
        restaurantId: currentRestaurant.id,
        tableNumber: newTable.tableNumber,
        capacity: newTable.capacity,
        location: newTable.location,
        status: 1, // 1 = Available (from backend TableStatus enum)
        isActive: newTable.isActive,
      });
      setSnackbar({ open: true, message: 'Table created successfully!', severity: 'success' });
      setCreateTableDialogOpen(false);
      setNewTable({
        tableNumber: '',
        capacity: 4,
        location: '',
        isActive: true,
        tableName: '',
        shape: TableShape.Rectangle,
        xPosition: 0,
        yPosition: 0,
        width: 100,
        height: 100,
        rotation: 0
      });
      loadTables();
    } catch (error) {
      console.error('Error creating table:', error);
      setSnackbar({ open: true, message: 'Failed to create table', severity: 'error' });
    }
  };

  const handleOpenEditTable = (table: Table) => {
    setEditingTable(table);
    setEditTableDialogOpen(true);
  };

  const handleCloseEditTable = () => {
    setEditTableDialogOpen(false);
    setEditingTable(null);
  };

  const handleUpdateTable = async () => {
    if (!editingTable) return;
    try {
      await apiService.updateTable(editingTable.id, {
        tableNumber: editingTable.tableNumber,
        capacity: editingTable.capacity,
        location: editingTable.location,
        isActive: editingTable.isActive,
      });
      setSnackbar({ open: true, message: 'Table updated successfully!', severity: 'success' });
      handleCloseEditTable();
      loadTables();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update table', severity: 'error' });
    }
  };

  const handleDeleteTable = async (tableId: string) => {
    try {
      await apiService.deleteTable(tableId);
      setSnackbar({ open: true, message: 'Table deleted successfully!', severity: 'success' });
      loadTables();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete table', severity: 'error' });
    }
  };

  const handleToggleTableStatus = async (tableId: string, currentStatus: Table['status']) => {
    const statusMap: Record<Table['status'], Table['status']> = {
      1: 2, // Available -> Occupied
      2: 1, // Occupied -> Available
      3: 1, // Reserved -> Available
      4: 1, // OutOfService -> Available
      5: 1, // Cleaning -> Available
    };
    try {
      await apiService.updateTableStatus(tableId, statusMap[currentStatus]);
      setSnackbar({ open: true, message: 'Table status updated successfully!', severity: 'success' });
      loadTables();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update table status', severity: 'error' });
    }
  };

  // Menu category handlers
  const handleCreateCategory = async () => {
    if (!currentRestaurant?.id) {
      setSnackbar({ open: true, message: 'No restaurant selected. Please select a restaurant first.', severity: 'error' });
      return;
    }
    try {
      console.log('Creating category with data:', {
        restaurantId: currentRestaurant.id,
        name: newCategory.name,
        description: newCategory.description,
        displayOrder: newCategory.displayOrder,
        isActive: newCategory.isActive,
      });
      await apiService.createMenuCategory({
        restaurantId: currentRestaurant.id,
        name: newCategory.name,
        description: newCategory.description,
        displayOrder: newCategory.displayOrder,
        isActive: newCategory.isActive,
      });
      setSnackbar({ open: true, message: 'Category created successfully!', severity: 'success' });
      setCreateCategoryDialogOpen(false);
      setNewCategory({ name: '', description: '', displayOrder: 0, isActive: true });
      loadMenuCategories();
    } catch (error: any) {
      console.error('Failed to create category:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create category';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await apiService.deleteMenuCategory(categoryId);
      setSnackbar({ open: true, message: 'Category deleted successfully!', severity: 'success' });
      loadMenuCategories();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete category', severity: 'error' });
    }
  };

  // Menu item handlers
  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const max_size = 800;

          if (width > height) {
            if (width > max_size) {
              height *= max_size / width;
              width = max_size;
            }
          } else {
            if (height > max_size) {
              width *= max_size / height;
              height = max_size;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
            } else {
              reject(new Error('Canvas to Blob failed'));
            }
          }, 'image/jpeg', 0.8);
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleOcrReorder = (dragIndex: number, hoverIndex: number) => {
    const dragItem = ocrResults[dragIndex];
    const newResults = [...ocrResults];
    newResults.splice(dragIndex, 1);
    newResults.splice(hoverIndex, 0, dragItem);
    setOcrResults(newResults);
  };

  const handleOcrFileChange = async (file: File) => {
    if (!file) return;
    setOcrFile(file);
    setOcrImagePreview(URL.createObjectURL(file));
    setOcrScanning(true);
    setOcrResults([]);

    try {
      const items = await apiService.scanMenuImage(file);
      if (items.length === 0) {
        setSnackbar({ open: true, message: 'No menu items detected in the image. Try a clearer photo.', severity: 'warning' });
      }
      setOcrResults(items.map((item: any, idx: number) => ({
        id: String(idx + 1),
        category: item.category || 'Uncategorized',
        name: item.name,
        price: Number(item.price) || 0,
        description: item.description || '',
        isVegetarian: item.isVegetarian !== false,
        isSpicy: item.isSpicy === true,
        sizeName: item.sizeName || null,
        tag: item.tag || null,
        selected: true,
      })));
      setOcrProgress(100);
      setOcrStage('Done');
      setOcrViewMode('preview');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to scan menu image';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      // brief pause so the user sees the bar reach 100%
      setTimeout(() => setOcrScanning(false), 350);
    }
  };

  const handleOcrImport = async () => {
    if (!currentRestaurant?.id) {
      setSnackbar({ open: true, message: 'No restaurant selected.', severity: 'error' });
      return;
    }

    const selectedItems = ocrResults.filter(item => item.selected);
    if (selectedItems.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one item to import.', severity: 'warning' });
      return;
    }

    // The size is part of the item name, so "Tandoori Pizza (Large)" and
    // "Tandoori Pizza (Medium)" are separate items; only an exact name repeat
    // counts as a duplicate.
    const fullName = (it: any) => (it.sizeName ? `${it.name} (${it.sizeName})` : it.name).trim();

    // Drop items already in this restaurant's menu, plus repeats within the batch.
    const existingNames = new Set(menuItems.map(m => (m.name || '').trim().toLowerCase()));
    const batchSeen = new Set<string>();
    const itemsToImport: typeof selectedItems = [];
    let skipped = 0;
    for (const it of selectedItems) {
      const key = fullName(it).toLowerCase();
      if (existingNames.has(key) || batchSeen.has(key)) { skipped++; continue; }
      batchSeen.add(key);
      itemsToImport.push(it);
    }

    if (itemsToImport.length === 0) {
      setSnackbar({ open: true, message: `All ${skipped} selected item(s) already exist in your menu — nothing to import.`, severity: 'warning' });
      return;
    }

    const missingPrice = itemsToImport.filter(i => !i.price || i.price <= 0);
    if (missingPrice.length > 0) {
      setSnackbar({ open: true, message: `${missingPrice.length} selected item(s) have no price (could not be read from the photo). Add their prices or uncheck them before importing.`, severity: 'warning' });
      setOcrViewMode('edit');
      return;
    }

    setOcrScanning(true); // show loader during import
    try {
      // 1. Fetch current categories to resolve against
      const existingCategories = [...menuCategories];
      let imported = 0;

      for (const item of itemsToImport) {
        // Resolve or create category
        let category = existingCategories.find(c => c.name.toLowerCase() === item.category.toLowerCase());
        let categoryId = category?.id;

        if (!categoryId) {
          // Create the category
          try {
            const createdCat = await apiService.createMenuCategory({
              restaurantId: currentRestaurant.id,
              name: item.category,
              description: `Imported via OCR scanning`,
              displayOrder: existingCategories.length + 1,
              isActive: true
            });
            categoryId = createdCat.id;
            existingCategories.push(createdCat);
          } catch (catErr) {
            console.error('Failed to create category during OCR import:', catErr);
            continue; // Skip item if category creation fails
          }
        }

        // Create the menu item — the size is part of its name.
        const itemName = fullName(item);
        // Build tags: include the OCR-detected tag and a 'spicy' marker when applicable
        const tags = Array.from(new Set([
          ...(item.tag ? [item.tag] : []),
          ...(item.isSpicy ? ['Spicy'] : []),
        ]));
        try {
          await apiService.createMenuItem({
            categoryId: categoryId,
            restaurantId: currentRestaurant.id,
            name: itemName,
            description: item.description,
            price: item.price,
            isAvailable: true,
            isVegetarian: item.isVegetarian,
            isSpicy: item.isSpicy,
            tags
          } as any);
          imported++;
        } catch (e: any) {
          // 409 Conflict = the item already exists in this restaurant; skip it.
          if (e?.response?.status === 409) { skipped++; continue; }
          throw e;
        }
      }

      const importMsg = skipped > 0
        ? `Imported ${imported} item(s); skipped ${skipped} duplicate(s) already in your menu.`
        : `Successfully imported ${imported} menu items!`;
      setSnackbar({ open: true, message: importMsg, severity: 'success' });
      setOcrDialogOpen(false);
      setOcrFile(null);
      setOcrImagePreview(null);
      setOcrResults([]);
      
      // Refresh categories and items
      loadMenuCategories();
      loadMenuItems();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to complete OCR import', severity: 'error' });
    } finally {
      setOcrScanning(false);
    }
  };

  const handleExcelFileChange = (file: File) => {
    if (!file) return;
    setExcelFile(file);
    setExcelLoading(true);
    setExcelResults([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) {
        setExcelLoading(false);
        setSnackbar({ open: true, message: 'Could not read file content.', severity: 'error' });
        return;
      }

      try {
        const lines = text.split(/\r?\n/);
        const results: any[] = [];
        let headers: string[] = [];
        let startIndex = 0;

        while (startIndex < lines.length && !lines[startIndex].trim()) {
          startIndex++;
        }

        if (startIndex < lines.length) {
          headers = lines[startIndex].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
          startIndex++;
        }

        const categoryIdx = headers.findIndex(h => h.toLowerCase().includes('category'));
        const nameIdx = headers.findIndex(h => h.toLowerCase().includes('name') || h.toLowerCase().includes('item'));
        const priceIdx = headers.findIndex(h => h.toLowerCase().includes('price'));
        const descIdx = headers.findIndex(h => h.toLowerCase().includes('description') || h.toLowerCase().includes('desc'));

        for (let i = startIndex; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const columns: string[] = [];
          let currentField = '';
          let insideQuotes = false;
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            if (char === '"' || char === "'") {
              insideQuotes = !insideQuotes;
            } else if (char === ',' && !insideQuotes) {
              columns.push(currentField.trim().replace(/^["']|["']$/g, ''));
              currentField = '';
            } else {
              currentField += char;
            }
          }
          columns.push(currentField.trim().replace(/^["']|["']$/g, ''));

          if (columns.length === 0 || !columns.some(col => col)) continue;

          const category = categoryIdx !== -1 ? columns[categoryIdx] : 'General';
          const name = nameIdx !== -1 ? columns[nameIdx] : '';
          const priceStr = priceIdx !== -1 ? columns[priceIdx] : '0';
          const price = parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
          const description = descIdx !== -1 ? columns[descIdx] : '';

          if (name) {
            results.push({
              id: `${Date.now()}-${Math.random()}-${i}`,
              category: category || 'General',
              name,
              price,
              description: description || '',
              selected: true
            });
          }
        }

        if (results.length === 0) {
          if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
            setTimeout(() => {
              setExcelResults([
                { id: 'ex1', category: 'Starter', name: 'Paneer Tikka', price: 180, description: 'Clay oven cooked cottage cheese', selected: true },
                { id: 'ex2', category: 'Starter', name: 'Veg Crispy', price: 150, description: 'Deep fried crispy vegetables', selected: true },
                { id: 'ex3', category: 'Main Course', name: 'Dal Tadka', price: 160, description: 'Yellow lentils tempered with ghee and spices', selected: true },
                { id: 'ex4', category: 'Main Course', name: 'Butter Naan', price: 40, description: 'Leavened flatbread with butter', selected: true },
                { id: 'ex5', category: 'Dessert', name: 'Gulab Jamun', price: 60, description: 'Two pieces sweet milk balls', selected: true }
              ]);
              setExcelLoading(false);
            }, 1200);
            return;
          }
          
          setExcelLoading(false);
          setSnackbar({ open: true, message: 'No valid menu items found in CSV. Please use the template format.', severity: 'warning' });
          return;
        }

        setExcelResults(results);
      } catch (err) {
        console.error(err);
        setSnackbar({ open: true, message: 'Failed parsing CSV file.', severity: 'error' });
      } finally {
        setExcelLoading(false);
      }
    };

    reader.onerror = () => {
      setExcelLoading(false);
      setSnackbar({ open: true, message: 'Error reading file.', severity: 'error' });
    };

    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      setTimeout(() => {
        setExcelResults([
          { id: 'ex1', category: 'Starter', name: 'Paneer Tikka', price: 180, description: 'Clay oven cooked cottage cheese', selected: true },
          { id: 'ex2', category: 'Starter', name: 'Veg Crispy', price: 150, description: 'Deep fried crispy vegetables', selected: true },
          { id: 'ex3', category: 'Main Course', name: 'Dal Tadka', price: 160, description: 'Yellow lentils tempered with ghee and spices', selected: true },
          { id: 'ex4', category: 'Main Course', name: 'Butter Naan', price: 40, description: 'Leavened flatbread with butter', selected: true },
          { id: 'ex5', category: 'Dessert', name: 'Gulab Jamun', price: 60, description: 'Two pieces sweet milk balls', selected: true }
        ]);
        setExcelLoading(false);
      }, 1200);
    } else {
      reader.readAsText(file);
    }
  };

  const handleExcelImport = async () => {
    if (!currentRestaurant?.id) {
      setSnackbar({ open: true, message: 'No restaurant selected.', severity: 'error' });
      return;
    }

    const selectedItems = excelResults.filter(item => item.selected);
    if (selectedItems.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one item to import.', severity: 'warning' });
      return;
    }

    // Skip items already in this restaurant's menu, plus repeats within the file.
    const existingNames = new Set(menuItems.map(m => (m.name || '').trim().toLowerCase()));
    const batchSeen = new Set<string>();
    const itemsToImport: typeof selectedItems = [];
    let skipped = 0;
    for (const it of selectedItems) {
      const key = (it.name || '').trim().toLowerCase();
      if (!key || existingNames.has(key) || batchSeen.has(key)) { skipped++; continue; }
      batchSeen.add(key);
      itemsToImport.push(it);
    }

    if (itemsToImport.length === 0) {
      setSnackbar({ open: true, message: `All ${skipped} selected item(s) already exist in your menu — nothing to import.`, severity: 'warning' });
      return;
    }

    setExcelLoading(true);
    try {
      const existingCategories = [...menuCategories];
      let imported = 0;

      for (const item of itemsToImport) {
        let category = existingCategories.find(c => c.name.toLowerCase() === item.category.toLowerCase());
        let categoryId = category?.id;

        if (!categoryId) {
          try {
            const createdCat = await apiService.createMenuCategory({
              restaurantId: currentRestaurant.id,
              name: item.category,
              description: `Imported via Excel/CSV`,
              displayOrder: existingCategories.length + 1,
              isActive: true
            });
            categoryId = createdCat.id;
            existingCategories.push(createdCat);
          } catch (catErr) {
            console.error('Failed to create category during Excel import:', catErr);
            continue;
          }
        }

        try {
          await apiService.createMenuItem({
            categoryId: categoryId,
            restaurantId: currentRestaurant.id,
            name: item.name,
            description: item.description,
            price: item.price,
            isAvailable: true,
            tags: []
          } as any);
          imported++;
        } catch (e: any) {
          // 409 Conflict = the item already exists in this restaurant; skip it.
          if (e?.response?.status === 409) { skipped++; continue; }
          throw e;
        }
      }

      const importMsg = skipped > 0
        ? `Imported ${imported} item(s); skipped ${skipped} duplicate(s) already in your menu.`
        : `Successfully imported ${imported} menu items!`;
      setSnackbar({ open: true, message: importMsg, severity: 'success' });
      setExcelDialogOpen(false);
      setExcelFile(null);
      setExcelResults([]);
      
      loadMenuCategories();
      loadMenuItems();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to complete Excel import', severity: 'error' });
    } finally {
      setExcelLoading(false);
    }
  };

  // Reusable optional size-options editor (e.g. S/M/L) shared by the Create and
  // Edit item dialogs. Empty list = item has no sizes (uses its single price).
  const renderSizesEditor = (
    sizes: { name: string; price: number }[],
    onChange: (next: { name: string; price: number }[]) => void
  ) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 600 }}>Sizes (optional)</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {sizes.length === 0 && (
            <Button size="small" onClick={() => onChange([{ name: 'S', price: 0 }, { name: 'M', price: 0 }, { name: 'L', price: 0 }])}>
              S / M / L
            </Button>
          )}
          <Button size="small" startIcon={<AddCircleOutline />} onClick={() => onChange([...sizes, { name: '', price: 0 }])}>
            Add size
          </Button>
        </Box>
      </Box>
      {sizes.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No sizes — the item uses its single price above. Add sizes like S/M/L if this item is sold in multiple sizes (each with its own price).
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sizes.map((sz, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Size"
                size="small"
                placeholder="S"
                value={sz.name}
                onChange={(e) => onChange(sizes.map((s, i) => i === idx ? { ...s, name: e.target.value } : s))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Price (₹)"
                size="small"
                type="number"
                value={sz.price || ''}
                onChange={(e) => onChange(sizes.map((s, i) => i === idx ? { ...s, price: parseFloat(e.target.value) || 0 } : s))}
                sx={{ width: 120 }}
              />
              <IconButton size="small" color="error" aria-label="Remove size" onClick={() => onChange(sizes.filter((_, i) => i !== idx))}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const handleCreateMenuItem = async () => {
    if (!currentRestaurant?.id) {
      setSnackbar({ open: true, message: 'No restaurant selected. Please select a restaurant first.', severity: 'error' });
      return;
    }
    try {
      const menuItemData = {
        categoryId: newItem.categoryId,
        restaurantId: currentRestaurant.id,
        name: newItem.name,
        shortcutName: newItem.shortcutName || undefined,
        description: newItem.description,
        price: newItem.price,
        isAvailable: newItem.isAvailable,
        tags: newItem.tags,
        sizes: newItem.sizes.filter(s => s.name.trim()),
        isHappyHourEnabled: newItem.isHappyHourEnabled,
        happyHourPrice: newItem.isHappyHourEnabled ? newItem.happyHourPrice : null,
        happyHourStart: newItem.isHappyHourEnabled ? (newItem.happyHourStart ? (newItem.happyHourStart.length === 5 ? newItem.happyHourStart + ':00' : newItem.happyHourStart) : null) : null,
        happyHourEnd: newItem.isHappyHourEnabled ? (newItem.happyHourEnd ? (newItem.happyHourEnd.length === 5 ? newItem.happyHourEnd + ':00' : newItem.happyHourEnd) : null) : null,
        happyHourDays: newItem.isHappyHourEnabled ? newItem.happyHourDays : null,
      };
      const createdItem = await apiService.createMenuItem(menuItemData);

      if (newItem.imageFile && createdItem.id) {
        const compressedFile = await compressImage(newItem.imageFile);
        await apiService.uploadMenuItemImage(createdItem.id, compressedFile);
      }

      setSnackbar({ open: true, message: 'Menu item created successfully!', severity: 'success' });
      setCreateItemDialogOpen(false);
      setNewItem({
        name: '', shortcutName: '', description: '', price: 0, categoryId: '', isAvailable: true, preparationTime: 15, isVegetarian: true, isSpicy: false, tags: [], imageFile: null,
        sizes: [],
        isHappyHourEnabled: false, happyHourPrice: 0, happyHourStart: '12:00', happyHourEnd: '15:00', happyHourDays: 'Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday'
      });
      loadMenuItems();
    } catch (error: any) {
      // 409 Conflict = an item with this exact name already exists.
      const isDuplicate = error?.response?.status === 409;
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || (isDuplicate ? 'This item already exists in your menu.' : 'Failed to create menu item'),
        severity: isDuplicate ? 'warning' : 'error',
      });
    }
  };

  const handleOpenEditItem = (item: MenuItemType) => {
    console.log('[EditItem] Edit button clicked for item:', item?.id, item?.name);
    try {
      // Deep copy and normalize the item to ensure all fields exist and are safe
      const normalizedItem: MenuItemType = {
        ...item,
        id: item.id || '',
        name: item.name || '',
        description: item.description || '',
        price: typeof item.price === 'number' ? item.price : 0,
        isAvailable: item.isAvailable !== undefined ? item.isAvailable : true,
        isVegetarian: item.isVegetarian || false,
        isSpicy: item.isSpicy || false,
        categoryId: item.categoryId || '',
        preparationTime: item.preparationTime || 15,
        tags: Array.isArray(item.tags) ? [...item.tags] : [],
        sizes: Array.isArray(item.sizes) ? item.sizes.map(s => ({ ...s })) : [],
        isHappyHourEnabled: item.isHappyHourEnabled || false,
        happyHourPrice: item.happyHourPrice || 0,
        happyHourStart: item.happyHourStart || '12:00',
        happyHourEnd: item.happyHourEnd || '15:00',
        happyHourDays: item.happyHourDays || 'Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday',
        createdAt: item.createdAt instanceof Date ? item.createdAt : new Date(),
        updatedAt: item.updatedAt instanceof Date ? item.updatedAt : new Date(),
      };
      console.log('[EditItem] Normalized item OK, opening dialog...');
      setEditingItem(normalizedItem);
      setEditItemDialogOpen(true);
      console.log('[EditItem] Dialog state set to true');
    } catch (err) {
      console.error('[EditItem] Error in handleOpenEditItem:', err);
    }
  };

  const handleCloseEditItem = () => {
    setEditItemDialogOpen(false);
    setEditingItem(null);
    setEditItemImageFile(null);
  };

  const handleUpdateMenuItem = async () => {
    if (!editingItem) return;
    try {
      console.log('[EditItem] Sending update for item:', editingItem.id, editingItem.name);
      const updatePayload = {
        categoryId: editingItem.categoryId,
        name: editingItem.name,
        shortcutName: editingItem.shortcutName || undefined,
        description: editingItem.description,
        price: editingItem.price,
        isAvailable: editingItem.isAvailable,
        preparationTime: editingItem.preparationTime,
        isVegetarian: editingItem.isVegetarian,
        isSpicy: editingItem.isSpicy,
        tags: editingItem.tags,
        sizes: Array.isArray(editingItem.sizes) ? editingItem.sizes.filter(s => s.name.trim()) : [],
        isHappyHourEnabled: editingItem.isHappyHourEnabled,
        happyHourPrice: editingItem.isHappyHourEnabled ? editingItem.happyHourPrice : null,
        happyHourStart: editingItem.isHappyHourEnabled ? (editingItem.happyHourStart ? (editingItem.happyHourStart.length === 5 ? editingItem.happyHourStart + ':00' : editingItem.happyHourStart) : null) : null,
        happyHourEnd: editingItem.isHappyHourEnabled ? (editingItem.happyHourEnd ? (editingItem.happyHourEnd.length === 5 ? editingItem.happyHourEnd + ':00' : editingItem.happyHourEnd) : null) : null,
        happyHourDays: editingItem.isHappyHourEnabled ? editingItem.happyHourDays : null,
      };
      console.log('[EditItem] Update payload:', JSON.stringify(updatePayload));
      await apiService.updateMenuItem(editingItem.id, updatePayload);

      if (editItemImageFile) {
        const compressedFile = await compressImage(editItemImageFile);
        await apiService.uploadMenuItemImage(editingItem.id, compressedFile);
      }

      setSnackbar({ open: true, message: 'Menu item updated successfully!', severity: 'success' });
      handleCloseEditItem();
      loadMenuItems();
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.response?.data || error?.message || 'Failed to update menu item';
      console.error('[EditItem] Update failed:', error?.response?.status, error?.response?.data, error);
      setSnackbar({ open: true, message: typeof errMsg === 'string' ? errMsg : 'Failed to update menu item', severity: 'error' });
    }
  };

  const handleDeleteMenuItem = async (itemId: string) => {
    try {
      await apiService.deleteMenuItem(itemId);
      setSnackbar({ open: true, message: 'Menu item deleted successfully!', severity: 'success' });
      loadMenuItems();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete menu item', severity: 'error' });
    }
  };

  // Mock data - Role-specific stats
  const superAdminStats = [
    { label: 'Total Tenants', value: tenants.length.toString(), change: '+15%', icon: <Domain />, color: '#6366f1' },
    { label: 'Total Users', value: users.length.toString(), change: '+22%', icon: <People />, color: '#10b981' },
    { label: 'Active Plans', value: pricingPlans.length.toString(), change: '', icon: <Sell />, color: '#f59e0b' },
    { label: 'Monthly Revenue', value: '₹2,45,000', change: '+18%', icon: <TrendingUp />, color: '#ef4444' },
  ];

  const restaurantStats = [
    { label: "Today's Orders", value: '42', change: '+12%', icon: <ShoppingCart />, color: '#6366f1' },
    { label: "Today's Revenue", value: '₹12,560', change: '+8%', icon: <TrendingUp />, color: '#10b981' },
    { label: 'Active Tables', value: '8/15', change: '', icon: <TableRestaurant />, color: '#f59e0b' },
    { label: 'Pending Orders', value: '5', change: '-3', icon: <Receipt />, color: '#ef4444' },
  ];

  const stats = user?.role === UserRole.SuperAdmin ? superAdminStats : restaurantStats;

  const recentOrders = [
    { id: 'ORD-001', table: 'T-05', amount: 1250, status: 'Preparing', time: '2 min ago', source: 'POS' },
    { id: 'ORD-002', table: 'T-12', amount: 890, status: 'Ready', time: '5 min ago', source: 'QR' },
    { id: 'ORD-003', table: 'T-03', amount: 2150, status: 'Paid', time: '12 min ago', source: 'POS' },
    { id: 'ORD-004', table: 'T-08', amount: 560, status: 'Pending', time: '15 min ago', source: 'QR' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ready': return colors.success.main;
      case 'Preparing': return colors.warning.main;
      case 'Paid': return colors.info.main;
      case 'Pending': return colors.dark.textMuted;
      default: return colors.dark.textMuted;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.SuperAdmin: return '#6366f1';
      case UserRole.Admin: return '#8b5cf6';
      case UserRole.Staff: return '#10b981';
      default: return '#64748b';
    }
  };

  interface NavItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    roles: string[];
    category?: string;
    action?: () => void;
    featureKey?: 'HasPOS' | 'HasInventory' | 'HasCRM' | 'HasOnlineOrdering' | 'HasDelivery' | 'HasKDS' | 'HasReports';
  }

  const navItems: NavItem[] = [
    { id: 'dashboard',  label: t('Dashboard', 'डैशबोर्ड'),         icon: <DashboardIcon />,   roles: ['SuperAdmin', 'Admin', 'Staff'] },
    // ── SuperAdmin only ──
    { id: 'admins',     label: t('Manage Admins', 'एडमिन प्रबंधन'),      icon: <People />,          roles: ['SuperAdmin'] },
    { id: 'tenants',    label: t('Restaurants', 'रेस्टोरेंट'),         icon: <Domain />,          roles: ['SuperAdmin'] },
    { id: 'revenue',    label: t('Restaurant Revenue', 'रेस्टोरेंट राजस्व'),  icon: <TrendingUp />,      roles: ['SuperAdmin'] },
    { id: 'pricing',    label: t('Pricing Plans', 'मूल्य योजनाएं'),       icon: <Sell />,            roles: ['SuperAdmin'] },
    { id: 'earnings',   label: t('Earning Ledger', 'कमाई बहीखाता'),      icon: <AttachMoney />,     roles: ['SuperAdmin'] },
    { id: 'gateways',   label: t('Payment Gateways', 'भुगतान गेटवे'),    icon: <CreditCard />,      roles: ['SuperAdmin'] },
    // ── Admin / Staff ──
    { id: 'pos',        label: t('POS Terminal', 'पीओएस टर्मिनल'),        icon: <PointOfSale />,     roles: ['Admin', 'Staff'], category: 'core', action: () => navigate('/pos'), featureKey: 'HasPOS' },
    { id: 'tables',     label: t('Tables', 'टेबल'),              icon: <TableRestaurant />, roles: ['Admin', 'Staff'], category: 'core' },
    { id: 'orders',     label: t('Orders', 'ऑर्डर'),              icon: <ShoppingCart />,    roles: ['Admin', 'Staff'], category: 'core' },
    { id: 'kitchen',    label: t('Kitchen Display (KDS)', 'रसोई डिस्प्ले (KDS)'),icon: <Kitchen />,        roles: ['Admin', 'Staff'], category: 'core', featureKey: 'HasKDS' },
    { id: 'menu',       label: t('Menu Items', 'मेनू आइटम'),          icon: <MenuBook />,        roles: ['Admin', 'Staff'], category: 'operations' },
    { id: 'inventory',  label: t('Inventory', 'इन्वेंटरी'),            icon: <Inventory />,       roles: ['Admin', 'Staff'], category: 'operations', featureKey: 'HasInventory' },
    { id: 'combos',     label: t('Combo Meals', 'कॉम्बो भोजन'),          icon: <FastfoodOutlined />, roles: ['Admin'], category: 'operations' },
    { id: 'coupons',    label: t('Coupons & Discounts', 'कूपन और छूट'), icon: <LocalOffer />,      roles: ['Admin'], category: 'operations' },
    { id: 'gst',       label: t('GST & Billing Config', 'जीएसटी और बिलिंग'), icon: <Receipt />,         roles: ['Admin'], category: 'operations' },
    { id: 'staff',      label: t('Staff Management', 'कर्मचारी प्रबंधन'),    icon: <ManageAccounts />,  roles: ['Admin'], category: 'management' },
    { id: 'approval',   label: t('Order Approval', 'ऑर्डर स्वीकृति'),      icon: <ThumbUpAlt />,      roles: ['Admin'], category: 'core' },
    { id: 'order_flow', label: t('Order Flow Settings', 'ऑर्डर प्रवाह सेटिंग्स'), icon: <SwapHoriz />,      roles: ['Admin'], category: 'management' },
    { id: 'expenses',   label: t('Expense Tracking', 'खर्च ट्रैकिंग'),    icon: <AttachMoney />,     roles: ['Admin'], category: 'management' },
    { id: 'feedback',   label: t('Customer Feedback', 'ग्राहक प्रतिक्रिया'),   icon: <StarRate />,        roles: ['Admin'], category: 'management', featureKey: 'HasCRM' },
    { id: 'tts_templates', label: t('Voice Templates', 'वॉइस टेम्पलेट'), icon: <RecordVoiceOver />, roles: ['Admin'], category: 'management' },
    { id: 'payment',    label: t('Payment Gateways', 'भुगतान गेटवे'),     icon: <CreditCard />,      roles: ['Admin'], category: 'delivery', featureKey: 'HasPOS' },
    { id: 'delivery_api', label: t('Delivery Integration', 'डिलीवरी एकीकरण'), icon: <DeliveryDining />,  roles: ['Admin'], category: 'delivery', featureKey: 'HasDelivery' },
    { id: 'captain',    label: t('Captain App', 'कैप्टन ऐप'),          icon: <PhoneAndroid />,    roles: ['Admin', 'Staff'], category: 'core', action: () => navigate('/captain') },
    { id: 'chatbot',    label: t('AI Food Assistant', 'एआई भोजन सहायक'),    icon: <Avatar src="/images/chatbot_avatar_female.png" className="maya-avatar-float maya-avatar-hoverable" sx={{ width: 22, height: 22 }} />,        roles: ['Admin'], category: 'analytics', featureKey: 'HasCRM' },
    { id: 'reports',    label: t('Reports & GST', 'रिपोर्ट और जीएसटी'),       icon: <BarChart />,        roles: ['Admin'], category: 'analytics', featureKey: 'HasReports' },
    { id: 'settings',   label: t('Settings', 'सेटिंग्स'),            icon: <Settings />,        roles: ['SuperAdmin', 'Admin', 'Staff'], category: 'analytics' },
  ];

  const filteredNavItems = useMemo(() => navItems.filter(item => {
    if (!user || !item.roles.includes(user.role)) return false;

    // SuperAdmin has global access bypass
    if (user.role === UserRole.SuperAdmin) return true;

    // Hide Menu Items tab if plan MaxMenuItems is 0 or less
    if (item.id === 'menu' && planFeatures && planFeatures.MaxMenuItems <= 0) {
      return false;
    }

    // Hide Tables tab if plan MaxTables is 0 or less
    if (item.id === 'tables' && planFeatures && planFeatures.MaxTables <= 0) {
      return false;
    }

    // Hide Staff Management tab if plan MaxUsers is 1 or less (meaning only the admin user is allowed)
    if (item.id === 'staff' && planFeatures && planFeatures.MaxUsers <= 1) {
      return false;
    }

    // If the item requires a specific pricing plan feature, check if active in planFeatures
    if (item.featureKey && planFeatures) {
      const key = item.featureKey;
      return planFeatures[key] === true;
    }

    return true;
  }), [user, planFeatures]);

  // Sidebar — Modern Soft-Gradient Food with enhanced glassmorphism
  const renderSidebar = () => {
    const n = isDark ? neu.dark : neu.light;
    const sidebarGlass = isDark
      ? 'rgba(28,24,22,0.7)'
      : 'rgba(255,255,255,0.4)';

    const renderNavItem = (item: any) => (
      <Tooltip key={item.id} title={!sidebarOpen ? item.label : ''} placement="right" arrow>
        <ListItemButton
          selected={activeSection === item.id}
          onClick={() => { setActiveSection(item.id); if (item.action) item.action(); }}
          sx={{
            mx: 1, mb: 0.5, borderRadius: '14px', minHeight: 44,
            justifyContent: sidebarOpen ? 'initial' : 'center',
            px: sidebarOpen ? 1.5 : 1,
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              background: food.redSoft,
              boxShadow: `0 2px 12px ${food.redGlow}`,
              '& .MuiListItemIcon-root': { color: food.red },
              '& .MuiListItemText-primary': { color: food.red, fontWeight: 700 },
            },
            '&:hover:not(.Mui-selected)': {
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
              boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(26,18,9,0.04)',
            },
          }}>
          <ListItemIcon sx={{
            minWidth: sidebarOpen ? 38 : 'auto',
            color: n.textMuted, justifyContent: 'center',
            '& svg': { fontSize: 20 },
          }}>
            {item.icon}
          </ListItemIcon>
          {sidebarOpen && (
            <ListItemText primary={item.label}
              primaryTypographyProps={{ fontSize: '0.855rem', fontWeight: 500, color: n.textSecondary }} />
          )}
          {sidebarOpen && activeSection === item.id && (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: food.red, ml: 'auto', boxShadow: `0 0 6px ${food.redGlow}` }} />
          )}
        </ListItemButton>
      </Tooltip>
    );

    return (
      <Box sx={{
        width: sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED,
        minHeight: '100vh',
        background: sidebarGlass,
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.6)'}`,
        boxShadow: isDark
          ? '4px 0 40px rgba(0,0,0,0.4)'
          : '4px 0 40px rgba(26,18,9,0.05)',
        transition: 'width 0.3s cubic-bezier(0.16,1,0.3,1)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0,
        zIndex: 1200, overflow: 'hidden',
      }}>
        {/* Logo */}
        <Box sx={{
          p: 2, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 72,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(200,185,170,0.2)'}`,
        }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
            background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 16px ${food.redGlow}`,
          }}>
            <Restaurant sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          {sidebarOpen && (
            <div>
              <Typography variant="h6" sx={{
                fontWeight: 800, fontFamily: 'Space Grotesk', whiteSpace: 'nowrap',
                color: food.red, letterSpacing: '-0.02em',
              }}>
                MenuMaster
              </Typography>
              <Typography variant="caption" sx={{ color: n.textMuted, fontSize: '0.68rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {planFeatures.PlanName || 'RESTAURANT PLATFORM'}
              </Typography>
            </div>
          )}
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1, py: 1.5, overflow: 'auto', '&::-webkit-scrollbar': { width: '3px' } }}>
          {filteredNavItems.filter(item => !item.category).map(item => renderNavItem(item))}

          {user?.role !== 'SuperAdmin' && [
            { id: 'core', label: '🟢 Core Operations' },
            { id: 'operations', label: '🟡 Menu & Inventory' },
            { id: 'management', label: '🔵 Management' },
            { id: 'delivery', label: '🧡 Delivery & Payments' },
            { id: 'analytics', label: '🟣 Analytics & Settings' }
          ].map(cat => {
            const catItems = filteredNavItems.filter(item => item.category === cat.id);
            if (catItems.length === 0) return null;
            const isCollapsed = collapsedCategories.has(cat.id);
            return (
              <Box key={cat.id} sx={{ mt: 1.5, mb: 0.5 }}>
                {sidebarOpen && (
                  <Box
                    onClick={() => toggleCategory(cat.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2.5, py: 0.5, cursor: 'pointer', borderRadius: '10px', mx: 1,
                      userSelect: 'none',
                      transition: 'background 0.15s',
                      '&:hover': {
                        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <Typography variant="caption" sx={{
                      fontSize: '0.68rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                    }}>
                      {cat.label}
                    </Typography>
                    <Box sx={{
                      display: 'flex', alignItems: 'center',
                      color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
                    }}>
                      <ExpandMore sx={{ fontSize: 15 }} />
                    </Box>
                  </Box>
                )}
                {!sidebarOpen && (
                  <Tooltip title={cat.label} placement="right">
                    <Box
                      onClick={() => toggleCategory(cat.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', py: 0.5,
                      }}
                    >
                      <Divider sx={{ width: '60%', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />
                    </Box>
                  </Tooltip>
                )}
                {!isCollapsed && catItems.map(item => renderNavItem(item))}
              </Box>
            );
          })}

          {user?.role === 'SuperAdmin' && filteredNavItems.filter(item => item.category).map(item => renderNavItem(item))}
        </Box>

        {/* Collapse toggle */}
        <Box sx={{ p: 1.5, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(200,185,170,0.2)'}` }}>
          <IconButton onClick={() => setSidebarOpen(!sidebarOpen)}
            sx={{
              width: '100%', borderRadius: '14px', color: n.textMuted,
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(200,185,170,0.25)'}`,
              boxShadow: 'none',
              '&:hover': { background: food.redSoft, color: food.red },
            }}>
            {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
      </Box>
    );
  };

  // Top bar — Enhanced Glassmorphism
  const renderTopBar = () => {
    const n = isDark ? neu.dark : neu.light;
    return (
    <Box sx={{
      height: 72, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', px: 3,
      background: isDark ? 'rgba(28,24,22,0.75)' : 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(32px)',
      WebkitBackdropFilter: 'blur(32px)',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.6)'}`,
      boxShadow: isDark
        ? '0 8px 32px rgba(0,0,0,0.3)'
        : '0 8px 32px rgba(26,18,9,0.04)',
      position: 'sticky', top: 0, zIndex: 1100,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {isMobile && (
          <IconButton onClick={() => setSidebarOpen(!sidebarOpen)}>
            <MenuOpen />
          </IconButton>
        )}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontFamily: '"Space Grotesk", sans-serif',
              color: isDark ? colors.dark.textPrimary : colors.light.textPrimary,
            }}
          >
            {activeSection === 'dashboard' ? 'Dashboard' : navItems.find(n => n.id === activeSection)?.label}
          </Typography>
          {currentRestaurant && (
            <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
              {currentRestaurant.name}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {currentTenant && (
          <Chip
            label={currentTenant.name}
            size="small"
            icon={<Domain sx={{ fontSize: 16 }} />}
            onClick={handleTenantMenuOpen}
            sx={{
              mr: 1, borderRadius: '9999px',
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.3)'}`,
              color: isDark ? food.snowPrimary : food.inkMid,
              fontWeight: 600,
              boxShadow: 'none',
              '& .MuiChip-icon': { color: food.red },
              '&:hover': { background: food.redSoft, borderColor: food.red },
            }}
          />
        )}

        {currentRestaurant ? (
          <Chip
            label={currentRestaurant.name}
            size="small"
            icon={<Restaurant sx={{ fontSize: 16 }} />}
            onClick={handleRestaurantMenuOpen}
            sx={{
              mr: 1, borderRadius: '9999px',
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.3)'}`,
              color: isDark ? food.snowPrimary : food.inkMid,
              fontWeight: 600,
              boxShadow: 'none',
              '& .MuiChip-icon': { color: '#388E3C' },
              '&:hover': { background: 'rgba(56,142,60,0.08)', borderColor: '#388E3C' },
            }}
          />
        ) : currentTenant ? (
          <Chip
            label="Select Restaurant"
            size="small"
            icon={<Restaurant sx={{ fontSize: 16 }} />}
            onClick={() => { loadRestaurants(); handleRestaurantMenuOpen({} as React.MouseEvent<HTMLElement>); }}
            sx={{
              mr: 1, borderRadius: '9999px',
              background: isDark ? 'rgba(255,152,0,0.12)' : 'rgba(255,152,0,0.08)',
              backdropFilter: 'blur(12px)',
              border: `1px solid ${isDark ? 'rgba(255,152,0,0.25)' : 'rgba(255,152,0,0.2)'}`,
              color: '#f57c00',
              fontWeight: 600,
              boxShadow: 'none',
              '& .MuiChip-icon': { color: '#f57c00' },
            }}
          />
        ) : null}

        <IconButton
          onClick={handleThemeMenuOpen}
          size="small"
          sx={{
            color: isDark ? colors.dark.textSecondary : colors.light.textSecondary,
            '&:hover': { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
          }}
        >
          <AutoAwesome fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={themeAnchorEl}
          open={Boolean(themeAnchorEl)}
          onClose={handleThemeMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              borderRadius: '16px',
              minWidth: 180,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              background: isDark ? 'rgba(28,24,22,0.95)' : 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(16px)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
            }
          }}
        >
          <MenuItem 
            onClick={() => selectTheme('dark')}
            selected={dashboardTheme === 'dark'}
            sx={{ gap: 1, fontWeight: 600, py: 1.2 }}
          >
            <DarkMode fontSize="small" sx={{ color: '#ff9800' }} />
            Sleek Dark Mode
          </MenuItem>
          <MenuItem 
            onClick={() => selectTheme('forest')}
            selected={dashboardTheme === 'forest'}
            sx={{ gap: 1, fontWeight: 600, py: 1.2 }}
          >
            <Restaurant fontSize="small" sx={{ color: '#4caf50' }} />
            Forest Light
          </MenuItem>
          <MenuItem 
            onClick={() => selectTheme('glass')}
            selected={dashboardTheme === 'glass'}
            sx={{ gap: 1, fontWeight: 600, py: 1.2 }}
          >
            <AutoAwesome fontSize="small" sx={{ color: '#2196f3' }} />
            Apple Liquid Glass
          </MenuItem>
        </Menu>

        {/* Online / Offline mode toggle */}
        <Tooltip title={forceOffline ? 'Manually set to Offline — click to go Online' : isOnline ? 'System Online — click to force Offline mode' : 'No network connection'}>
          <Box
            onClick={toggleNetworkMode}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              mr: 1.5, px: 1.25, py: 0.4,
              borderRadius: '20px',
              cursor: 'pointer',
              border: `1.5px solid ${isOnline ? '#4caf50' : '#f44336'}`,
              background: isOnline
                ? (isDark ? 'rgba(76,175,80,0.12)' : 'rgba(76,175,80,0.08)')
                : (isDark ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)'),
              transition: 'all 0.2s',
              '&:hover': {
                background: isOnline
                  ? (isDark ? 'rgba(76,175,80,0.22)' : 'rgba(76,175,80,0.15)')
                  : (isDark ? 'rgba(244,67,54,0.22)' : 'rgba(244,67,54,0.15)'),
              },
              userSelect: 'none',
            }}
          >
            {/* Toggle track */}
            <Box sx={{
              width: 32, height: 16, borderRadius: '8px', position: 'relative',
              background: isOnline ? '#4caf50' : '#f44336',
              transition: 'background 0.2s',
              flexShrink: 0,
            }}>
              <Box sx={{
                position: 'absolute', top: 2,
                left: isOnline ? 'calc(100% - 14px)' : '2px',
                width: 12, height: 12, borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </Box>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              color: isOnline ? '#4caf50' : '#f44336',
              lineHeight: 1,
            }}>
              {isOnline ? 'Online' : 'Offline'}
            </Typography>
            {!isOnline && unsyncedCount > 0 && (
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', background: '#f44336', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 9, fontWeight: 800, color: '#fff' }}>{unsyncedCount}</Typography>
              </Box>
            )}
            {isOnline && unsyncedCount > 0 && (
              <SyncIcon sx={{ fontSize: 13, color: '#ff9800', animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            )}
          </Box>
        </Tooltip>

        <Button
          onClick={() => setAppLang(appLang === 'en' ? 'hi' : 'en')}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: '12px',
            borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
            color: isDark ? '#ffffff' : colors.light.textPrimary,
            fontWeight: 700,
            fontSize: '0.72rem',
            px: 1.5,
            py: 0.5,
            mr: 1,
            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
            '&:hover': { background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)' },
            textTransform: 'none'
          }}
        >
          {appLang === 'en' ? 'ENGLISH' : 'हिन्दी'}
        </Button>

        <IconButton
          size="small"
          sx={{
            color: isDark ? colors.dark.textSecondary : colors.light.textSecondary,
            '&:hover': { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
          }}
        >
          <Badge badgeContent={3} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18 } }}>
            <Notifications fontSize="small" />
          </Badge>
        </IconButton>

        <Box
          onClick={handleMenuOpen}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, ml: 1,
            p: 0.75, pr: 2, borderRadius: '9999px', cursor: 'pointer',
            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.3)'}`,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(26,18,9,0.05)',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.35)' : '0 4px 16px rgba(26,18,9,0.08)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700,
              background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
            }}
          >
            {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2, color: isDark ? food.snowPrimary : food.inkDark }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isDark ? food.snowMuted : food.inkLight }}>
              {user?.role}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
    );
  };

  // Stats cards — Glass morphism food
  const renderStats = () => {
    const n = isDark ? neu.dark : neu.light;
    const glassBg = isDark ? 'rgba(28,24,22,0.72)' : 'rgba(255,255,255,0.82)';
    const glassBorder = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)';
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2.5, mb: 3 }}>
        {stats.map((stat, index) => (
          <div>
            <Card sx={{
              p: 2.5, cursor: 'pointer',
              background: glassBg,
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: `1px solid ${glassBorder}`,
              borderRadius: '32px',
              boxShadow: isDark
                ? '0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.25)'
                : '0 8px 32px rgba(26,18,9,0.07), 0 2px 8px rgba(26,18,9,0.04)',
              transition: 'all 0.25s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: isDark
                  ? '0 16px 48px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.3)'
                  : '0 16px 48px rgba(26,18,9,0.11), 0 4px 16px rgba(26,18,9,0.06)',
              },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{
                  width: 46, height: 46, borderRadius: '14px',
                  background: `${stat.color}15`,
                  border: `1px solid ${stat.color}25`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.color,
                }}>
                  {stat.icon}
                </Box>
                {stat.change && (
                  <Chip label={stat.change} size="small" sx={{
                    height: 24, fontSize: '0.7rem', fontWeight: 700, borderRadius: '9999px',
                    background: stat.change.startsWith('+') ? 'rgba(56,142,60,0.12)' : 'rgba(224,42,42,0.1)',
                    color: stat.change.startsWith('+') ? '#388E3C' : food.red,
                    border: `1px solid ${stat.change.startsWith('+') ? 'rgba(56,142,60,0.2)' : food.redSoft}`,
                    boxShadow: 'none',
                  }} />
                )}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', letterSpacing: '-0.03em', mb: 0.5 }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" sx={{ color: n.textMuted, fontWeight: 500 }}>
                {stat.label}
              </Typography>
              {/* Colored bottom accent line */}
              <Box sx={{ mt: 2, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${stat.color} 0%, ${stat.color}30 100%)` }} />
            </Card>
          </div>
        ))}
      </Box>
    );
  };

  // Quick actions - Role-specific
  const renderQuickActions = () => {
    const superAdminActions = [
      { label: 'Create Admin',       icon: <PersonAdd />,       color: food.red,    primary: true,  action: () => { setActiveSection('admins'); setCreateUserDialogOpen(true); } },
      { label: 'Pricing Plans',      icon: <Sell />,            color: '#388E3C',   primary: false, action: () => setActiveSection('pricing') },
      { label: 'Manage Restaurants', icon: <Domain />,          color: '#F57C00',   primary: false, action: () => setActiveSection('tenants') },
      { label: 'Revenue Report',     icon: <TrendingUp />,      color: '#1565C0',   primary: false, action: () => setActiveSection('revenue') },
    ];

    const restaurantActions = [
      { label: 'New Order',    icon: <PointOfSale />,     color: food.red,    primary: true,  action: () => navigate('/pos') },
      { label: 'View Tables',  icon: <TableRestaurant />, color: '#388E3C',   primary: false, action: () => setActiveSection('tables') },
      { label: 'View Menu',    icon: <MenuBook />,        color: '#F57C00',   primary: false, action: () => setActiveSection('menu') },
      { label: 'Process Bill', icon: <Receipt />,         color: '#1565C0',   primary: false, action: () => setActiveSection('orders') },
    ];

    const actions = user?.role === UserRole.SuperAdmin ? superAdminActions : restaurantActions;
    const n = isDark ? neu.dark : neu.light;
    const glassBg = isDark ? 'rgba(28,24,22,0.72)' : 'rgba(255,255,255,0.82)';
    const chartColor = '#7C3AED';

    return (
      <div>
        <Card sx={{
          p: 3, mb: 3,
          background: glassBg, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)'}`,
          borderRadius: '32px',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 8px 32px rgba(26,18,9,0.07)',
        }}>
          {/* Accent-line heading */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ width: 3, height: 22, borderRadius: '2px', background: food.red, flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', letterSpacing: '-0.02em' }}>
              Quick Actions
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {actions.map(action => (
              <Button key={action.label} startIcon={action.icon} onClick={action.action}
                variant={action.primary ? 'contained' : 'outlined'}
                sx={action.primary ? {
                  borderRadius: '9999px', py: 1.2, px: 2.5, fontWeight: 700,
                  background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
                  boxShadow: `0 4px 16px ${food.redGlow}`,
                  color: '#fff',
                  '&:hover': { boxShadow: `0 8px 24px ${food.redGlow}`, transform: 'translateY(-2px)' },
                  '& .MuiButton-startIcon svg': { color: '#fff' },
                } : {
                  borderRadius: '9999px', py: 1.2, px: 2.5, fontWeight: 600,
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(8px)',
                  borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.4)',
                  color: action.color,
                  '& .MuiButton-startIcon svg': { color: action.color },
                  '&:hover': {
                    borderColor: action.color,
                    background: `${action.color}10`,
                    boxShadow: `0 4px 16px ${action.color}25`,
                    transform: 'translateY(-2px)',
                  },
                }}>
                {action.label}
              </Button>
            ))}
            {/* Sales Graph toggle — only for restaurant users */}
            {user?.role !== UserRole.SuperAdmin && (
              <Button
                startIcon={<BarChart />}
                onClick={() => setShowSalesGraph(prev => !prev)}
                variant="outlined"
                sx={{
                  borderRadius: '9999px', py: 1.2, px: 2.5, fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s ease',
                  ...(showSalesGraph ? {
                    background: `${chartColor}18`,
                    borderColor: chartColor,
                    color: chartColor,
                    boxShadow: `0 4px 16px ${chartColor}30`,
                    '& .MuiButton-startIcon svg': { color: chartColor },
                    '&:hover': { background: `${chartColor}28`, boxShadow: `0 6px 20px ${chartColor}40`, transform: 'translateY(-2px)' },
                  } : {
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
                    borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.4)',
                    color: chartColor,
                    '& .MuiButton-startIcon svg': { color: chartColor },
                    '&:hover': { borderColor: chartColor, background: `${chartColor}10`, boxShadow: `0 4px 16px ${chartColor}25`, transform: 'translateY(-2px)' },
                  }),
                }}
              >
                Sales Graph
              </Button>
            )}
          </Box>
        </Card>
      </div>
    );
  };

  // Sales graph — tabbed: day-wise & top items
  const renderSalesGraph = () => {
    if (user?.role === UserRole.SuperAdmin) return null;
    const n = isDark ? neu.dark : neu.light;
    const glassBg = isDark ? 'rgba(28,24,22,0.72)' : 'rgba(255,255,255,0.82)';
    const accent = food.red;
    const accentSoft = isDark ? 'rgba(220,53,69,0.18)' : 'rgba(220,53,69,0.10)';
    const purple = '#7C3AED';
    const grid = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const tooltipStyle = {
      background: isDark ? '#1c1816' : '#fff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
      borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', fontFamily: 'Inter', fontSize: 13,
    };

    const totalRevenue = salesChartData.reduce((s, d) => s + d.revenue, 0);
    const totalOrders  = salesChartData.reduce((s, d) => s + d.orders, 0);
    const peakDay      = salesChartData.reduce((best, d) => d.revenue > best.revenue ? d : best, { day: '', revenue: 0, orders: 0 });
    const totalQty     = topItemsData.reduce((s, d) => s + d.quantity, 0);

    const tabSx = (active: boolean, color: string) => ({
      borderRadius: '9999px', py: 0.6, px: 2, fontWeight: 600, fontSize: '0.8rem', minWidth: 0,
      transition: 'all 0.18s ease',
      ...(active ? {
        background: `${color}18`, borderColor: color, color,
        '& .MuiButton-startIcon svg': { color },
      } : {
        background: 'transparent',
        borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        color: n.textMuted,
        '&:hover': { borderColor: color, background: `${color}0D`, color },
        '& .MuiButton-startIcon svg': { color: n.textMuted },
      }),
    });

    // Bar colours for top-10: rank 1 brightest, fading down
    const itemColors = [
      '#DC2626','#EA580C','#D97706','#65A30D','#16A34A',
      '#0891B2','#2563EB','#7C3AED','#DB2777','#6B7280',
    ];

    return (
      <Card sx={{
        p: 3, mb: 3,
        background: glassBg, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)'}`,
        borderRadius: '32px',
        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 8px 32px rgba(26,18,9,0.07)',
      }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 3, height: 22, borderRadius: '2px', background: salesGraphTab === 'daily' ? accent : purple, flexShrink: 0, transition: 'background 0.2s' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                {salesGraphTab === 'daily' ? 'Sales — Last 7 Days' : 'Top 10 Items — Last 30 Days'}
              </Typography>
              <Typography variant="caption" sx={{ color: n.textMuted }}>
                {salesGraphTab === 'daily' ? 'Revenue & order trend' : 'Ranked by quantity sold'}
              </Typography>
            </Box>
          </Box>

          {/* KPI pills */}
          {salesGraphTab === 'daily' ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: n.textMuted, display: 'block' }}>7-day Revenue</Typography>
                <Typography sx={{ fontWeight: 800, fontFamily: 'Space Grotesk', color: n.textPrimary, fontSize: '1rem' }}>₹{totalRevenue.toLocaleString('en-IN')}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: n.textMuted, display: 'block' }}>Orders</Typography>
                <Typography sx={{ fontWeight: 800, fontFamily: 'Space Grotesk', color: n.textPrimary, fontSize: '1rem' }}>{totalOrders}</Typography>
              </Box>
              {peakDay.revenue > 0 && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: n.textMuted, display: 'block' }}>Peak Day</Typography>
                  <Typography sx={{ fontWeight: 700, color: accent, fontSize: '0.88rem' }}>{peakDay.day}</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: n.textMuted, display: 'block' }}>Items tracked</Typography>
                <Typography sx={{ fontWeight: 800, fontFamily: 'Space Grotesk', color: n.textPrimary, fontSize: '1rem' }}>{topItemsData.length}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: n.textMuted, display: 'block' }}>Total qty sold</Typography>
                <Typography sx={{ fontWeight: 800, fontFamily: 'Space Grotesk', color: n.textPrimary, fontSize: '1rem' }}>{totalQty}</Typography>
              </Box>
              {topItemsData[0] && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: n.textMuted, display: 'block' }}>Best seller</Typography>
                  <Typography sx={{ fontWeight: 700, color: purple, fontSize: '0.88rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topItemsData[0].name}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ── Tab switcher ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
          <Button variant="outlined" startIcon={<TrendingUp />} onClick={() => setSalesGraphTab('daily')} sx={tabSx(salesGraphTab === 'daily', accent)}>
            Day-wise
          </Button>
          <Button variant="outlined" startIcon={<BarChart />} onClick={() => setSalesGraphTab('items')} sx={tabSx(salesGraphTab === 'items', purple)}>
            Top Items
          </Button>
        </Box>

        {/* ══════════════ DAY-WISE TAB ══════════════ */}
        {salesGraphTab === 'daily' && (
          <>
            {salesChartLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress size={32} sx={{ color: accent }} />
              </Box>
            ) : totalRevenue === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 1 }}>
                <TrendingUp sx={{ fontSize: 40, color: n.textMuted, opacity: 0.35 }} />
                <Typography variant="body2" sx={{ color: n.textMuted }}>No sales data yet — place some orders to see the graph</Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={accent} stopOpacity={0.35} />
                        <stop offset="95%" stopColor={accent} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: n.textMuted, fontSize: 11, fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: n.textMuted, fontSize: 11, fontFamily: 'Inter' }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`} width={52} />
                    <RechartsTooltip contentStyle={tooltipStyle} labelStyle={{ fontWeight: 700, color: n.textPrimary }}
                      formatter={(value: any, name: any) => name === 'revenue' ? [`₹${Number(value).toLocaleString('en-IN')}`, 'Revenue'] : [value, 'Orders']} />
                    <Area type="monotone" dataKey="revenue" stroke={accent} strokeWidth={2.5} fill="url(#salesGradient)"
                      dot={{ fill: accent, r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* Mini order-count bars */}
            {!salesChartLoading && totalOrders > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}` }}>
                <Typography variant="caption" sx={{ color: n.textMuted, mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem' }}>
                  Order count per day
                </Typography>
                <Box sx={{ width: '100%', height: 52 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={salesChartData} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                      <Bar dataKey="orders" fill={accentSoft} radius={[4, 4, 0, 0]} stroke={accent} strokeWidth={1} />
                      <XAxis dataKey="day" hide />
                      <RechartsTooltip contentStyle={{ ...tooltipStyle, fontSize: 12 }} formatter={(v: any) => [v, 'Orders']}
                        labelStyle={{ fontWeight: 700, color: n.textPrimary }} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            )}
          </>
        )}

        {/* ══════════════ TOP ITEMS TAB ══════════════ */}
        {salesGraphTab === 'items' && (
          <>
            {topItemsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
                <CircularProgress size={32} sx={{ color: purple }} />
              </Box>
            ) : topItemsData.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 1 }}>
                <BarChart sx={{ fontSize: 40, color: n.textMuted, opacity: 0.35 }} />
                <Typography variant="body2" sx={{ color: n.textMuted }}>No item data for the last 30 days</Typography>
              </Box>
            ) : (
              <>
                {/* Horizontal bar chart */}
                <Box sx={{ width: '100%', height: topItemsData.length * 38 + 24 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart layout="vertical" data={topItemsData} margin={{ top: 0, right: 56, left: 8, bottom: 0 }}>
                      <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke={grid} />
                      <XAxis type="number" tick={{ fill: n.textMuted, fontSize: 11, fontFamily: 'Inter' }} axisLine={false} tickLine={false}
                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
                      <YAxis type="category" dataKey="name" width={148}
                        tick={{ fill: n.textPrimary, fontSize: 11.5, fontFamily: 'Inter', fontWeight: 600 }}
                        axisLine={false} tickLine={false}
                        tickFormatter={(v: string) => v.length > 18 ? v.slice(0, 17) + '…' : v} />
                      <RechartsTooltip contentStyle={tooltipStyle} labelStyle={{ fontWeight: 700, color: n.textPrimary }}
                        formatter={(value: any, name: any) => name === 'revenue'
                          ? [`₹${Number(value).toLocaleString('en-IN')}`, 'Revenue']
                          : [value, 'Qty sold']} />
                      <Bar dataKey="quantity" radius={[0, 6, 6, 0]} label={{ position: 'right', fontSize: 11, fill: n.textMuted, formatter: (v: any) => String(v) }}>
                        {topItemsData.map((_, i) => (
                          <Cell key={i} fill={itemColors[i] ?? '#6B7280'} fillOpacity={1 - i * 0.05} />
                        ))}
                      </Bar>
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </Box>

                {/* ── Daily breakdown (stacked bar) ── */}
                <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}` }}>
                  <Typography variant="caption" sx={{ color: n.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem', mb: 1.5, display: 'block' }}>
                    Daily qty sold — last 7 days (top 5 items)
                  </Typography>
                  {dailyItemsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 140 }}>
                      <CircularProgress size={24} sx={{ color: purple }} />
                    </Box>
                  ) : dailyItemsData.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 100 }}>
                      <Typography variant="caption" sx={{ color: n.textMuted }}>No daily breakdown available</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: 180 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart data={dailyItemsData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                          <XAxis dataKey="day" tick={{ fill: n.textMuted, fontSize: 11, fontFamily: 'Inter' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: n.textMuted, fontSize: 11, fontFamily: 'Inter' }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                          <RechartsTooltip
                            contentStyle={tooltipStyle}
                            labelStyle={{ fontWeight: 700, color: n.textPrimary }}
                            formatter={(value: any, name: any) => [value, name]}
                          />
                          <RechartsLegend
                            wrapperStyle={{ fontSize: 11, fontFamily: 'Inter', paddingTop: 8 }}
                            formatter={(value) => (
                              <span style={{ color: n.textMuted, fontWeight: 600 }}>
                                {String(value).length > 16 ? String(value).slice(0, 15) + '…' : value}
                              </span>
                            )}
                          />
                          {topItemNames.map((name, i) => (
                            <Bar key={name} dataKey={name} stackId="items" fill={itemColors[i] ?? '#6B7280'}
                              radius={i === topItemNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                          ))}
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Box>

                {/* Ranked list */}
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  <Typography variant="caption" sx={{ color: n.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem', mb: 0.5, display: 'block' }}>
                    Full Ranking
                  </Typography>
                  {topItemsData.map((item, i) => {
                    const pct = topItemsData[0].quantity > 0 ? (item.quantity / topItemsData[0].quantity) * 100 : 0;
                    return (
                      <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Rank badge */}
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          background: i === 0 ? 'linear-gradient(135deg,#F59E0B,#D97706)' : i === 1 ? 'linear-gradient(135deg,#9CA3AF,#6B7280)' : i === 2 ? 'linear-gradient(135deg,#CD7F32,#A0522D)' : `${itemColors[i] ?? '#6B7280'}22`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: i < 3 ? '#fff' : itemColors[i] ?? '#6B7280' }}>
                            {i + 1}
                          </Typography>
                        </Box>

                        {/* Name + bar */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: n.textPrimary, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: n.textMuted, fontFamily: 'Space Grotesk', whiteSpace: 'nowrap' }}>
                              {item.quantity} sold &nbsp;·&nbsp; ₹{item.revenue.toLocaleString('en-IN')}
                            </Typography>
                          </Box>
                          <Box sx={{ height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                            <Box sx={{ height: '100%', borderRadius: 2, width: `${pct}%`, background: itemColors[i] ?? '#6B7280', transition: 'width 0.6s ease' }} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </>
        )}
      </Card>
    );
  };

  // Recent orders — Glass food
  const renderRecentOrders = () => {
    const n = isDark ? neu.dark : neu.light;
    const glassBg = isDark ? 'rgba(28,24,22,0.72)' : 'rgba(255,255,255,0.82)';
    return (
      <div>
        <Card sx={{
          p: 3,
          background: glassBg, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)'}`,
          borderRadius: '32px',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 8px 32px rgba(26,18,9,0.07)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 3, height: 22, borderRadius: '2px', background: food.red, flexShrink: 0 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', letterSpacing: '-0.02em' }}>
                Recent Orders
              </Typography>
            </Box>
            <Button size="small" onClick={() => setActiveSection('orders')}
              sx={{
                borderRadius: '9999px', fontWeight: 600, color: food.red,
                px: 2, fontSize: '0.8rem',
                '&:hover': { background: food.redSoft },
              }}>
              View All
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {recentOrders.map((order, index) => (
              <div>
                <Box sx={{
                  p: 1.8, borderRadius: '16px',
                  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.6)',
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(200,185,170,0.2)'}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.85)',
                    transform: 'translateX(4px)',
                  },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: getStatusColor(order.status),
                        boxShadow: `0 0 8px ${getStatusColor(order.status)}60`,
                      }} />
                      {order.source === 'QR' && <QrCode sx={{ fontSize: 12, color: food.red }} />}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: n.textPrimary, letterSpacing: '-0.01em' }}>
                        {order.id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: n.textMuted }}>
                        Table {order.table} • {order.time}{order.source === 'QR' ? ' • QR Order' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: n.textPrimary, fontFamily: 'Space Grotesk' }}>
                      ₹{order.amount}
                    </Typography>
                    <Chip label={order.status} size="small"
                      sx={{
                        height: 26, fontSize: '0.7rem', fontWeight: 600, borderRadius: '9999px',
                        background: `${getStatusColor(order.status)}15`,
                        color: getStatusColor(order.status),
                        border: `1px solid ${getStatusColor(order.status)}30`,
                        boxShadow: 'none',
                      }} />
                  </Box>
                </Box>
              </div>
            ))}
          </Box>
        </Card>
      </div>
    );
  };

  // Admin management for SuperAdmin (only shows Admin-role users, not Staff)
  const renderUsersSection = () => {
    if (user?.role !== UserRole.SuperAdmin || activeSection !== 'admins') return null;
    const adminUsers = users.filter(u => u.role === UserRole.Admin);

    return (
      <div>
        <Card
          sx={{
            p: 3,
            mb: 3,
            background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`,
            borderRadius: '20px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: `${colors.accent.indigo}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.accent.indigo,
                }}
              >
                <People />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                Admin Management
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setCreateUserDialogOpen(true)}
              sx={{
                borderRadius: '10px',
                background: colors.accent.gradient,
                boxShadow: shadows.glow.primary,
                '&:hover': { boxShadow: shadows.glow.primaryLg },
              }}
            >
              Create Admin
            </Button>
          </Box>

          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: colors.accent.indigo }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {adminUsers.map((userItem) => (
                <Box
                  key={userItem.id}
                  sx={{
                    p: 2,
                    borderRadius: '14px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        background: userItem.role === UserRole.SuperAdmin ? `${colors.accent.indigo}20` : `${colors.accent.violet}20`,
                        color: userItem.role === UserRole.SuperAdmin ? colors.accent.indigo : colors.accent.violet,
                      }}
                    >
                      {userItem.firstName?.charAt(0)}{userItem.lastName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                        {userItem.firstName} {userItem.lastName}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        {userItem.email} • @{userItem.username} • Tenant: {tenants.find(t => t.id === userItem.tenantId)?.name || 'No Tenant'}
                      </Typography>
                      {(user?.role === UserRole.SuperAdmin || user?.role === UserRole.Admin) && userItem.passwordHash && (
                        <Typography variant="caption" sx={{ display: 'block', color: colors.accent.indigo, mt: 0.5, fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>
                          🔑 Password: {userItem.passwordHash}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={userItem.role}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: userItem.role === UserRole.SuperAdmin ? `${colors.accent.indigo}15` : `${colors.accent.violet}15`,
                        color: userItem.role === UserRole.SuperAdmin ? colors.accent.indigo : colors.accent.violet,
                      }}
                    />
                    <Chip
                      label={userItem.status === UserStatus.Active ? 'Active' : 'Inactive'}
                      size="small"
                      onClick={() => handleToggleUserStatus(userItem.id, userItem.status)}
                      clickable
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: userItem.status === UserStatus.Active ? `${colors.success.main}15` : `${colors.error.main}15`,
                        color: userItem.status === UserStatus.Active ? colors.success.main : colors.error.main,
                        '&:hover': {
                          background: userItem.status === UserStatus.Active ? `${colors.success.main}25` : `${colors.error.main}25`,
                        },
                      }}
                      icon={userItem.status === UserStatus.Active ? <ToggleOn sx={{ fontSize: 16 }} /> : <ToggleOff sx={{ fontSize: 16 }} />}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleEditUserClick(userItem)}
                      sx={{
                        color: colors.accent.indigo,
                        padding: '4px',
                        background: isDark ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                        border: `1px solid ${colors.accent.indigo}20`,
                        '&:hover': {
                          background: isDark ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)',
                        }
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(userItem.id, `${userItem.firstName} ${userItem.lastName}`)}
                      sx={{
                        color: colors.error.main,
                        padding: '4px',
                        background: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                        border: `1px solid ${colors.error.main}20`,
                        '&:hover': {
                          background: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {adminUsers.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                    No admin users found. Create your first admin.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </div>
    );
  };

  // Tenant management for SuperAdmin
  const renderTenantSection = () => {
    if (user?.role !== UserRole.SuperAdmin || activeSection !== 'tenants') return null;

    return (
      <div>
        <Card
          sx={{
            p: 3,
            mb: 3,
            background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`,
            borderRadius: '20px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: `${colors.accent.indigo}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.accent.indigo,
                }}
              >
                <Domain />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                Tenant Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddBusiness />}
                onClick={() => setCreateRestaurantDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  background: colors.accent.gradient,
                  boxShadow: shadows.glow.primary,
                }}
              >
                Create Restaurant
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleTenantMenuOpen}
                sx={{
                  borderRadius: '10px',
                  borderColor: `${colors.accent.indigo}30`,
                  color: colors.accent.indigo,
                  '&:hover': { borderColor: colors.accent.indigo, background: `${colors.accent.indigo}10` },
                }}
              >
                Switch Tenant
              </Button>
            </Box>
          </Box>

          {loadingTenants ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: colors.accent.indigo }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {tenants.map((tenant) => (
                <Box
                  key={tenant.id}
                  sx={{
                    p: 2,
                    borderRadius: '14px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        background: `${colors.accent.violet}20`,
                        color: colors.accent.violet,
                      }}
                    >
                      {tenant.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                        {tenant.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        {tenant.subdomain}
                      </Typography>
                      {/* Plan & Expiry row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                        {/* Active Plan chip */}
                        {tenant.planId ? (() => {
                          const plan = pricingPlans.find(p => p.id === tenant.planId);
                          return plan ? (
                            <Chip
                              label={plan.name}
                              size="small"
                              icon={<Sell sx={{ fontSize: '12px !important' }} />}
                              sx={{
                                height: 20,
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                background: `${colors.accent.indigo}18`,
                                color: colors.accent.indigo,
                                '& .MuiChip-icon': { color: colors.accent.indigo },
                                borderRadius: '6px',
                              }}
                            />
                          ) : (
                            <Chip
                              label={`Plan ID: ${tenant.planId.slice(0, 8)}…`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, borderRadius: '6px', opacity: 0.6 }}
                            />
                          );
                        })() : (
                          <Chip
                            label="No Plan"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 600,
                              background: `${colors.error.main}12`,
                              color: colors.error.main,
                              borderRadius: '6px',
                            }}
                          />
                        )}
                        {/* Expiry date chip */}
                        {tenant.planExpiry ? (() => {
                          const expiry = new Date(tenant.planExpiry as string);
                          const now = new Date();
                          const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                          const isExpired = daysLeft < 0;
                          const isWarning = daysLeft >= 0 && daysLeft <= 7;
                          const color = isExpired ? colors.error.main : isWarning ? colors.warning.main : colors.success.main;
                          return (
                            <Chip
                              label={
                                isExpired
                                  ? `Expired ${Math.abs(daysLeft)}d ago`
                                  : isWarning
                                  ? `Expires in ${daysLeft}d`
                                  : expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                              }
                              size="small"
                              icon={<CalendarToday sx={{ fontSize: '11px !important' }} />}
                              sx={{
                                height: 20,
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                background: `${color}15`,
                                color,
                                '& .MuiChip-icon': { color },
                                borderRadius: '6px',
                              }}
                            />
                          );
                        })() : null}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={tenant.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      clickable
                      onClick={() => handleToggleTenantStatus(tenant.id, tenant.isActive)}
                      sx={{
                        height: 26,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: tenant.isActive ? `${colors.success.main}15` : `${colors.error.main}15`,
                        color: tenant.isActive ? colors.success.main : colors.error.main,
                        '&:hover': {
                          background: tenant.isActive ? `${colors.success.main}25` : `${colors.error.main}25`,
                        },
                      }}
                      icon={tenant.isActive ? <ToggleOn sx={{ fontSize: 16 }} /> : <ToggleOff sx={{ fontSize: 16 }} />}
                    />
                    <Tooltip title={tenant.canAdjustSales ? "Disable Cash Adjust" : "Enable Cash Adjust"}>
                      <Chip
                        label="Cash Adj"
                        size="small"
                        clickable
                        onClick={() => handleToggleCashAdjust(tenant)}
                        sx={{
                          height: 26,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: tenant.canAdjustSales ? `${colors.accent.indigo}15` : 'rgba(0,0,0,0.05)',
                          color: tenant.canAdjustSales ? colors.accent.indigo : colors.light.textMuted,
                          border: `1px solid ${tenant.canAdjustSales ? `${colors.accent.indigo}30` : 'transparent'}`,
                          '&:hover': {
                            background: tenant.canAdjustSales ? `${colors.accent.indigo}25` : 'rgba(0,0,0,0.08)',
                          },
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Renew/Pay Plan">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRenewPlanDialog(tenant)}
                        sx={{
                          color: colors.success.main,
                          '&:hover': { background: `${colors.success.main}15` },
                        }}
                      >
                        <CreditCard sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditTenant(tenant)}
                      sx={{
                        color: colors.accent.indigo,
                        '&:hover': { background: `${colors.accent.indigo}15` },
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTenant(tenant.id, tenant.name)}
                      sx={{
                        color: colors.error.main,
                        '&:hover': { background: `${colors.error.main}15` },
                      }}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Card>
      </div>
    );
  };

  const renderOrdersSection = () => {
    if (activeSection !== 'orders') return null;

    const n = isDark ? neu.dark : neu.light;
    const glassBg = isDark ? 'rgba(28,24,22,0.72)' : 'rgba(255,255,255,0.82)';

    const getSourceIcon = (src: any) => {
      switch (String(src).toLowerCase()) {
        case 'pos': return '🖥️ POS';
        case 'qr': return '📱 QR Self';
        case 'swiggy': return '🛵 Swiggy';
        case 'zomato': return '🛵 Zomato';
        default: return '📋 Captain';
      }
    };

    return (
      <div>
        <Card sx={{
          p: 4,
          background: glassBg, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.9)'}`,
          borderRadius: '32px',
          boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.35)' : '0 16px 48px rgba(26,18,9,0.07)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', letterSpacing: '-0.02em', mb: 0.5 }}>
                Order Management
              </Typography>
              <Typography variant="body2" sx={{ color: n.textMuted }}>
                Monitor and manage all active, served, and settled orders placed via Captain App, POS, or QR.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={loadRealOrders}
              disabled={loadingOrders}
              sx={{
                borderRadius: '12px',
                background: colors.accent.gradient,
                boxShadow: shadows.glow.primary,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Refresh Orders
            </Button>
          </Box>

          {loadingOrders ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
              <CircularProgress size={44} sx={{ color: colors.accent.indigo }} />
              <Typography sx={{ color: n.textMuted, fontWeight: 600 }}>Loading live orders...</Typography>
            </Box>
          ) : ordersList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 12 }}>
              <ShoppingCart sx={{ fontSize: 64, color: n.textMuted, mb: 2, opacity: 0.5 }} />
              <Typography sx={{ color: n.textPrimary, fontWeight: 700, mb: 0.5 }}>No Orders Found</Typography>
              <Typography variant="caption" sx={{ color: n.textMuted }}>
                Place orders from POS, Captain App, or QR code to view them here.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {ordersList.map((order) => {
                const dateStr = new Date(order.orderDate || order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const fullDateStr = new Date(order.orderDate || order.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' });
                return (
                  <Box
                    key={order.id}
                    sx={{
                      p: 2.5,
                      borderRadius: '20px',
                      background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', md: 'center' },
                      gap: 2.5,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
                        border: `1px solid ${colors.accent.indigo}20`,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '12px',
                        background: `${getStatusColor(order.status)}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: getStatusColor(order.status),
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        fontFamily: 'Space Grotesk'
                      }}>
                        {order.table?.tableNumber || order.tableNumber || 'N/A'}
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: n.textPrimary }}>
                            #{order.orderNumber || order.id.slice(0, 8).toUpperCase()}
                          </Typography>
                          <Chip 
                            label={getSourceIcon(order.source || 'POS')} 
                            size="small" 
                            sx={{ 
                              height: 20, fontSize: '0.65rem', fontWeight: 700, 
                              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                              color: n.textSecondary
                            }} 
                          />
                          <Chip 
                            label={order.status} 
                            size="small" 
                            sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 700,
                              background: `${getStatusColor(order.status)}15`,
                              color: getStatusColor(order.status),
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: n.textMuted, display: 'block', mb: 1 }}>
                          Placed at {dateStr} on {fullDateStr} • {order.orderItems?.length || order.items?.length || 0} items
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {(order.orderItems || order.items || []).map((oi: any, idx: number) => (
                            <Chip
                              key={idx}
                              label={`${oi.menuItem?.name || oi.menuItemName || 'Item'} x ${oi.quantity}`}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                height: 22, fontSize: '0.7rem', 
                                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                                color: n.textSecondary 
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'flex-end' }, flexDirection: 'column', gap: 1.2, alignSelf: { xs: 'flex-end', md: 'center' } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk' }}>
                        ₹{order.totalAmount}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {order.status !== 'Completed' && order.status !== 'Cancelled' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={async () => {
                                try {
                                  await apiService.updateOrderStatus(order.id, 'Completed' as any);
                                  setSnackbar({ open: true, message: 'Order marked as Completed successfully!', severity: 'success' });
                                  loadRealOrders();
                                } catch (err) {
                                  setSnackbar({ open: true, message: 'Failed to update order status.', severity: 'error' });
                                }
                              }}
                              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                            >
                              Settle
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={async () => {
                                try {
                                  await apiService.updateOrderStatus(order.id, 'Cancelled' as any);
                                  setSnackbar({ open: true, message: 'Order marked as Cancelled successfully!', severity: 'warning' });
                                  loadRealOrders();
                                } catch (err) {
                                  setSnackbar({ open: true, message: 'Failed to cancel order.', severity: 'error' });
                                }
                              }}
                              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>
      </div>
    );
  };

  // Tables management for Admin/Staff
  const renderTablesSection = () => {
    if (activeSection !== 'tables') return null;

    return (
      <div>
        <Card
          sx={{
            p: 3,
            mb: 3,
            background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`,
            borderRadius: '20px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: `${colors.accent.indigo}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.accent.indigo,
                }}
              >
                <TableRestaurant />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                Table Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddBusiness />}
                onClick={() => setCreateTableDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  background: colors.accent.gradient,
                  boxShadow: shadows.glow.primary,
                  '&:hover': { boxShadow: shadows.glow.primaryLg },
                }}
              >
                Add Table
              </Button>
              <Button
                variant="outlined"
                startIcon={<DesignServices />}
                onClick={() => setVisualDesignerDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  border: `2px solid ${colors.accent.indigo}`,
                  color: colors.accent.indigo,
                  '&:hover': {
                    border: `2px solid ${colors.accent.indigo}`,
                    background: `${colors.accent.indigo}10`,
                  },
                }}
              >
                Visual Designer
              </Button>
            </Box>
          </Box>

          {loadingTables ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: colors.accent.indigo }} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {tables.map((table) => (
                <Box
                  key={table.id}
                  sx={{
                    p: 2,
                    borderRadius: '14px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          background: table.status === TableStatus.Available ? `${colors.success.main}20` :
                                     table.status === TableStatus.Occupied ? `${colors.error.main}20` :
                                     table.status === TableStatus.Reserved ? `${colors.warning.main}20` : `${colors.accent.indigo}20`,
                          color: table.status === TableStatus.Available ? colors.success.main :
                                 table.status === TableStatus.Occupied ? colors.error.main :
                                 table.status === TableStatus.Reserved ? colors.warning.main : colors.accent.indigo,
                        }}
                      >
                        {table.tableNumber}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                          Table {table.tableNumber}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                          {table.capacity} seats
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setQrDialogTable(table)}
                        sx={{ color: '#10b981' }}
                        title="Show QR Code"
                      >
                        <QrCode sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditTable(table)}
                        sx={{ color: colors.accent.indigo }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTable(table.id)}
                        sx={{ color: colors.error.main }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip
                      label={table.status}
                      size="small"
                      clickable
                      onClick={() => handleToggleTableStatus(table.id, table.status)}
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: table.status === TableStatus.Available ? `${colors.success.main}15` :
                                   table.status === TableStatus.Occupied ? `${colors.error.main}15` :
                                   table.status === TableStatus.Reserved ? `${colors.warning.main}15` : `${colors.accent.indigo}15`,
                        color: table.status === TableStatus.Available ? colors.success.main :
                               table.status === TableStatus.Occupied ? colors.error.main :
                               table.status === TableStatus.Reserved ? colors.warning.main : colors.accent.indigo,
                      }}
                    />
                    {table.location && (
                      <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        {table.location}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {tables.length === 0 && (
                <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                    No tables found. Click "Add Table" to create one.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Inline Floor Plan Preview ─────────────────────────────────────── */}
          {(() => {
            const placedTables = tables.filter(t => t.xPosition !== undefined && t.yPosition !== undefined);
            if (placedTables.length === 0) return null;

            const FLOOR_W = 800;
            const FLOOR_H = 600;

            const statusStyle = (status: number) => {
              switch (status) {
                case TableStatus.Available:    return { bg: '#22c55e', border: '#16a34a' };
                case TableStatus.Occupied:     return { bg: '#ef4444', border: '#dc2626' };
                case TableStatus.Reserved:     return { bg: '#f59e0b', border: '#d97706' };
                case TableStatus.OutOfService: return { bg: '#6b7280', border: '#4b5563' };
                case TableStatus.Cleaning:     return { bg: '#a78bfa', border: '#7c3aed' };
                default:                       return { bg: '#94a3b8', border: '#64748b' };
              }
            };

            const shapeBorderRadius = (shape?: TableShape) => {
              if (shape === TableShape.Circle || shape === TableShape.Oval) return '50%';
              if (shape === TableShape.Booth) return '12px';
              return '6px';
            };

            const unplaced = tables.length - placedTables.length;

            return (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 3, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }} />

                {/* Section header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '9px', background: `${colors.accent.indigo}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.accent.indigo }}>
                      <DesignServices sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                        Floor Plan
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        {placedTables.length} table{placedTables.length !== 1 ? 's' : ''} placed
                        {unplaced > 0 ? ` · ${unplaced} not yet positioned` : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<DesignServices />}
                    onClick={() => setVisualDesignerDialogOpen(true)}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 600, border: `1.5px solid ${colors.accent.indigo}`, color: colors.accent.indigo, '&:hover': { background: `${colors.accent.indigo}10` } }}
                  >
                    Edit Layout
                  </Button>
                </Box>

                {/* Status legend */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Available',     color: '#22c55e', status: TableStatus.Available },
                    { label: 'Occupied',      color: '#ef4444', status: TableStatus.Occupied },
                    { label: 'Reserved',      color: '#f59e0b', status: TableStatus.Reserved },
                    { label: 'Out of Service',color: '#6b7280', status: TableStatus.OutOfService },
                    { label: 'Cleaning',      color: '#a78bfa', status: TableStatus.Cleaning },
                  ].filter(s => placedTables.some(t => t.status === s.status)).map(s => (
                    <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '3px', background: s.color }} />
                      <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted, fontWeight: 500 }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Floor plan canvas */}
                <Box sx={{ overflowX: 'auto', borderRadius: '14px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}` }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: FLOOR_W,
                      height: FLOOR_H,
                      background: isDark ? 'rgba(15,23,42,0.8)' : '#f8fafc',
                      backgroundImage: isDark
                        ? 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)'
                        : 'radial-gradient(circle, rgba(0,0,0,0.07) 1px, transparent 1px)',
                      backgroundSize: '40px 40px',
                      userSelect: 'none',
                    }}
                  >
                    {/* Subtle grid lines */}
                    {Array.from({ length: Math.floor(FLOOR_W / 100) }).map((_, i) => (
                      <Box key={`vg${i}`} sx={{ position: 'absolute', left: (i + 1) * 100, top: 0, bottom: 0, width: 1, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)' }} />
                    ))}
                    {Array.from({ length: Math.floor(FLOOR_H / 100) }).map((_, i) => (
                      <Box key={`hg${i}`} sx={{ position: 'absolute', top: (i + 1) * 100, left: 0, right: 0, height: 1, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)' }} />
                    ))}

                    {/* Tables */}
                    {placedTables.map(table => {
                      const s  = statusStyle(table.status);
                      const w  = table.width  ?? 120;
                      const h  = table.height ?? 80;
                      const br = shapeBorderRadius(table.shape);
                      return (
                        <Box
                          key={table.id}
                          sx={{
                            position: 'absolute',
                            left: table.xPosition,
                            top:  table.yPosition,
                            width: w,
                            height: h,
                            background: s.bg,
                            border: `2px solid ${s.border}`,
                            borderRadius: br,
                            transform: `rotate(${table.rotation ?? 0}deg)`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            transition: 'box-shadow 0.15s',
                            cursor: 'default',
                            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.35)', zIndex: 10 },
                          }}
                        >
                          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 13, lineHeight: 1.2 }}>
                            {table.tableNumber}
                          </Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 11, lineHeight: 1.2 }}>
                            {table.capacity} seats
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {unplaced > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                    💡 {unplaced} table{unplaced !== 1 ? 's are' : ' is'} not yet on the floor plan. Open "Edit Layout" to position {unplaced !== 1 ? 'them' : 'it'}.
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Card>
      </div>
    );
  };

  // Memoized derived list — avoids re-filtering on every unrelated state change
  const filteredMenuItems = useMemo(
    () => selectedCategory
      ? menuItems.filter(item => item.categoryId?.toLowerCase() === selectedCategory.toLowerCase())
      : menuItems,
    [menuItems, selectedCategory]
  );

  // Menu management for Admin
  const renderMenuSection = () => {
    if (activeSection !== 'menu') return null;

    const filteredItems = filteredMenuItems;

    return (
      <Box>
        <Card
          sx={{
            p: 3,
            mb: 3,
            background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`,
            borderRadius: '20px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: `${colors.accent.indigo}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.accent.indigo,
                }}
              >
                <MenuBook />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                Menu Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddBusiness />}
                onClick={() => setCreateCategoryDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  background: colors.accent.gradient,
                  boxShadow: shadows.glow.primary,
                }}
              >
                Add Category
              </Button>
              <Button
                variant="contained"
                startIcon={<AddBusiness />}
                onClick={() => setCreateItemDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  background: colors.accent.gradient,
                  boxShadow: shadows.glow.primary,
                }}
              >
                Add Item
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={() => setOcrDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  borderColor: colors.accent.indigo,
                  color: colors.accent.indigo,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: colors.accent.indigo,
                    background: `${colors.accent.indigo}12`,
                  }
                }}
              >
                Scan Menu Card (OCR)
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => setExcelDialogOpen(true)}
                sx={{
                  borderRadius: '10px',
                  borderColor: '#10b981',
                  color: '#10b981',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#10b981',
                    background: '#10b98112',
                  }
                }}
              >
                Import Excel/CSV
              </Button>
            </Box>
          </Box>

          {/* Category Filter */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              label="All Items"
              onClick={() => setSelectedCategory('')}
              sx={{
                background: !selectedCategory ? colors.accent.indigo : 'transparent',
                color: !selectedCategory ? '#fff' : isDark ? colors.dark.textPrimary : colors.light.textPrimary,
                border: `1px solid ${colors.accent.indigo}`,
                cursor: 'pointer',
              }}
            />
            {menuCategories.map((category) => (
              <Chip
                key={category.id}
                label={category.name}
                onClick={() => setSelectedCategory(category.id)}
                sx={{
                  background: selectedCategory === category.id ? colors.accent.indigo : 'transparent',
                  color: selectedCategory === category.id ? '#fff' : isDark ? colors.dark.textPrimary : colors.light.textPrimary,
                  border: `1px solid ${colors.accent.indigo}`,
                  cursor: 'pointer',
                }}
              />
            ))}
          </Box>

          {loadingMenuItems && menuItems.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: colors.accent.indigo }} />
            </Box>
          ) : (
            <Box sx={{ position: 'relative', zIndex: 5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {filteredItems.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    position: 'relative',
                    zIndex: 6,
                    p: 2,
                    borderRadius: '14px',
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    transition: 'none',
                    '&:hover': {
                      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  {item.imageUrl && (
                    <Box
                      sx={{
                        width: '100%',
                        height: 140,
                        borderRadius: '10px',
                        overflow: 'hidden',
                        mb: 1.5,
                        position: 'relative',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                      }}
                    >
                      <img
                        src={item.imageUrl.startsWith('http') ? item.imageUrl : `${IMAGE_BASE}${item.imageUrl}`}
                        alt={item.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        ₹{item.price}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.id); }}
                        sx={{ color: favorites.has(item.id) ? '#ffb300' : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)') }}
                        title="Toggle Favorite"
                      >
                        <StarRate sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenEditItem(item); }}
                        sx={{ color: colors.accent.indigo }}
                        title="Edit Item"
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setItemToDelete(item); setDeleteConfirmOpen(true); }}
                        sx={{ color: colors.error.main }}
                        title="Delete Item"
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {item.isVegetarian && (
                      <Chip label="Veg" size="small" sx={{ height: 20, fontSize: '0.65rem', background: `${colors.success.main}15`, color: colors.success.main }} />
                    )}
                    {item.isSpicy && (
                      <Chip label="Spicy" size="small" sx={{ height: 20, fontSize: '0.65rem', background: `${colors.error.main}15`, color: colors.error.main }} />
                    )}
                    {item.isAvailable ? (
                      <Chip label="Available" size="small" sx={{ height: 20, fontSize: '0.65rem', background: `${colors.success.main}15`, color: colors.success.main }} />
                    ) : (
                      <Chip label="Unavailable" size="small" sx={{ height: 20, fontSize: '0.65rem', background: `${colors.error.main}15`, color: colors.error.main }} />
                    )}
                  </Box>
                  {item.description && (
                    <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              ))}
              {filteredItems.length === 0 && (
                <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                    No menu items found. Click "Add Item" to create one.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </Box>
    );
  };

  const renderApprovalQueue = () => {
    if (activeSection !== 'approval') return null;

    const formatTime = (dateStr: string) => {
      try { return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); }
      catch { return '—'; }
    };

    return (
      <div>
        <Card sx={{ p: 3, mb: 3, background: isDark ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`, borderRadius: '32px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ThumbUpAlt sx={{ color: '#6366f1', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>Order Approval Queue</Typography>
                <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                  {pendingOrders.length} order{pendingOrders.length !== 1 ? 's' : ''} awaiting approval
                </Typography>
              </Box>
            </Box>
            <Button size="small" startIcon={<Refresh />} onClick={loadPendingOrders} disabled={loadingPendingOrders} sx={{ textTransform: 'none', borderRadius: '10px' }}>Refresh</Button>
          </Box>

          {loadingPendingOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : pendingOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ThumbUpAlt sx={{ fontSize: 48, color: isDark ? colors.dark.textMuted : colors.light.textMuted, mb: 1 }} />
              <Typography sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted, fontWeight: 600 }}>No pending orders</Typography>
              <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>All orders have been processed.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' }, gap: 2 }}>
              {pendingOrders.map((order: any) => {
                const isBusy = approvalBusy.has(order.id);
                const items: any[] = order.items || order.orderItems || [];
                const tableLabel = order.tableNumber ? `Table ${order.tableNumber}` : order.orderType === 'Takeaway' || order.orderType === 2 ? 'Takeaway' : 'Dine-In';
                return (
                  <Box key={order.id} sx={{ borderRadius: '14px', border: `1px solid ${order.source === 'swiggy' ? 'rgba(252,128,25,0.4)' : order.source === 'zomato' ? 'rgba(226,55,68,0.4)' : isDark ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.2)'}`, background: isDark ? 'rgba(30,41,59,0.8)' : 'rgba(248,250,252,0.9)', overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, background: order.source === 'swiggy' ? 'rgba(252,128,25,0.15)' : order.source === 'zomato' ? 'rgba(226,55,68,0.15)' : 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 15, color: order.source === 'swiggy' ? '#FC8019' : order.source === 'zomato' ? '#E23744' : '#6366f1' }}>{order.orderNumber || `#${order.id?.slice(-4).toUpperCase()}`}</Typography>
                        {order.source === 'swiggy' && <Chip label="🧡 Swiggy" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(252,128,25,0.15)', color: '#FC8019' }} />}
                        {order.source === 'zomato' && <Chip label="❤️ Zomato" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(226,55,68,0.15)', color: '#E23744' }} />}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={tableLabel} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
                        <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>{formatTime(order.orderDate || order.createdAt)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {items.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: isDark ? colors.dark.textMuted : colors.light.textMuted, mb: 1.5 }}>No item details</Typography>
                      ) : (
                        <Box sx={{ display: 'grid', gap: 0.5, mb: 1.5 }}>
                          {items.map((item: any, idx: number) => {
                            const name = item.menuItemName || item.name || item.menuItem?.name || `Item #${(item.menuItemId ?? '').slice(-4)}`;
                            const qty  = item.quantity ?? 1;
                            return (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, borderRadius: '8px', px: 0.75, py: 0.4, background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.06)' }}>
                                <Box sx={{ minWidth: 24, height: 22, borderRadius: '6px', background: 'rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6366f1' }}>×{qty}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, lineHeight: 1.3 }}>{name}</Typography>
                                  {(item.specialInstructions || item.notes) && (
                                    <Typography sx={{ fontSize: 10, color: '#f59e0b', fontStyle: 'italic' }}>★ {item.specialInstructions || item.notes}</Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>₹{Math.round(order.totalAmount ?? 0)}</Typography>
                          {items.length > 0 && <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>{items.length} item{items.length !== 1 ? 's' : ''}</Typography>}
                        </Box>
                        {order.notes && <Typography sx={{ fontSize: 11, color: '#f59e0b', fontStyle: 'italic' }}>Note: {order.notes}</Typography>}
                      </Box>
                      {/* Payment status row */}
                      {(() => {
                        const pay = order.payment;
                        const isPaid = pay?.isPaid;
                        const statusLabel = pay ? pay.statusName : 'Unpaid';
                        const methodLabel = pay ? pay.methodName : null;
                        const paidAmount  = pay ? pay.paidAmount : null;
                        const statusColor = isPaid ? '#10b981' : pay ? '#f59e0b' : '#94a3b8';
                        const statusBg    = isPaid ? 'rgba(16,185,129,0.12)' : pay ? 'rgba(245,158,11,0.12)' : 'rgba(148,163,184,0.12)';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{ height: 20, fontSize: 10, fontWeight: 700, background: statusBg, color: statusColor, border: `1px solid ${statusColor}33` }}
                            />
                            {methodLabel && (
                              <Chip
                                label={methodLabel}
                                size="small"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600, background: 'rgba(99,102,241,0.1)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.25)' }}
                              />
                            )}
                            {paidAmount != null && paidAmount > 0 && (
                              <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>₹{Math.round(paidAmount)}</Typography>
                            )}
                          </Box>
                        );
                      })()}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button fullWidth size="small" variant="contained" disabled={isBusy} onClick={() => approveOrder(order.id)}
                          startIcon={isBusy ? <CircularProgress size={12} color="inherit" /> : <ThumbUpAlt />}
                          sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 700, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', '&:hover': { opacity: 0.9 } }}>
                          Approve
                        </Button>
                        <Button fullWidth size="small" variant="outlined" disabled={isBusy} onClick={() => cancelOrder(order.id)}
                          startIcon={isBusy ? <CircularProgress size={12} color="inherit" /> : <CancelOutlined />}
                          sx={{ borderRadius: '8px', textTransform: 'none', fontSize: 12, fontWeight: 700, borderColor: '#ef4444', color: '#ef4444', '&:hover': { background: 'rgba(239,68,68,0.08)' } }}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>
      </div>
    );
  };

  const renderRevenueSection = () => {
    if (user?.role !== UserRole.SuperAdmin || activeSection !== 'revenue') return null;

    const totalRevenue = allRestaurants.reduce((sum, r) => sum + (restaurantRevenues[r.id]?.revenue ?? 0), 0);
    const totalOrders  = allRestaurants.reduce((sum, r) => sum + (restaurantRevenues[r.id]?.orderCount ?? 0), 0);

    return (
      <div>
        <Card sx={{ p: 3, mb: 3, background: isDark ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`, borderRadius: '32px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp sx={{ color: '#10b981', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>Restaurant Revenue</Typography>
                <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                  All-time revenue across all restaurants
                </Typography>
              </Box>
            </Box>
            <Button size="small" startIcon={<Refresh />} onClick={loadAllRestaurantsWithRevenue} disabled={loadingRevenue} sx={{ textTransform: 'none', borderRadius: '10px' }}>Refresh</Button>
          </Box>

          {/* Summary totals */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 2, mb: 3 }}>
            {[
              { label: 'Total Revenue', value: `₹${totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, color: '#10b981' },
              { label: 'Total Orders', value: totalOrders.toLocaleString(), color: '#6366f1' },
            ].map(s => (
              <Box key={s.label} sx={{ p: 2, borderRadius: '14px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</Typography>
                <Typography sx={{ fontSize: 12, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>

          {loadingRevenue ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
          ) : allRestaurants.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>No restaurants found.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {allRestaurants.map((r) => {
                const rev = restaurantRevenues[r.id];
                return (
                  <Box key={r.id} sx={{ p: 2, borderRadius: '14px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                      <Avatar sx={{ width: 40, height: 40, background: 'rgba(99,102,241,0.15)', color: '#6366f1', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                        {r.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>{r.restaurantCode || r.id?.slice(0, 8)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                      {rev?.loading ? (
                        <CircularProgress size={18} />
                      ) : (
                        <>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>
                              ₹{(rev?.revenue ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>revenue</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#6366f1' }}>{rev?.orderCount ?? 0}</Typography>
                            <Typography sx={{ fontSize: 10, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>orders</Typography>
                          </Box>
                        </>
                      )}
                      <Chip
                        label={r.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          height: 24, fontSize: '0.7rem', fontWeight: 600,
                          background: r.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: r.isActive ? '#10b981' : '#ef4444',
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>
      </div>
    );
  };

  const renderEarningsSection = () => {
    if (user?.role !== UserRole.SuperAdmin || activeSection !== 'earnings') return null;

    const totalEarnings = subscriptionPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalTransactions = subscriptionPayments.length;
    const avgEarnings = totalTransactions > 0 ? totalEarnings / totalTransactions : 0;

    return (
      <div>
        <Card sx={{ p: 3, mb: 3, background: isDark ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', border: `1px solid ${isDark ? colors.glass.border : colors.light.border}`, borderRadius: '32px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center' }}>
                <AttachMoney sx={{ color: '#6366f1', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>Earning Ledger</Typography>
                <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                  Pricing plan subscription earnings tracking
                </Typography>
              </Box>
            </Box>
            <Button size="small" startIcon={<Refresh />} onClick={loadSubscriptionPayments} disabled={loadingEarnings} sx={{ textTransform: 'none', borderRadius: '10px' }}>Refresh</Button>
          </Box>

          {/* Metrics */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
            {[
              { label: 'Total Earnings', value: `₹${totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, color: '#10b981' },
              { label: 'Total Purchases', value: totalTransactions.toString(), color: '#6366f1' },
              { label: 'Average Plan Order', value: `₹${avgEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, color: '#eab308' },
            ].map(s => (
              <Box key={s.label} sx={{ p: 2, borderRadius: '14px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</Typography>
                <Typography sx={{ fontSize: 12, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>

          {loadingEarnings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
          ) : subscriptionPayments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>No pricing plan purchases logged yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {subscriptionPayments.map((p) => {
                const pf = p.planFeatures;
                const activeFeatureLabels = pf ? [
                  pf.hasPOS && 'POS', pf.hasKDS && 'KDS', pf.hasInventory && 'Inventory',
                  pf.hasCRM && 'CRM', pf.hasOnlineOrdering && 'Online', pf.hasDelivery && 'Delivery', pf.hasReports && 'Reports',
                ].filter(Boolean) as string[] : [];

                const planExpiry = p.planExpiry ? new Date(p.planExpiry as string) : null;
                const daysLeft = planExpiry ? Math.ceil((planExpiry.getTime() - Date.now()) / 86400000) : null;

                return (
                  <Box key={p.id} sx={{ p: 2, borderRadius: '14px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                    {/* Row 1: tenant + plan name + amount + status */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: activeFeatureLabels.length > 0 ? 1 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 180 }}>
                        <Avatar sx={{ width: 36, height: 36, background: p.tenantIsActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)', color: p.tenantIsActive ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 13 }}>
                          {p.tenantName?.charAt(0).toUpperCase() || 'T'}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>{p.tenantName}</Typography>
                            <Chip
                              label={p.tenantIsActive ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: p.tenantIsActive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: p.tenantIsActive ? '#10b981' : '#ef4444' }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: 10, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                            <strong>{p.pricingPlanName}</strong> · {p.billingCycle} · via {p.paymentMethod.toUpperCase()}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {/* Expiry */}
                        {planExpiry && daysLeft !== null && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: daysLeft < 0 ? '#ef4444' : daysLeft <= 7 ? '#eab308' : '#10b981' }}>
                              {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : daysLeft === 0 ? 'Expires today' : `${daysLeft}d left`}
                            </Typography>
                            <Typography sx={{ fontSize: 9, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                              {planExpiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </Typography>
                          </Box>
                        )}

                        {/* Amount + date */}
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>₹{p.amount.toLocaleString('en-IN')}</Typography>
                          <Typography sx={{ fontSize: 10, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                            {new Date(p.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </Typography>
                        </Box>

                        {/* Transaction ID */}
                        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, fontFamily: 'monospace' }}>{p.transactionId}</Typography>
                          <Chip
                            label={p.paymentStatus}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, mt: 0.3,
                              background: p.paymentStatus === 'Completed' ? 'rgba(16,185,129,0.15)' : p.paymentStatus === 'Pending' ? 'rgba(234,179,8,0.15)' : 'rgba(239,68,68,0.15)',
                              color: p.paymentStatus === 'Completed' ? '#10b981' : p.paymentStatus === 'Pending' ? '#eab308' : '#ef4444',
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Row 2: plan features chips */}
                    {activeFeatureLabels.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pt: 1, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                        {activeFeatureLabels.map(f => (
                          <Chip key={f} label={f} size="small" icon={<CheckCircleOutline sx={{ fontSize: '10px !important' }} />}
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, background: 'rgba(99,102,241,0.1)', color: '#6366f1', '& .MuiChip-icon': { color: '#6366f1' } }} />
                        ))}
                        {pf && (
                          <>
                            <Chip label={`${pf.maxUsers} Users`} size="small" sx={{ height: 18, fontSize: '0.6rem', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', color: isDark ? colors.dark.textMuted : colors.light.textMuted }} />
                            <Chip label={`${pf.maxTables} Tables`} size="small" sx={{ height: 18, fontSize: '0.6rem', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', color: isDark ? colors.dark.textMuted : colors.light.textMuted }} />
                            <Chip label={`${pf.maxMenuItems} Menu Items`} size="small" sx={{ height: 18, fontSize: '0.6rem', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', color: isDark ? colors.dark.textMuted : colors.light.textMuted }} />
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Card>
      </div>
    );
  };

  const handleOneClickSetup = async (gatewayName: string) => {
    try {
      let payload: any = {};
      if (gatewayName === 'Razorpay') {
        payload = {
          gatewayName: 'Razorpay',
          displayName: 'Razorpay',
          isActive: true,
          isTestMode: true,
          apiKey: 'rzp_test_mockKey123',
          apiSecret: 'mockSecret123',
          supportsCard: true,
          supportsUPI: true,
          supportsWallet: true,
          supportsNetBanking: true,
        };
      } else if (gatewayName === 'PhonePe') {
        payload = {
          gatewayName: 'PhonePe',
          displayName: 'PhonePe',
          isActive: true,
          isTestMode: true,
          merchantId: 'MERCHANT_MOCK_123',
          apiSecret: 'saltKeyMockSecret123',
          apiKey: '1', // Mapped to salt index
          supportsCard: true,
          supportsUPI: true,
          supportsWallet: true,
          supportsNetBanking: true,
        };
      }

      await apiService.saveGateway('00000000-0000-0000-0000-000000000000', payload);
      setSnackbar({ open: true, message: `${gatewayName} Gateway configured with one-click setup!`, severity: 'success' });
      loadGlobalGateways();
    } catch (error: any) {
      console.error('One-click setup failed:', error);
      setSnackbar({ open: true, message: error?.response?.data?.message || 'One-click setup failed', severity: 'error' });
    }
  };

  const handleToggleGlobalGateway = async (gatewayId: string) => {
    try {
      const res = await apiService.toggleGateway(gatewayId);
      setSnackbar({ open: true, message: `Gateway status updated successfully!`, severity: 'success' });
      loadGlobalGateways();
    } catch (error) {
      console.error('Failed to toggle gateway:', error);
      setSnackbar({ open: true, message: 'Failed to toggle gateway', severity: 'error' });
    }
  };

  const handleDeleteGlobalGateway = async (gatewayId: string) => {
    if (!window.confirm('Are you sure you want to remove this gateway configuration?')) return;
    try {
      await apiService.deleteGateway(gatewayId);
      setSnackbar({ open: true, message: 'Gateway configuration deleted', severity: 'success' });
      loadGlobalGateways();
    } catch (error) {
      console.error('Failed to delete gateway:', error);
      setSnackbar({ open: true, message: 'Failed to delete gateway', severity: 'error' });
    }
  };

  const handleOpenEditGlobalGateway = (gw: any) => {
    setGlobalGatewayForm({
      gatewayName: gw.name || gw.gatewayName,
      displayName: gw.displayName || gw.name || gw.gatewayName,
      isTestMode: gw.isTestMode ?? true,
      apiKey: gw.apiKey || '',
      apiSecret: gw.apiSecret || '',
      merchantId: gw.merchantId || '',
      saltIndex: gw.name === 'PhonePe' || gw.gatewayName === 'PhonePe' ? (gw.apiKey || '1') : '1',
    });
    setGlobalGatewayDialogOpen(true);
  };

  const handleSaveGlobalGatewayManual = async () => {
    try {
      const payload: any = {
        gatewayName: globalGatewayForm.gatewayName,
        displayName: globalGatewayForm.displayName,
        isActive: true,
        isTestMode: globalGatewayForm.isTestMode,
        apiKey: globalGatewayForm.gatewayName === 'PhonePe' ? globalGatewayForm.saltIndex : globalGatewayForm.apiKey,
        apiSecret: globalGatewayForm.apiSecret || undefined,
        merchantId: globalGatewayForm.merchantId || undefined,
        supportsCard: true,
        supportsUPI: true,
        supportsWallet: true,
        supportsNetBanking: true,
      };

      await apiService.saveGateway('00000000-0000-0000-0000-000000000000', payload);
      setSnackbar({ open: true, message: `${globalGatewayForm.displayName} gateway configuration saved!`, severity: 'success' });
      setGlobalGatewayDialogOpen(false);
      loadGlobalGateways();
    } catch (error: any) {
      console.error('Failed to save gateway:', error);
      setSnackbar({ open: true, message: error?.response?.data?.message || 'Failed to save gateway details', severity: 'error' });
    }
  };

  const renderPaymentGatewaysSection = () => {
    if (user?.role !== UserRole.SuperAdmin || activeSection !== 'gateways') return null;

    return (
      <div>
        <PaymentGatewaySetup
          active={true}
          isDark={isDark}
          currentRestaurantId='00000000-0000-0000-0000-000000000000'
          onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
        />
      </div>
    );
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDark ? colors.dark.background : colors.light.background,
        display: 'flex',
        overflowX: 'hidden',
        width: '100%',
      }}
    >
      <FloatingShapes variant="dashboard" />

      {/* Sidebar */}
      {!isMobile && renderSidebar()}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{
            sx: {
              width: SIDEBAR_WIDTH,
              background: isDark ? colors.dark.background : colors.light.surface,
              borderRight: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            },
          }}
        >
          {renderSidebar()}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          ml: isMobile ? 0 : `${sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED}px`,
          transition: 'margin-left 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          position: 'relative',
          zIndex: 1,
          minWidth: 0,
          width: '100%',
        }}
      >
        {renderTopBar()}

        <Box
          sx={{
            p: { xs: 1.5, sm: 2, md: 3 },
            width: '100%',
            maxWidth: 'none',
            mx: 0,
          }}
        >


          {/* Stats - Only show on dashboard section */}
          {activeSection === 'dashboard' && renderStats()}

          {/* Quick Actions - Only show on dashboard section */}
          {activeSection === 'dashboard' && renderQuickActions()}

          {/* Sales Graph - toggled by Quick Actions button */}
          {activeSection === 'dashboard' && showSalesGraph && renderSalesGraph()}

          {/* Kitchen Display — full-bleed, no card wrapper */}
          {activeSection === 'kitchen' && (
            <Box sx={{ mx: -3, mt: -3 }}>
              <KDSPage isDark={isDark} currentRestaurantId={currentRestaurant?.id} />
            </Box>
          )}

          {/* Content grid */}
          {activeSection !== 'kitchen' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: activeSection === 'dashboard' && user?.role !== UserRole.SuperAdmin ? '1fr 1fr' : '1fr' }, gap: 3 }}>
              {activeSection === 'dashboard' && user?.role !== UserRole.SuperAdmin && renderRecentOrders()}
              {renderUsersSection()}
              {renderTenantSection()}
              {renderRevenueSection()}
              {renderEarningsSection()}
              {renderPaymentGatewaysSection()}
              {renderTablesSection()}
              {renderOrdersSection()}
              {renderMenuSection()}
              {renderApprovalQueue()}
              <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>}>
                <ReportsSection
                  active={activeSection === 'reports' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  canAdjustSales={planFeatures.CanAdjustSales || currentTenant?.canAdjustSales}
                  onNotify={handleNotify}
                />
                {activeSection === 'pricing' && <PricingPlansContent />}
                <StaffManagementSection
                  active={activeSection === 'staff' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <CouponManagementSection
                  active={activeSection === 'coupons' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <GSTSection
                  active={activeSection === 'gst' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <ComboManagementSection
                  active={activeSection === 'combos' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <ExpenseSection
                  active={activeSection === 'expenses' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <InventorySection
                  active={activeSection === 'inventory' && (user?.role === UserRole.Admin || user?.role === UserRole.Staff)}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  userRole={user?.role ? String(user.role) : ''}
                  onNotify={handleNotify}
                />
                <PaymentGatewaySetup
                  active={activeSection === 'payment' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <OrderFlowSettings
                  active={activeSection === 'order_flow' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <DeliveryIntegrationSetup
                  active={activeSection === 'delivery_api' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                  onInjectOrder={(order) => {
                    const injected = {
                      id: order.id,
                      orderNumber: order.orderNumber,
                      orderDate: order.createdAt.toISOString(),
                      createdAt: order.createdAt.toISOString(),
                      totalAmount: order.totalAmount,
                      orderType: 'Delivery',
                      tableNumber: null,
                      notes: `🛵 ${order.platform === 'swiggy' ? 'Swiggy' : 'Zomato'} · ${order.customerName} · ${order.address}${order.notes ? ' · Note: ' + order.notes : ''}`,
                      items: order.items.map((i: any, idx: number) => ({
                        id: `${order.id}-item-${idx}`,
                        menuItemName: i.name,
                        quantity: i.qty,
                        price: i.price,
                      })),
                      payment: null,
                      source: order.platform,
                    };
                    setPendingOrders((prev: any[]) => [injected, ...prev]);
                    if (activeSection !== 'approval') {
                      setSnackbar({
                        open: true,
                        message: `New ${order.platform === 'swiggy' ? 'Swiggy 🧡' : 'Zomato ❤️'} order ${order.orderNumber} awaiting approval!`,
                        severity: 'warning',
                      });
                    }
                  }}
                />
                <ChatbotSection
                  active={activeSection === 'chatbot' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <FeedbackSection
                  active={activeSection === 'feedback' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <SettingsSection
                  active={activeSection === 'settings'}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
                <TTSTemplateSection
                  active={activeSection === 'tts_templates' && user?.role === UserRole.Admin}
                  isDark={isDark}
                  currentRestaurantId={currentRestaurant?.id}
                  onNotify={handleNotify}
                />
              </Suspense>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Always-visible AI Assistant floating button ── */}
      <ChatbotFAB
        isDark={isDark}
        currentRestaurantId={currentRestaurant?.id}
        userRole={user?.role ? String(user.role) : ''}
        userName={user?.firstName || user?.username || 'there'}
      />

      {/* User menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '14px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: isDark ? shadows.glassDark : shadows.lg,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
            {user?.email}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: isDark ? colors.dark.borderLight : colors.light.borderLight }} />
        <MenuItem onClick={() => { setActiveSection('settings'); handleMenuClose(); }} sx={{ py: 1.25, fontSize: '0.875rem' }}>
          <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => { setActiveSection('settings'); handleMenuClose(); }} sx={{ py: 1.25, fontSize: '0.875rem' }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ borderColor: isDark ? colors.dark.borderLight : colors.light.borderLight }} />
        <MenuItem onClick={handleLogout} sx={{ py: 1.25, fontSize: '0.875rem', color: colors.error.main }}>
          <ListItemIcon><Logout fontSize="small" sx={{ color: colors.error.main }} /></ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Tenant menu */}
      <Menu
        anchorEl={tenantAnchorEl}
        open={Boolean(tenantAnchorEl)}
        onClose={handleTenantMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '14px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: isDark ? shadows.glassDark : shadows.lg,
            minWidth: 280,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? colors.dark.textMuted : colors.light.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Switch Tenant
          </Typography>
        </Box>
        <Divider sx={{ borderColor: isDark ? colors.dark.borderLight : colors.light.borderLight }} />
        {loadingTenants ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          </MenuItem>
        ) : tenants.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
              No tenants available
            </Typography>
          </MenuItem>
        ) : (
          tenants.map((tenant) => (
            <MenuItem
              key={tenant.id}
              onClick={() => handleSwitchTenant(tenant.id)}
              selected={currentTenant?.id === tenant.id}
              sx={{ py: 1.25 }}
            >
              <ListItemIcon>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', background: `${colors.accent.violet}20`, color: colors.accent.violet }}>
                  {tenant.name.charAt(0)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={tenant.name}
                secondary={tenant.subdomain}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              {currentTenant?.id === tenant.id && (
                <Chip label="Current" size="small" sx={{ height: 22, fontSize: '0.65rem', background: `${colors.accent.indigo}15`, color: colors.accent.indigo }} />
              )}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Restaurant menu */}
      <Menu
        anchorEl={restaurantAnchorEl}
        open={Boolean(restaurantAnchorEl)}
        onClose={handleRestaurantMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '14px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: isDark ? shadows.glassDark : shadows.lg,
            minWidth: 280,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? colors.dark.textMuted : colors.light.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Switch Restaurant
          </Typography>
        </Box>
        <Divider sx={{ borderColor: isDark ? colors.dark.borderLight : colors.light.borderLight }} />
        {loadingRestaurants ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          </MenuItem>
        ) : restaurants.length === 0 ? (
          <>
            <MenuItem disabled>
              <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                No restaurants available
              </Typography>
            </MenuItem>
            <Divider sx={{ borderColor: isDark ? colors.dark.borderLight : colors.light.borderLight }} />
            <MenuItem
              onClick={() => {
                setCreateRestaurantDialogOpen(true);
                handleRestaurantMenuClose();
              }}
              sx={{ py: 1.25, color: colors.accent.indigo }}
            >
              <ListItemIcon>
                <AddCircleOutline sx={{ color: colors.accent.indigo, fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary="Create Restaurant"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
              />
            </MenuItem>
          </>
        ) : (
          restaurants.map((restaurant) => (
            <MenuItem
              key={restaurant.id}
              onClick={() => handleSwitchRestaurant(restaurant.id)}
              selected={currentRestaurant?.id === restaurant.id}
              sx={{ py: 1.25 }}
            >
              <ListItemIcon>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', background: 'rgba(76, 175, 80, 0.2)', color: '#4caf50' }}>
                  {restaurant.name.charAt(0)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={restaurant.name}
                secondary={restaurant.address}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              {currentRestaurant?.id === restaurant.id && (
                <Chip label="Current" size="small" sx={{ height: 22, fontSize: '0.65rem', background: 'rgba(76, 175, 80, 0.15)', color: '#4caf50' }} />
              )}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Create User Dialog */}
      <Dialog
        open={createUserDialogOpen}
        onClose={() => setCreateUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonAdd sx={{ color: colors.accent.indigo }} />
          Create New Admin User
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={newUser.firstName}
                onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={newUser.lastName}
                onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
              />
            </Box>
            <TextField
              label="Username"
              fullWidth
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <TextField
              label="Password"
              type={showNewUserPassword ? 'text' : 'password'}
              fullWidth
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                      edge="end"
                    >
                      {showNewUserPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {user?.role === UserRole.SuperAdmin && (
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select
                  value={newUser.tenantId || ''}
                  label="Tenant"
                  onChange={(e) => setNewUser({ ...newUser, tenantId: e.target.value })}
                >
                  {loadingTenants ? (
                    <MenuItem disabled value="">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        Loading tenants...
                      </Box>
                    </MenuItem>
                  ) : tenants.length === 0 ? (
                    <MenuItem disabled value="">No tenants found</MenuItem>
                  ) : (
                    tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
            {/* SuperAdmin can only create Admin users; Admin can create Staff */}
            {user?.role !== UserRole.SuperAdmin && (
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.role}
                  label="Role"
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="Staff">Staff</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCreateUserDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!newUser.username || !newUser.email || !newUser.password || !newUser.firstName || (user?.role === UserRole.SuperAdmin && !newUser.tenantId)}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
                color: '#fff'
            }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editUserDialogOpen}
        onClose={() => setEditUserDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Edit sx={{ color: colors.accent.indigo }} />
          Edit Admin User
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={editUserForm.firstName}
                onChange={(e) => setEditUserForm({ ...editUserForm, firstName: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={editUserForm.lastName}
                onChange={(e) => setEditUserForm({ ...editUserForm, lastName: e.target.value })}
              />
            </Box>
            <TextField
              label="Username"
              fullWidth
              value={editUserForm.username}
              onChange={(e) => setEditUserForm({ ...editUserForm, username: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editUserForm.email}
              onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
            />
            <TextField
              label="Phone"
              fullWidth
              value={editUserForm.phone}
              onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
            />
            <TextField
              label="New Password (Leave blank to keep current)"
              type={showEditUserPassword ? 'text' : 'password'}
              fullWidth
              value={editUserForm.password}
              onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowEditUserPassword(!showEditUserPassword)}
                      edge="end"
                    >
                      {showEditUserPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {editingUser?.passwordHash && (
              <Typography variant="caption" sx={{ display: 'block', color: colors.accent.indigo, mt: -1.5, pl: 1, fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}>
                🔑 Current Password: {editingUser.passwordHash}
              </Typography>
            )}
            {user?.role === UserRole.SuperAdmin && (
              <FormControl fullWidth>
                <InputLabel>Tenant</InputLabel>
                <Select
                  value={editUserForm.tenantId || ''}
                  label="Tenant"
                  onChange={(e) => setEditUserForm({ ...editUserForm, tenantId: e.target.value })}
                >
                  {loadingTenants ? (
                    <MenuItem disabled value="">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        Loading tenants...
                      </Box>
                    </MenuItem>
                  ) : tenants.length === 0 ? (
                    <MenuItem disabled value="">No tenants found</MenuItem>
                  ) : (
                    tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditUserDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateUser}
            disabled={!editUserForm.username || !editUserForm.email || (user?.role === UserRole.SuperAdmin && !editUserForm.tenantId)}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
              color: '#fff'
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay & Activate Plan Dialog — 2-step */}
      <Dialog
        open={renewPlanDialogOpen}
        onClose={() => setRenewPlanDialogOpen(false)}
        maxWidth={payPlanStep === 1 ? 'md' : 'sm'}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
          },
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CreditCard sx={{ color: '#6366f1', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                  {payPlanStep === 1 ? 'Select Plan' : 'Payment Details'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                  {renewingTenant?.name}
                </Typography>
              </Box>
            </Box>
            {/* Step indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {[1, 2].map(s => (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    background: payPlanStep >= s ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
                    color: payPlanStep >= s ? '#fff' : (isDark ? colors.dark.textMuted : colors.light.textMuted),
                    transition: 'all 0.2s',
                  }}>{s}</Box>
                  {s < 2 && <Box sx={{ width: 20, height: 2, borderRadius: 1, background: payPlanStep > 1 ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)') }} />}
                </Box>
              ))}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>

          {/* ── STEP 1: Plan Selection ── */}
          {payPlanStep === 1 && (() => {
            const selectedPlan = pricingPlans.find(p => p.id === renewPlanForm.planId);
            return (
              <Box>
                {/* Billing cycle toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2.5, mt: 0.5 }}>
                  {['monthly', 'yearly'].map(cycle => (
                    <Button
                      key={cycle}
                      variant={renewPlanForm.billingCycle === cycle ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => {
                        const plan = pricingPlans.find(p => p.id === renewPlanForm.planId);
                        const amount = plan ? (cycle === 'yearly' && plan.priceYearly ? plan.priceYearly : plan.priceMonthly) : 0;
                        setRenewPlanForm({ ...renewPlanForm, billingCycle: cycle, amount });
                      }}
                      sx={{
                        textTransform: 'none', borderRadius: '10px', px: 2.5,
                        ...(renewPlanForm.billingCycle === cycle ? {
                          background: '#6366f1', boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                          '&:hover': { background: '#4f46e5' }
                        } : {
                          borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                          color: isDark ? colors.dark.textMuted : colors.light.textMuted,
                        })
                      }}
                    >
                      {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                      {cycle === 'yearly' && <Chip label="Save ~17%" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', background: '#10b981', color: '#fff' }} />}
                    </Button>
                  ))}
                </Box>

                {/* Plan cards grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {pricingPlans.filter(p => p.isActive).map(plan => {
                    const isSelected = renewPlanForm.planId === plan.id;
                    const price = renewPlanForm.billingCycle === 'yearly' && plan.priceYearly ? plan.priceYearly : plan.priceMonthly;
                    const features = [
                      { label: 'POS', enabled: plan.hasPOS },
                      { label: 'KDS', enabled: plan.hasKDS },
                      { label: 'Inventory', enabled: plan.hasInventory },
                      { label: 'CRM', enabled: plan.hasCRM },
                      { label: 'Online Orders', enabled: plan.hasOnlineOrdering },
                      { label: 'Delivery', enabled: plan.hasDelivery },
                      { label: 'Reports', enabled: plan.hasReports },
                    ];
                    return (
                      <Box
                        key={plan.id}
                        onClick={() => setRenewPlanForm({ ...renewPlanForm, planId: plan.id, amount: price })}
                        sx={{
                          p: 2, borderRadius: '14px', cursor: 'pointer',
                          border: `2px solid ${isSelected ? '#6366f1' : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')}`,
                          background: isSelected
                            ? (isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.06)')
                            : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
                          transition: 'all 0.2s',
                          position: 'relative',
                          '&:hover': { borderColor: '#6366f1', background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)' }
                        }}
                      >
                        {isSelected && (
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <CheckCircleOutline sx={{ color: '#6366f1', fontSize: 18 }} />
                          </Box>
                        )}
                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, mb: 0.5 }}>{plan.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.5 }}>
                          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#6366f1' }}>₹{price.toLocaleString('en-IN')}</Typography>
                          <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>/ {renewPlanForm.billingCycle === 'yearly' ? 'yr' : 'mo'}</Typography>
                        </Box>

                        {/* Feature list */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mb: 1.5 }}>
                          {features.map(f => (
                            <Box key={f.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: f.enabled ? '#10b981' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {f.enabled && <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                              </Box>
                              <Typography sx={{ fontSize: 11, color: f.enabled ? (isDark ? colors.dark.textPrimary : colors.light.textPrimary) : (isDark ? colors.dark.textMuted : colors.light.textMuted), textDecoration: f.enabled ? 'none' : 'line-through' }}>
                                {f.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Limits */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {[
                            { label: `${plan.maxUsers} Users` },
                            { label: `${plan.maxMenuItems} Items` },
                            { label: `${plan.maxTables} Tables` },
                          ].map(l => (
                            <Chip key={l.label} label={l.label} size="small" sx={{ height: 18, fontSize: '0.62rem', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', color: isDark ? colors.dark.textMuted : colors.light.textMuted }} />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Current plan indicator */}
                {renewingTenant?.planId && (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Sell sx={{ fontSize: 14, color: isDark ? colors.dark.textMuted : colors.light.textMuted }} />
                    <Typography sx={{ fontSize: 12, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                      Current plan: <strong>{pricingPlans.find(p => p.id === renewingTenant.planId)?.name || 'Unknown'}</strong>
                      {renewingTenant.planExpiry && ` · Expires ${new Date(renewingTenant.planExpiry as string).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* ── STEP 2: Payment Details ── */}
          {payPlanStep === 2 && (() => {
            const plan = pricingPlans.find(p => p.id === renewPlanForm.planId);
            const newExpiry = new Date();
            if (renewPlanForm.billingCycle === 'yearly') newExpiry.setFullYear(newExpiry.getFullYear() + 1);
            else newExpiry.setMonth(newExpiry.getMonth() + 1);

            const activeFeatures = plan ? [
              plan.hasPOS && 'POS', plan.hasKDS && 'KDS', plan.hasInventory && 'Inventory',
              plan.hasCRM && 'CRM', plan.hasOnlineOrdering && 'Online Orders', plan.hasDelivery && 'Delivery',
              plan.hasReports && 'Reports',
            ].filter(Boolean) : [];

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 0.5 }}>

                {/* Plan summary */}
                <Box sx={{ p: 2.5, borderRadius: '14px', background: isDark ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 18, color: '#6366f1' }}>{plan?.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: isDark ? colors.dark.textMuted : colors.light.textMuted, textTransform: 'capitalize' }}>{renewPlanForm.billingCycle} billing</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 26, fontWeight: 900, color: '#10b981' }}>₹{renewPlanForm.amount.toLocaleString('en-IN')}</Typography>
                      <Typography sx={{ fontSize: 10, color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        Active until {newExpiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {activeFeatures.map(f => (
                      <Chip key={f as string} label={f as string} size="small" icon={<CheckCircleOutline sx={{ fontSize: '12px !important' }} />}
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, background: 'rgba(16,185,129,0.12)', color: '#10b981', '& .MuiChip-icon': { color: '#10b981' } }} />
                    ))}
                    <Chip label={`${plan?.maxUsers} Users`} size="small" sx={{ height: 22, fontSize: '0.7rem', background: 'rgba(99,102,241,0.12)', color: '#6366f1' }} />
                    <Chip label={`${plan?.maxTables} Tables`} size="small" sx={{ height: 22, fontSize: '0.7rem', background: 'rgba(99,102,241,0.12)', color: '#6366f1' }} />
                  </Box>
                </Box>

                {/* Activation note */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: '10px', background: isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                  <ToggleOn sx={{ color: '#10b981', fontSize: 20 }} />
                  <Typography sx={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>
                    Tenant "{renewingTenant?.name}" will be activated immediately upon payment confirmation.
                  </Typography>
                </Box>

                {/* Payment method */}
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={renewPlanForm.paymentMethod}
                    label="Payment Method"
                    onChange={(e) => setRenewPlanForm({ ...renewPlanForm, paymentMethod: e.target.value })}
                    sx={{ borderRadius: '10px' }}
                  >
                    <MenuItem value="upi">UPI / QR Code</MenuItem>
                    <MenuItem value="card">Debit / Credit Card</MenuItem>
                    <MenuItem value="bank">Bank Transfer / NEFT</MenuItem>
                    <MenuItem value="cash">Cash / Manual Entry</MenuItem>
                  </Select>
                </FormControl>

                {/* Amount (editable) */}
                <TextField
                  label="Amount (₹)"
                  type="number"
                  size="small"
                  fullWidth
                  value={renewPlanForm.amount}
                  onChange={(e) => setRenewPlanForm({ ...renewPlanForm, amount: parseFloat(e.target.value) || 0 })}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />

                {/* Transaction ID */}
                <TextField
                  label="Transaction ID / Reference No."
                  size="small"
                  fullWidth
                  value={renewPlanForm.transactionId}
                  onChange={(e) => setRenewPlanForm({ ...renewPlanForm, transactionId: e.target.value })}
                  helperText="Auto-generated. Update if you have a bank reference."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </Box>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setRenewPlanDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          {payPlanStep === 1 ? (
            <Button
              variant="contained"
              onClick={() => setPayPlanStep(2)}
              disabled={!renewPlanForm.planId}
              sx={{ borderRadius: '10px', background: '#6366f1', '&:hover': { background: '#4f46e5' }, px: 3 }}
            >
              Next: Payment Details →
            </Button>
          ) : (
            <>
              <Button onClick={() => setPayPlanStep(1)} sx={{ borderRadius: '10px' }}>
                ← Back
              </Button>
              <Button
                variant="contained"
                onClick={handleRenewPlanSubmit}
                disabled={!renewPlanForm.planId || renewPlanForm.amount <= 0}
                startIcon={<CreditCard />}
                sx={{
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                  color: '#fff', px: 3,
                  '&:hover': { background: '#059669' }
                }}
              >
                Confirm Payment & Activate
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Restaurant Dialog */}
      <Dialog
        open={createRestaurantDialogOpen}
        onClose={() => setCreateRestaurantDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AddBusiness sx={{ color: colors.accent.indigo }} />
          Create New Restaurant with Admin
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.accent.indigo }}>
              Restaurant Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Restaurant Name"
                fullWidth
                value={newRestaurant.name}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
              />
              <TextField
                label="Restaurant Code"
                fullWidth
                value={newRestaurant.restaurantCode}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, restaurantCode: e.target.value })}
                placeholder="e.g., REST001"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Domain"
                fullWidth
                value={newRestaurant.domain}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, domain: e.target.value })}
                placeholder="e.g., myrestaurant.com"
              />
              <FormControl fullWidth>
                <InputLabel>Pricing Plan</InputLabel>
                <Select
                  value={newRestaurant.planId}
                  label="Pricing Plan"
                  onChange={(e) => {
                    const planId = e.target.value;
                    const selectedPlan = pricingPlans.find(p => p.id === planId);
                    // Auto-calculate expiry from trialDays
                    let autoExpiry = '';
                    if (selectedPlan && selectedPlan.trialDays > 0) {
                      const d = new Date();
                      d.setDate(d.getDate() + selectedPlan.trialDays);
                      autoExpiry = d.toISOString().split('T')[0];
                    }
                    setNewRestaurant({ ...newRestaurant, planId, planExpiry: autoExpiry });
                  }}
                >
                  {loadingPlans ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : pricingPlans.length > 0 ? (
                    pricingPlans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name} — ₹{plan.priceMonthly}/mo · {plan.trialDays}d trial
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No plans available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
            {/* Auto-calculated expiry preview */}
            {newRestaurant.planId && newRestaurant.planExpiry && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1 }}>
                <CalendarToday sx={{ fontSize: 14, color: colors.success.main }} />
                <Typography variant="caption" sx={{ color: colors.success.main, fontWeight: 600 }}>
                  Trial expires on: {new Date(newRestaurant.planExpiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {' '}({pricingPlans.find(p => p.id === newRestaurant.planId)?.trialDays} days trial)
                </Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 1 }} />
            
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.accent.violet }}>
              Admin User Details
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Admin First Name"
                fullWidth
                value={newRestaurant.adminFirstName}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, adminFirstName: e.target.value })}
              />
              <TextField
                label="Admin Last Name"
                fullWidth
                value={newRestaurant.adminLastName}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, adminLastName: e.target.value })}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Admin Username"
                fullWidth
                value={newRestaurant.adminUsername}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, adminUsername: e.target.value })}
              />
              <TextField
                label="Admin Email"
                type="email"
                fullWidth
                value={newRestaurant.adminEmail}
                onChange={(e) => setNewRestaurant({ ...newRestaurant, adminEmail: e.target.value })}
              />
            </Box>
            <TextField
              label="Admin Password"
              type="password"
              fullWidth
              value={newRestaurant.adminPassword}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, adminPassword: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCreateRestaurantDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRestaurantWithAdmin}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
            }}
          >
            Create Restaurant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editTenantDialogOpen} onClose={handleCloseEditTenant} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Edit sx={{ color: colors.accent.indigo }} />
          Edit Tenant
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Tenant Name"
              fullWidth
              value={editingTenant?.name || ''}
              onChange={(e) => setEditingTenant({ ...editingTenant!, name: e.target.value })}
            />
            <TextField
              label="Subdomain"
              fullWidth
              value={editingTenant?.subdomain || ''}
              onChange={(e) => setEditingTenant({ ...editingTenant!, subdomain: e.target.value })}
            />
            <TextField
              label="Restaurant Code"
              fullWidth
              value={editingTenant?.restaurantCode || ''}
              onChange={(e) => setEditingTenant({ ...editingTenant!, restaurantCode: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editingTenant?.isActive ? 'Active' : 'Inactive'}
                label="Status"
                onChange={(e) => setEditingTenant({ ...editingTenant!, isActive: e.target.value === 'Active' })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subscription Plan</InputLabel>
              <Select
                value={editingTenant?.planId || ''}
                label="Subscription Plan"
                onChange={(e) => {
                  const planId = e.target.value || undefined;
                  const selectedPlan = pricingPlans.find(p => p.id === planId);
                  // Auto-compute expiry from trialDays (from today)
                  let autoExpiry: string | undefined;
                  if (selectedPlan && selectedPlan.trialDays > 0) {
                    const d = new Date();
                    d.setDate(d.getDate() + selectedPlan.trialDays);
                    autoExpiry = d.toISOString().split('T')[0];
                  }
                  setEditingTenant({ ...editingTenant!, planId, planExpiry: autoExpiry });
                }}
              >
                <MenuItem value=""><em>No Plan</em></MenuItem>
                {pricingPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name} — ₹{plan.priceMonthly}/mo · {plan.trialDays}d trial
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Subscription Expiry Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={
                editingTenant?.planExpiry
                  ? new Date(editingTenant.planExpiry as string).toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) => setEditingTenant({ ...editingTenant!, planExpiry: e.target.value || undefined })}
              helperText="Auto-calculated from trial days — you can override manually"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseEditTenant} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateTenant}
            disabled={!editingTenant?.name || !editingTenant?.subdomain}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
            }}
          >
            Update Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!qrDialogTable} onClose={() => setQrDialogTable(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCode sx={{ color: '#10b981' }} />
            Table {qrDialogTable?.tableNumber} — QR Code
          </Box>
          <IconButton size="small" onClick={() => setQrDialogTable(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {qrDialogTable && (() => {
            const qrUrl = `${window.location.origin}/order/${qrDialogTable.id}`;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
                <Box ref={qrRef} sx={{ p: 2.5, background: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', display: 'inline-flex' }}>
                  <QRCodeSVG value={qrUrl} size={220} level="H" includeMargin={false} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary }}>
                    {currentRestaurant?.name ?? 'Restaurant'} · Table {qrDialogTable.tableNumber}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                    Capacity: {qrDialogTable.capacity} seats
                  </Typography>
                </Box>
                <Box sx={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: '10px', px: 2, py: 1, width: '100%' }}>
                  <Typography sx={{ fontSize: 11, color: isDark ? colors.dark.textMuted : colors.light.textMuted, wordBreak: 'break-all', textAlign: 'center' }}>{qrUrl}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<QrCode />}
                    onClick={() => {
                      navigator.clipboard.writeText(qrUrl);
                    }}
                    sx={{ borderRadius: '10px', textTransform: 'none', borderColor: '#10b981', color: '#10b981' }}
                  >
                    Copy Link
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      const printWin = window.open('', '_blank');
                      if (!printWin) return;
                      const svgEl = qrRef.current?.querySelector('svg')?.outerHTML ?? '';
                      printWin.document.write(`
                        <html><head><title>QR Code - Table ${qrDialogTable.tableNumber}</title>
                        <style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;background:#fff;}
                        .label{margin-top:16px;font-size:18px;font-weight:700;color:#111;} .sub{color:#666;font-size:13px;margin-top:4px;}</style></head>
                        <body>${svgEl}<div class="label">Table ${qrDialogTable.tableNumber}</div><div class="sub">Scan to order</div></body></html>
                      `);
                      printWin.document.close();
                      printWin.focus();
                      setTimeout(() => printWin.print(), 400);
                    }}
                    sx={{ borderRadius: '10px', textTransform: 'none', background: '#10b981', '&:hover': { background: '#059669' } }}
                  >
                    Print QR
                  </Button>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Create Table Dialog */}
      <Dialog open={createTableDialogOpen} onClose={() => setCreateTableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: `${colors.accent.indigo}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.accent.indigo }}>
            <TableRestaurant />
          </Box>
          Add New Table
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Table Number"
              fullWidth
              value={newTable.tableNumber}
              onChange={(e) => setNewTable({ ...newTable, tableNumber: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Table Name (Optional)"
              fullWidth
              value={newTable.tableName}
              onChange={(e) => setNewTable({ ...newTable, tableName: e.target.value })}
              placeholder="e.g., VIP Table, Window Table"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Capacity"
              type="number"
              fullWidth
              value={newTable.capacity}
              onChange={(e) => setNewTable({ ...newTable, capacity: parseInt(e.target.value) || 4 })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Location"
              fullWidth
              value={newTable.location}
              onChange={(e) => setNewTable({ ...newTable, location: e.target.value })}
              placeholder="e.g., Main Hall, Outdoor"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Table Shape</InputLabel>
              <Select
                value={newTable.shape}
                label="Table Shape"
                onChange={(e) => setNewTable({ ...newTable, shape: e.target.value as TableShape })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              >
                <MenuItem value={TableShape.Rectangle}>Rectangle</MenuItem>
                <MenuItem value={TableShape.Square}>Square</MenuItem>
                <MenuItem value={TableShape.Circle}>Circle</MenuItem>
                <MenuItem value={TableShape.Oval}>Oval</MenuItem>
                <MenuItem value={TableShape.Booth}>Booth</MenuItem>
                <MenuItem value={TableShape.CinemaHall}>Cinema Hall</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Width"
                type="number"
                value={newTable.width}
                onChange={(e) => setNewTable({ ...newTable, width: parseInt(e.target.value) || 100 })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                label="Height"
                type="number"
                value={newTable.height}
                onChange={(e) => setNewTable({ ...newTable, height: parseInt(e.target.value) || 100 })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="X Position"
                type="number"
                value={newTable.xPosition}
                onChange={(e) => setNewTable({ ...newTable, xPosition: parseInt(e.target.value) || 0 })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                label="Y Position"
                type="number"
                value={newTable.yPosition}
                onChange={(e) => setNewTable({ ...newTable, yPosition: parseInt(e.target.value) || 0 })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Box>

            <TextField
              label="Rotation (degrees)"
              type="number"
              fullWidth
              value={newTable.rotation}
              onChange={(e) => setNewTable({ ...newTable, rotation: parseInt(e.target.value) || 0 })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={newTable.isActive}
                  onChange={(e) => setNewTable({ ...newTable, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCreateTableDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateTable}
            variant="contained"
            disabled={!newTable.tableNumber}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
              '&:hover': { boxShadow: shadows.glow.primaryLg },
            }}
          >
            Create Table
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Table Dialog */}
      <Dialog open={editTableDialogOpen} onClose={handleCloseEditTable} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: `${colors.accent.indigo}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.accent.indigo }}>
            <Edit />
          </Box>
          Edit Table
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Table Number"
              fullWidth
              value={editingTable?.tableNumber || ''}
              onChange={(e) => setEditingTable(editingTable ? { ...editingTable, tableNumber: e.target.value } : null)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Capacity"
              type="number"
              fullWidth
              value={editingTable?.capacity || 4}
              onChange={(e) => setEditingTable(editingTable ? { ...editingTable, capacity: parseInt(e.target.value) || 4 } : null)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Location"
              fullWidth
              value={editingTable?.location || ''}
              onChange={(e) => setEditingTable(editingTable ? { ...editingTable, location: e.target.value } : null)}
              placeholder="e.g., Main Hall, Outdoor"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingTable?.isActive || false}
                  onChange={(e) => setEditingTable(editingTable ? { ...editingTable, isActive: e.target.checked } : null)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseEditTable} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateTable}
            variant="contained"
            disabled={!editingTable?.tableNumber}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
              '&:hover': { boxShadow: shadows.glow.primaryLg },
            }}
          >
            Update Table
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visual Table Designer Dialog */}
      <Dialog open={visualDesignerDialogOpen} onClose={() => setVisualDesignerDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: `${colors.accent.indigo}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.accent.indigo }}>
            <DesignServices />
          </Box>
          Visual Table Designer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TableDesigner
              initialTables={tables.map(table => ({
                id: table.id,
                tableNumber: table.tableNumber,
                capacity: table.capacity,
                shape: table.shape || TableShape.Rectangle,
                x: table.xPosition || 100,
                y: table.yPosition || 100,
                width: table.width || 100,
                height: table.height || 100,
                rotation: table.rotation || 0,
                color: '#3f51b5'
              }))}
              onSave={(updatedTables) => {
                const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                // Only send tables that exist in the DB (valid UUID ids); designer-only temp ids are skipped
                const layoutUpdates = updatedTables
                  .filter(t => uuidPattern.test(t.id))
                  .map(t => ({
                    tableId: t.id,
                    shape: t.shape,
                    xPosition: Math.round(t.x),
                    yPosition: Math.round(t.y),
                    width: Math.round(t.width),
                    height: Math.round(t.height),
                    rotation: Math.round(t.rotation)
                  }));

                if (layoutUpdates.length === 0) {
                  setSnackbar({ open: true, message: 'No saved tables to update. Add tables via "Add Table" first, then position them here.', severity: 'warning' });
                  return;
                }

                // Call API to update table layouts
                apiService.updateTableLayout(layoutUpdates)
                  .then(() => {
                    setSnackbar({
                      open: true,
                      message: 'Table layouts saved successfully!',
                      severity: 'success'
                    });
                    setVisualDesignerDialogOpen(false);
                    loadTables(); // Refresh tables
                  })
                  .catch((error: any) => {
                    setSnackbar({
                      open: true,
                      message: `Failed to save table layouts: ${error.message}`,
                      severity: 'error'
                    });
                  });
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setVisualDesignerDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // The save functionality is handled inside TableDesigner component
              const saveButton = document.querySelector('[data-save-button]');
              if (saveButton) {
                (saveButton as HTMLButtonElement).click();
              }
            }}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
              '&:hover': { boxShadow: shadows.glow.primaryLg },
            }}
          >
            Save Layout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={createCategoryDialogOpen} onClose={() => setCreateCategoryDialogOpen(false)} maxWidth="sm" fullWidth transitionDuration={0}>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: `${colors.accent.indigo}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.accent.indigo }}>
            <Category />
          </Box>
          Add New Category
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Category Name"
              fullWidth
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Display Order"
              type="number"
              fullWidth
              value={newCategory.displayOrder}
              onChange={(e) => setNewCategory({ ...newCategory, displayOrder: parseInt(e.target.value) || 0 })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newCategory.isActive}
                  onChange={(e) => setNewCategory({ ...newCategory, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCreateCategoryDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            disabled={!newCategory.name}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
              '&:hover': { boxShadow: shadows.glow.primaryLg },
            }}
          >
            Create Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Menu Item Dialog */}
      <Dialog open={createItemDialogOpen} onClose={() => setCreateItemDialogOpen(false)} maxWidth="sm" fullWidth transitionDuration={0}>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: `${colors.accent.indigo}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.accent.indigo }}>
            <Restaurant />
          </Box>
          Add New Menu Item
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newItem.categoryId}
                onChange={(e) => setNewItem({ ...newItem, categoryId: e.target.value })}
                label="Category"
                sx={{ borderRadius: '10px' }}
              >
                {menuCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Item Name"
              fullWidth
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Shortcut Name (optional)"
              fullWidth
              value={newItem.shortcutName}
              onChange={(e) => setNewItem({ ...newItem, shortcutName: e.target.value })}
              helperText="Short name shown on POS buttons (e.g. 'Veg Biryani' → 'V.Bir')"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              label="Price (₹)"
              type="number"
              fullWidth
              value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            {renderSizesEditor(newItem.sizes, (sizes) => setNewItem({ ...newItem, sizes }))}
            <TextField
              label="Preparation Time (minutes)"
              type="number"
              fullWidth
              value={newItem.preparationTime}
              onChange={(e) => setNewItem({ ...newItem, preparationTime: parseInt(e.target.value) || 15 })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="outlined" component="label" sx={{ borderRadius: '10px' }}>
                Upload Image
                <input type="file" hidden accept="image/*" onChange={(e) => {
                  if (e.target.files && e.target.files[0]) setNewItem({ ...newItem, imageFile: e.target.files[0] });
                }} />
              </Button>
              {newItem.imageFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img
                    src={URL.createObjectURL(newItem.imageFile)}
                    alt="Preview"
                    style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{newItem.imageFile.name}</Typography>
                </Box>
              )}
            </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newItem.isAvailable}
                        onChange={(e) => setNewItem({ ...newItem, isAvailable: e.target.checked })}
                        color="primary"
                      />
                    }
                    label="Available"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newItem.isVegetarian}
                        onChange={(e) => setNewItem({ ...newItem, isVegetarian: e.target.checked })}
                        color="success"
                      />
                    }
                    label="Vegetarian"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newItem.isSpicy}
                        onChange={(e) => setNewItem({ ...newItem, isSpicy: e.target.checked })}
                        color="error"
                      />
                    }
                    label="Spicy"
                  />
                </Box>
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newItem.isHappyHourEnabled || false}
                      onChange={(e) => setNewItem({ ...newItem, isHappyHourEnabled: e.target.checked })}
                      color="secondary"
                    />
                  }
                  label={<Typography sx={{ fontWeight: 600 }}>Enable Happy Hour Pricing</Typography>}
                />

                {newItem.isHappyHourEnabled && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: `1px solid ${colors.accent.indigo}20`, borderRadius: '12px', background: `${colors.accent.indigo}05` }}>
                    <TextField
                      label="Happy Hour Price (₹)"
                      type="number"
                      fullWidth
                      size="small"
                      value={newItem.happyHourPrice || ''}
                      onChange={(e) => setNewItem({ ...newItem, happyHourPrice: parseFloat(e.target.value) || 0 })}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="Start Time"
                        type="time"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={newItem.happyHourStart || '12:00'}
                        onChange={(e) => setNewItem({ ...newItem, happyHourStart: e.target.value })}
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={newItem.happyHourEnd || '15:00'}
                        onChange={(e) => setNewItem({ ...newItem, happyHourEnd: e.target.value })}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Active Days of Week</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                        const daysArr = newItem.happyHourDays ? newItem.happyHourDays.split(',') : [];
                        const active = daysArr.includes(day);
                        return (
                          <Chip
                            key={day}
                            label={day.substring(0, 3)}
                            size="small"
                            clickable
                            color={active ? "secondary" : "default"}
                            variant={active ? "filled" : "outlined"}
                            onClick={() => {
                              let nextDays;
                              if (active) {
                                nextDays = daysArr.filter(d => d !== day);
                              } else {
                                nextDays = [...daysArr, day];
                              }
                              setNewItem({ ...newItem, happyHourDays: nextDays.join(',') });
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCreateItemDialogOpen(false)} sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateMenuItem}
            variant="contained"
            disabled={!newItem.name || !newItem.categoryId}
            sx={{
              borderRadius: '10px',
              background: colors.accent.gradient,
              boxShadow: shadows.glow.primary,
              '&:hover': { boxShadow: shadows.glow.primaryLg },
            }}
          >
            Create Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scan Menu Card (OCR) Dialog */}
      <Dialog
        open={ocrDialogOpen}
        onClose={() => {
          if (!ocrScanning) {
            setOcrDialogOpen(false);
            setOcrFile(null);
            setOcrImagePreview(null);
            setOcrResults([]);
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.8, pb: 1, fontFamily: 'Space Grotesk' }}>
          <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: `linear-gradient(135deg, ${colors.accent.indigo} 0%, ${colors.accent.violet} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' }}>
            <AutoAwesome sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Import Menu via OCR</Typography>
            <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>Upload a menu photo to automatically extract categories, items & prices</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ borderTop: `1px solid ${isDark ? colors.dark.border : colors.light.border}`, borderBottom: `1px solid ${isDark ? colors.dark.border : colors.light.border}`, p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Language Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderRadius: '12px', background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}` }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Scan Language</Typography>
                <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>Select the language of your printed menu card</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={ocrLanguage === 'en' ? 'contained' : 'outlined'}
                  onClick={() => setOcrLanguage('en')}
                  sx={{ 
                    borderRadius: '8px', 
                    textTransform: 'none', 
                    fontWeight: 600,
                    background: ocrLanguage === 'en' ? colors.accent.gradient : 'transparent',
                    color: ocrLanguage === 'en' ? '#fff' : (isDark ? '#fff' : '#000'),
                    borderColor: ocrLanguage === 'en' ? 'transparent' : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)')
                  }}
                >
                  English
                </Button>
                <Button
                  size="small"
                  variant={ocrLanguage === 'hi' ? 'contained' : 'outlined'}
                  onClick={() => setOcrLanguage('hi')}
                  sx={{ 
                    borderRadius: '8px', 
                    textTransform: 'none', 
                    fontWeight: 600,
                    background: ocrLanguage === 'hi' ? colors.accent.gradient : 'transparent',
                    color: ocrLanguage === 'hi' ? '#fff' : (isDark ? '#fff' : '#000'),
                    borderColor: ocrLanguage === 'hi' ? 'transparent' : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)')
                  }}
                >
                  हिन्दी (Hindi)
                </Button>
              </Box>
            </Box>

            {/* Step 1: Upload or Scanning state */}
            {!ocrFile ? (
              <Box
                component="label"
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  minHeight: 220, borderRadius: '20px', cursor: 'pointer',
                  border: `2px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(99,102,241,0.3)'}`,
                  background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(99,102,241,0.02)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: colors.accent.indigo,
                    background: isDark ? 'rgba(99,102,241,0.04)' : 'rgba(99,102,241,0.04)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <input
                  type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e: any) => {
                    const file = e.target.files?.[0];
                    if (file) handleOcrFileChange(file);
                  }}
                />
                <CloudUpload sx={{ fontSize: 52, color: colors.accent.indigo, mb: 1.8, opacity: 0.8 }} />
                <Typography sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, mb: 0.5 }}>
                  Drop your menu card photo here, or browse
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                  Supports PNG, JPG, JPEG up to 10MB
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
                {/* Left side: Image preview + scanning animation */}
                <Box sx={{
                  flex: 1, minWidth: { md: 280 }, maxWidth: { md: 340 }, borderRadius: '16px',
                  position: 'relative', overflow: 'hidden', minHeight: 280,
                  background: 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${isDark ? colors.dark.border : colors.light.border}`
                }}>
                  {ocrImagePreview && (
                    <img src={ocrImagePreview} alt="Menu preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  
                  {/* Radar sweep animation */}
                  {ocrScanning && (
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(99,102,241,0.12)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      backdropFilter: 'blur(1px)'
                    }}>
                      {/* Radar sweep line */}
                      <Box sx={{
                        position: 'absolute', left: 0, right: 0, height: 3,
                        background: 'linear-gradient(90deg, transparent, #6366f1, #c084fc, #6366f1, transparent)',
                        boxShadow: '0 0 20px #6366f1, 0 0 40px #c084fc',
                        animation: 'radar-sweep 2s ease-in-out infinite'
                      }} />
                      
                      <CircularProgress size={44} sx={{ color: colors.accent.indigo, mb: 1.5 }} />
                      <Typography sx={{ color: '#fff', fontWeight: 800, textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                        AI Scanning Menu Card...
                      </Typography>
                      <Typography sx={{ color: '#fff', fontSize: 12, mt: 0.5, mb: 1, opacity: 0.95, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                        {ocrStage}
                      </Typography>
                      {/* Determinate progress bar */}
                      <Box sx={{ width: '78%', maxWidth: 320 }}>
                        <LinearProgress
                          variant="determinate"
                          value={ocrProgress}
                          sx={{
                            height: 8, borderRadius: 5,
                            backgroundColor: 'rgba(255,255,255,0.25)',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              background: 'linear-gradient(90deg, #6366f1, #c084fc)',
                            },
                          }}
                        />
                        <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700, textAlign: 'center', mt: 0.5, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                          {Math.round(ocrProgress)}%
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Right side: Extracted menu items table */}
                <Box sx={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleOutline sx={{ color: '#10b981', fontSize: 18 }} />
                      Extracted Items ({ocrResults.length})
                    </Typography>
                    {ocrResults.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant={ocrViewMode === 'preview' ? 'contained' : 'outlined'}
                          onClick={() => setOcrViewMode('preview')}
                          sx={{
                            borderRadius: '8px', textTransform: 'none', fontWeight: 600, py: 0.2, px: 1.2, fontSize: '0.72rem',
                            background: ocrViewMode === 'preview' ? colors.accent.gradient : 'transparent',
                            color: ocrViewMode === 'preview' ? '#fff' : (isDark ? '#fff' : '#000'),
                            borderColor: ocrViewMode === 'preview' ? 'transparent' : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'),
                          }}
                        >
                          Menu Preview
                        </Button>
                        <Button
                          size="small"
                          variant={ocrViewMode === 'edit' ? 'contained' : 'outlined'}
                          onClick={() => setOcrViewMode('edit')}
                          sx={{
                            borderRadius: '8px', textTransform: 'none', fontWeight: 600, py: 0.2, px: 1.2, fontSize: '0.72rem',
                            background: ocrViewMode === 'edit' ? colors.accent.gradient : 'transparent',
                            color: ocrViewMode === 'edit' ? '#fff' : (isDark ? '#fff' : '#000'),
                            borderColor: ocrViewMode === 'edit' ? 'transparent' : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'),
                          }}
                        >
                          Edit List
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{
                    maxHeight: 340, overflowY: 'auto', borderRadius: '12px',
                    border: `1px solid ${isDark ? colors.dark.border : colors.light.border}`,
                    background: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.4)',
                  }}>
                    {ocrResults.length === 0 ? (
                      ocrScanning ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.2, px: 3 }}>
                          <CircularProgress size={24} sx={{ color: colors.accent.indigo }} />
                          <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                            {ocrStage || 'Processing image text…'}
                          </Typography>
                          <Box sx={{ width: '90%', mt: 0.5 }}>
                            <LinearProgress variant="determinate" value={ocrProgress} sx={{ height: 6, borderRadius: 4 }} />
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1, px: 3, textAlign: 'center' }}>
                          <ImageSearch sx={{ fontSize: 40, color: isDark ? colors.dark.textMuted : colors.light.textMuted, opacity: 0.5 }} />
                          <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                            No items detected yet. Upload a clear, well-lit photo of your menu card to extract items.
                          </Typography>
                        </Box>
                      )
                    ) : ocrViewMode === 'preview' ? (
                      (() => {
                        // Group items by category, preserving scan (visual) order.
                        const groups: { category: string; items: typeof ocrResults }[] = [];
                        for (const it of ocrResults) {
                          const cat = it.category || 'Uncategorized';
                          const g = groups.find(x => x.category === cat);
                          if (g) g.items.push(it); else groups.push({ category: cat, items: [it] });
                        }
                        const ink = isDark ? '#f0e6d2' : '#433422';
                        const inkMuted = isDark ? 'rgba(240,230,210,0.45)' : 'rgba(67,52,34,0.45)';
                        const accent = isDark ? '#e8b15c' : '#9c6b1f';
                        return (
                          <Box sx={{
                            p: { xs: 2, md: 2.5 }, minHeight: '100%',
                            fontFamily: "'Georgia','Times New Roman',serif",
                            background: isDark
                              ? 'linear-gradient(180deg, #221d16 0%, #1b1712 100%)'
                              : 'linear-gradient(180deg, #fffdf6 0%, #fbf3e2 100%)',
                          }}>
                            {/* Menu card header */}
                            <Typography sx={{ fontFamily: 'inherit', textAlign: 'center', fontWeight: 700, fontSize: '1.15rem', letterSpacing: 3, textTransform: 'uppercase', color: ink }}>
                              {currentRestaurant?.name || 'Menu'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, mt: 0.4, mb: 2 }}>
                              <Box sx={{ height: '1px', width: 56, background: inkMuted }} />
                              <Typography sx={{ color: accent, fontSize: '0.7rem', lineHeight: 1 }}>✦</Typography>
                              <Box sx={{ height: '1px', width: 56, background: inkMuted }} />
                            </Box>

                            {/* Categories laid out in menu-card columns */}
                            <Box sx={{ columnCount: { xs: 1, sm: 2 }, columnGap: 4 }}>
                              {groups.map(group => (
                                <Box key={group.category} sx={{ breakInside: 'avoid', mb: 2.2 }}>
                                  <Typography sx={{
                                    fontFamily: 'inherit', fontWeight: 700, fontSize: '0.82rem',
                                    letterSpacing: 1.8, textTransform: 'uppercase', color: accent,
                                    borderBottom: `1px solid ${inkMuted}`, pb: 0.4, mb: 0.8,
                                  }}>
                                    {group.category}
                                  </Typography>
                                  {group.items.map(item => (
                                    <Box
                                      key={item.id}
                                      title={item.selected ? 'Click to exclude from import' : 'Click to include in import'}
                                      onClick={() => setOcrResults(prev => prev.map(r => r.id === item.id ? { ...r, selected: !r.selected } : r))}
                                      sx={{
                                        display: 'flex', alignItems: 'baseline', gap: 0.8, py: 0.4, px: 0.5,
                                        borderRadius: '6px', cursor: 'pointer',
                                        opacity: item.selected ? 1 : 0.35,
                                        textDecoration: item.selected ? 'none' : 'line-through',
                                        '&:hover': { background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(156,107,31,0.07)' },
                                      }}
                                    >
                                      {/* Veg / non-veg mark */}
                                      <Box sx={{ width: 11, height: 11, flexShrink: 0, alignSelf: 'center', border: `1.5px solid ${item.isVegetarian ? '#2e7d32' : '#c62828'}`, borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box sx={{ width: 5, height: 5, borderRadius: item.isVegetarian ? '50%' : 0, background: item.isVegetarian ? '#2e7d32' : '#c62828' }} />
                                      </Box>
                                      <Typography sx={{ fontFamily: 'inherit', fontSize: '0.84rem', color: ink, lineHeight: 1.35 }}>
                                        {item.name}{item.isSpicy ? ' 🌶️' : ''}{item.sizeName ? ` (${item.sizeName})` : ''}
                                      </Typography>
                                      {item.tag && (
                                        <Chip label={item.tag} size="small" sx={{ height: 15, fontSize: '0.55rem', fontWeight: 700, background: '#7c3aed', color: '#fff', flexShrink: 0, alignSelf: 'center', '& .MuiChip-label': { px: 0.7 } }} />
                                      )}
                                      {/* Dotted leader */}
                                      <Box sx={{ flex: 1, borderBottom: `2px dotted ${inkMuted}`, transform: 'translateY(-3px)', mx: 0.4, minWidth: 14 }} />
                                      {item.price > 0 ? (
                                        <Typography sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: '0.84rem', color: ink, flexShrink: 0 }}>
                                          ₹{item.price}
                                        </Typography>
                                      ) : (
                                        <Chip
                                          label="Add price"
                                          size="small"
                                          onClick={(e: any) => { e.stopPropagation(); setOcrViewMode('edit'); }}
                                          sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700, background: '#f59e0b', color: '#fff', flexShrink: 0, alignSelf: 'center', cursor: 'pointer', '& .MuiChip-label': { px: 0.7 } }}
                                        />
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              ))}
                            </Box>

                            <Typography sx={{ display: 'block', textAlign: 'center', mt: 1, fontSize: '0.62rem', color: inkMuted, fontFamily: 'inherit', fontStyle: 'italic' }}>
                              Click an item to include / exclude it • amber “Add price” items need a price before import
                            </Typography>
                          </Box>
                        );
                      })()
                    ) : (
                      (() => {
                        const allSelected = ocrResults.length > 0 && ocrResults.every(i => i.selected);
                        const someSelected = ocrResults.some(i => i.selected);
                        const headSx = {
                          fontWeight: 700, fontSize: '0.66rem', textTransform: 'uppercase' as const, letterSpacing: 0.6,
                          color: isDark ? colors.dark.textMuted : colors.light.textMuted,
                          background: isDark ? colors.dark.surface : colors.light.surface,
                          borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                          py: 0.7, px: 1, whiteSpace: 'nowrap' as const,
                        };
                        // Inline cell editor: invisible underline until hover/focus.
                        const cellInputSx = {
                          '& .MuiInput-root': { fontSize: '0.82rem' },
                          '& .MuiInput-root:before': { borderBottomColor: 'transparent' },
                          '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                            borderBottomColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                          },
                        };
                        return (
                          <MuiTable size="small" stickyHeader sx={{
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                              py: 0.3, px: 1,
                            },
                          }}>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" sx={headSx}>
                                  <Checkbox
                                    size="small" sx={{ p: 0.4 }}
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    onChange={(e: any) => setOcrResults(prev => prev.map(r => ({ ...r, selected: e.target.checked })))}
                                  />
                                </TableCell>
                                <TableCell sx={{ ...headSx, width: 36, px: 0.5 }}>#</TableCell>
                                <TableCell sx={headSx}>Item Name</TableCell>
                                <TableCell sx={{ ...headSx, width: 140 }}>Category</TableCell>
                                <TableCell sx={{ ...headSx, width: 80 }} align="right">Price (₹)</TableCell>
                                <TableCell sx={{ ...headSx, width: 44 }} align="center">Veg</TableCell>
                                <TableCell sx={{ ...headSx, width: 112 }}>Tag</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {ocrResults.map((item, index) => (
                                <TableRow
                                  key={item.id}
                                  hover
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData('text/plain', index.toString());
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                                    if (!isNaN(dragIndex) && dragIndex !== index) {
                                      handleOcrReorder(dragIndex, index);
                                    }
                                  }}
                                  sx={{
                                    opacity: item.selected ? 1 : 0.45,
                                    // Amber wash: price missing — needs attention before import.
                                    background: item.price <= 0
                                      ? (isDark ? 'rgba(245,158,11,0.09)' : 'rgba(245,158,11,0.1)')
                                      : 'transparent',
                                  }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={item.selected}
                                      onChange={(e: any) => {
                                        const newResults = [...ocrResults];
                                        newResults[index].selected = e.target.checked;
                                        setOcrResults(newResults);
                                      }}
                                      color="primary" size="small" sx={{ p: 0.4 }}
                                    />
                                  </TableCell>

                                  <TableCell sx={{ px: 0.5 }}>
                                    <Box
                                      title="Drag to reorder"
                                      sx={{
                                        cursor: 'grab', display: 'flex', alignItems: 'center', gap: 0.3,
                                        color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                                        fontSize: 13, userSelect: 'none', '&:active': { cursor: 'grabbing' },
                                      }}
                                    >
                                      ⋮⋮
                                      <Typography component="span" sx={{ fontSize: '0.66rem', fontWeight: 600 }}>{index + 1}</Typography>
                                    </Box>
                                  </TableCell>

                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                      <TextField
                                        fullWidth variant="standard" value={item.name}
                                        onChange={(e: any) => {
                                          const newResults = [...ocrResults];
                                          newResults[index].name = e.target.value;
                                          setOcrResults(newResults);
                                        }}
                                        sx={cellInputSx}
                                      />
                                      {item.isSpicy && (
                                        <Box title="Spicy" sx={{ fontSize: 13, flexShrink: 0 }}>🌶️</Box>
                                      )}
                                      {item.sizeName && (
                                        <Chip label={item.sizeName} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, flexShrink: 0 }} />
                                      )}
                                    </Box>
                                  </TableCell>

                                  <TableCell>
                                    <TextField
                                      fullWidth variant="standard" value={item.category}
                                      onChange={(e: any) => {
                                        const newResults = [...ocrResults];
                                        newResults[index].category = e.target.value;
                                        setOcrResults(newResults);
                                      }}
                                      sx={cellInputSx}
                                    />
                                  </TableCell>

                                  <TableCell align="right">
                                    <TextField
                                      variant="standard" type="number" value={item.price || ''}
                                      placeholder="—"
                                      onChange={(e: any) => {
                                        const newResults = [...ocrResults];
                                        newResults[index].price = parseFloat(e.target.value) || 0;
                                        setOcrResults(newResults);
                                      }}
                                      sx={{
                                        ...cellInputSx, width: 60,
                                        '& input': {
                                          textAlign: 'right', fontWeight: 700, fontSize: '0.82rem',
                                          color: item.price <= 0 ? '#f59e0b' : 'inherit',
                                        },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell align="center">
                                    <Box
                                      title={item.isVegetarian ? 'Vegetarian — click to toggle' : 'Non-Vegetarian — click to toggle'}
                                      onClick={() => {
                                        const newResults = [...ocrResults];
                                        newResults[index].isVegetarian = !newResults[index].isVegetarian;
                                        setOcrResults(newResults);
                                      }}
                                      sx={{
                                        cursor: 'pointer', width: 16, height: 16, mx: 'auto',
                                        border: `2px solid ${item.isVegetarian ? '#2e7d32' : '#c62828'}`,
                                        borderRadius: '3px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}
                                    >
                                      <Box sx={{
                                        width: 8, height: 8,
                                        borderRadius: item.isVegetarian ? '50%' : '0%',
                                        backgroundColor: item.isVegetarian ? '#2e7d32' : '#c62828',
                                      }} />
                                    </Box>
                                  </TableCell>

                                  <TableCell>
                                    <TextField
                                      fullWidth variant="standard" value={item.tag || ''}
                                      placeholder="—"
                                      onChange={(e: any) => {
                                        const newResults = [...ocrResults];
                                        newResults[index].tag = e.target.value || null;
                                        setOcrResults(newResults);
                                      }}
                                      sx={{
                                        ...cellInputSx,
                                        '& input': {
                                          fontSize: '0.72rem', fontWeight: 700,
                                          color: item.tag ? '#7c3aed' : 'inherit',
                                        },
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </MuiTable>
                        );
                      })()
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {ocrFile && !ocrScanning && (
            <Button
              onClick={() => handleOcrFileChange(ocrFile)}
              disabled={ocrScanning}
              startIcon={<Refresh />}
              sx={{ borderRadius: '10px', mr: 'auto', color: colors.accent.indigo }}
            >
              Rescan Image
            </Button>
          )}

          <Button
            onClick={() => {
              setOcrDialogOpen(false);
              setOcrFile(null);
              setOcrImagePreview(null);
              setOcrResults([]);
            }}
            disabled={ocrScanning}
            sx={{ borderRadius: '10px' }}
          >
            Cancel
          </Button>

          {ocrFile && ocrResults.length > 0 && (
            <Button
              onClick={handleOcrImport}
              variant="contained"
              disabled={ocrScanning}
              startIcon={ocrScanning ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
              sx={{
                borderRadius: '10px',
                background: colors.accent.gradient,
                boxShadow: shadows.glow.primary,
                '&:hover': { boxShadow: shadows.glow.primaryLg },
              }}
            >
              {ocrScanning ? 'Importing...' : `Import Selected ${ocrResults.filter(i => i.selected).length} Items`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Import Menu via Excel/CSV Dialog */}
      <Dialog
        open={excelDialogOpen}
        onClose={() => {
          if (!excelLoading) {
            setExcelDialogOpen(false);
            setExcelFile(null);
            setExcelResults([]);
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.8, pb: 1, fontFamily: 'Space Grotesk' }}>
          <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}>
            <CloudUpload sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Import Menu via Excel/CSV</Typography>
            <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>Upload a spreadsheet to import categories & menu items instantly</Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const csvContent = "data:text/csv;charset=utf-8,Category,Item Name,Price,Description\r\nStarter,Paneer Tikka,180,Clay oven cooked cottage cheese\r\nStarter,Crispy Corn,140,Golden fried corn tossed with spices\r\nMain Course,Dal Makhani,220,Slow cooked black lentils\r\nMain Course,Paneer Butter Masala,260,Cottage cheese in rich butter gravy\r\nDessert,Gulab Jamun,80,Golden fried sweet milk balls\r\nBeverages,Cold Coffee,110,Rich blended coffee served chilled";
              const encodedUri = encodeURI(csvContent);
              const link = document.createElement("a");
              link.setAttribute("href", encodedUri);
              link.setAttribute("download", "menu_import_template.csv");
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            sx={{
              borderRadius: '8px',
              borderColor: '#10b981',
              color: '#10b981',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#059669',
                background: '#10b98112'
              }
            }}
          >
            Download Sample CSV
          </Button>
        </DialogTitle>
        
        <DialogContent dividers sx={{ borderTop: `1px solid ${isDark ? colors.dark.border : colors.light.border}`, borderBottom: `1px solid ${isDark ? colors.dark.border : colors.light.border}`, p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {!excelFile ? (
              <Box
                component="label"
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  minHeight: 220, borderRadius: '20px', cursor: 'pointer',
                  border: `2px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(16,185,129,0.3)'}`,
                  background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(16,185,129,0.02)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#10b981',
                    background: isDark ? 'rgba(16,185,129,0.04)' : 'rgba(16,185,129,0.04)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <input
                  type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                  onChange={(e: any) => {
                    const file = e.target.files?.[0];
                    if (file) handleExcelFileChange(file);
                  }}
                />
                <CloudUpload sx={{ fontSize: 52, color: '#10b981', mb: 1.8, opacity: 0.8 }} />
                <Typography sx={{ fontWeight: 700, color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, mb: 0.5 }}>
                  Drop your CSV or Excel file here, or browse
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                  Supports CSV, XLSX, XLS up to 10MB
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutline sx={{ color: '#10b981', fontSize: 18 }} />
                    Excel/CSV Parsed Items ({excelResults.length})
                  </Typography>
                  <Button 
                    variant="text" 
                    color="error" 
                    size="small" 
                    onClick={() => {
                      setExcelFile(null);
                      setExcelResults([]);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear File
                  </Button>
                </Box>

                <Box sx={{
                  maxHeight: 380, overflowY: 'auto', borderRadius: '12px',
                  border: `1px solid ${isDark ? colors.dark.border : colors.light.border}`,
                  background: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.4)',
                }}>
                  {excelLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1 }}>
                      <CircularProgress size={32} sx={{ color: '#10b981' }} />
                      <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        Reading spreadsheet rows...
                      </Typography>
                    </Box>
                  ) : excelResults.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
                        No items found. Make sure headers are correct.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {excelResults.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2,
                            borderRadius: '10px',
                            background: isDark ? 'rgba(255,255,255,0.02)' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
                            '&:hover': { background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(16,185,129,0.03)' }
                          }}
                        >
                          <Checkbox
                            checked={item.selected}
                            onChange={(e: any) => {
                              const newResults = [...excelResults];
                              newResults[index].selected = e.target.checked;
                              setExcelResults(newResults);
                            }}
                            color="success" size="small"
                            sx={{ p: 0.5 }}
                          />
                          
                          <TextField
                            size="small" label="Item Name" value={item.name}
                            onChange={(e: any) => {
                              const newResults = [...excelResults];
                              newResults[index].name = e.target.value;
                              setExcelResults(newResults);
                            }}
                            sx={{ flex: 1.8, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />

                          <TextField
                            size="small" label="Category" value={item.category}
                            onChange={(e: any) => {
                              const newResults = [...excelResults];
                              newResults[index].category = e.target.value;
                              setExcelResults(newResults);
                            }}
                            sx={{ flex: 1.2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />

                          <TextField
                            size="small" label="Price" type="number" value={item.price}
                            onChange={(e: any) => {
                              const newResults = [...excelResults];
                              newResults[index].price = parseFloat(e.target.value) || 0;
                              setExcelResults(newResults);
                            }}
                            sx={{ width: 95, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setExcelDialogOpen(false);
              setExcelFile(null);
              setExcelResults([]);
            }}
            disabled={excelLoading}
            sx={{ borderRadius: '10px' }}
          >
            Cancel
          </Button>

          {excelFile && excelResults.length > 0 && (
            <Button
              onClick={handleExcelImport}
              variant="contained"
              disabled={excelLoading}
              startIcon={excelLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
              sx={{
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                '&:hover': { boxShadow: '0 6px 16px rgba(16,185,129,0.45)' },
              }}
            >
              {excelLoading ? 'Importing...' : `Import Selected ${excelResults.filter(i => i.selected).length} Items`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Edit Menu Item — rendered via portal to bypass all stacking context issues */}
      {editItemDialogOpen && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          {/* Backdrop */}
          <div
            onClick={handleCloseEditItem}
            style={{
              position: 'absolute', inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
            }}
          />
          {/* Panel */}
          <div style={{
            position: 'relative', zIndex: 1,
            width: '100%', maxWidth: 560,
            maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            borderRadius: 20,
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            overflow: 'hidden',
          }}>
            {/* Title */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '20px 24px 16px',
              borderBottom: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
              flexShrink: 0,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: `${colors.accent.indigo}20`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: colors.accent.indigo, flexShrink: 0,
              }}>
                <Edit style={{ fontSize: 18 }} />
              </div>
              <span style={{
                fontWeight: 700, fontSize: 18,
                color: isDark ? colors.dark.textPrimary : colors.light.textPrimary,
                fontFamily: 'Space Grotesk, sans-serif',
              }}>Edit Menu Item</span>
              <div style={{ marginLeft: 'auto' }}>
                <IconButton size="small" onClick={handleCloseEditItem}>
                  <Close style={{ fontSize: 20 }} />
                </IconButton>
              </div>
            </div>
            {/* Scrollable content */}
            <div style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editingItem?.categoryId || ''}
                    onChange={(e) => setEditingItem(editingItem ? { ...editingItem, categoryId: e.target.value } : null)}
                    label="Category"
                    sx={{ borderRadius: '10px' }}
                  >
                    {menuCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Item Name"
                  fullWidth
                  value={editingItem?.name || ''}
                  onChange={(e) => setEditingItem(editingItem ? { ...editingItem, name: e.target.value } : null)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <TextField
                  label="Shortcut Name (optional)"
                  fullWidth
                  value={editingItem?.shortcutName || ''}
                  onChange={(e) => setEditingItem(editingItem ? { ...editingItem, shortcutName: e.target.value } : null)}
                  helperText="Short name shown on POS buttons (e.g. 'Veg Biryani' → 'V.Bir')"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <TextField
                  label="Description"
                  fullWidth multiline rows={2}
                  value={editingItem?.description || ''}
                  onChange={(e) => setEditingItem(editingItem ? { ...editingItem, description: e.target.value } : null)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Price (₹)" type="number" fullWidth
                    value={editingItem?.price || 0}
                    onChange={(e) => setEditingItem(editingItem ? { ...editingItem, price: parseFloat(e.target.value) || 0 } : null)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                  <TextField
                    label="Prep Time (min)" type="number" fullWidth
                    value={editingItem?.preparationTime || 15}
                    onChange={(e) => setEditingItem(editingItem ? { ...editingItem, preparationTime: parseInt(e.target.value) || 15 } : null)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Box>
                {renderSizesEditor(
                  Array.isArray(editingItem?.sizes) ? editingItem!.sizes! : [],
                  (sizes) => setEditingItem(editingItem ? { ...editingItem, sizes } : null)
                )}
                {/* Image upload */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button variant="outlined" component="label" sx={{ borderRadius: '10px' }}>
                    Upload Image
                    <input type="file" hidden accept="image/*" onChange={(e) => {
                      if (e.target.files && e.target.files[0]) setEditItemImageFile(e.target.files[0]);
                    }} />
                  </Button>
                  {editItemImageFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src={URL.createObjectURL(editItemImageFile)} alt="Preview" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }} />
                      <Typography variant="caption">{editItemImageFile.name}</Typography>
                    </Box>
                  )}
                  {!editItemImageFile && editingItem?.imageUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src={editingItem.imageUrl.startsWith('http') ? editingItem.imageUrl : `${IMAGE_BASE}${editingItem.imageUrl}`} alt="Existing" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }} />
                      <Typography variant="caption" sx={{ color: 'success.main' }}>Image exists</Typography>
                    </Box>
                  )}
                </Box>
                {/* Toggles */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Switch checked={editingItem?.isAvailable || false} onChange={(e) => setEditingItem(editingItem ? { ...editingItem, isAvailable: e.target.checked } : null)} color="primary" />}
                    label="Available"
                  />
                  <FormControlLabel
                    control={<Switch checked={editingItem?.isVegetarian || false} onChange={(e) => setEditingItem(editingItem ? { ...editingItem, isVegetarian: e.target.checked } : null)} color="success" />}
                    label="Veg"
                  />
                  <FormControlLabel
                    control={<Switch checked={editingItem?.isSpicy || false} onChange={(e) => setEditingItem(editingItem ? { ...editingItem, isSpicy: e.target.checked } : null)} color="error" />}
                    label="Spicy"
                  />
                </Box>
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingItem?.isHappyHourEnabled || false}
                      onChange={(e) => setEditingItem(editingItem ? { ...editingItem, isHappyHourEnabled: e.target.checked } : null)}
                      color="secondary"
                    />
                  }
                  label={<Typography sx={{ fontWeight: 600 }}>Enable Happy Hour Pricing</Typography>}
                />
                {editingItem?.isHappyHourEnabled && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: `1px solid ${colors.accent.indigo}20`, borderRadius: '12px', background: `${colors.accent.indigo}05` }}>
                    <TextField
                      label="Happy Hour Price (₹)" type="number" fullWidth size="small"
                      value={editingItem?.happyHourPrice || ''}
                      onChange={(e) => setEditingItem(editingItem ? { ...editingItem, happyHourPrice: parseFloat(e.target.value) || 0 } : null)}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField label="Start Time" type="time" fullWidth size="small" InputLabelProps={{ shrink: true }}
                        value={editingItem?.happyHourStart ? editingItem.happyHourStart.substring(0, 5) : '12:00'}
                        onChange={(e) => setEditingItem(editingItem ? { ...editingItem, happyHourStart: e.target.value } : null)}
                      />
                      <TextField label="End Time" type="time" fullWidth size="small" InputLabelProps={{ shrink: true }}
                        value={editingItem?.happyHourEnd ? editingItem.happyHourEnd.substring(0, 5) : '15:00'}
                        onChange={(e) => setEditingItem(editingItem ? { ...editingItem, happyHourEnd: e.target.value } : null)}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Active Days</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                        const daysArr = editingItem?.happyHourDays ? editingItem.happyHourDays.split(',') : [];
                        const active = daysArr.includes(day);
                        return (
                          <Chip key={day} label={day.substring(0,3)} size="small" clickable
                            color={active ? 'secondary' : 'default'} variant={active ? 'filled' : 'outlined'}
                            onClick={() => {
                              if (!editingItem) return;
                              const nextDays = active ? daysArr.filter(d => d !== day) : [...daysArr, day];
                              setEditingItem({ ...editingItem, happyHourDays: nextDays.join(',') });
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </div>
            {/* Actions */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 12,
              padding: '16px 24px',
              borderTop: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
              flexShrink: 0,
            }}>
              <Button onClick={handleCloseEditItem} sx={{ borderRadius: '10px' }}>Cancel</Button>
              <Button
                onClick={handleUpdateMenuItem}
                variant="contained"
                disabled={!editingItem?.name}
                sx={{
                  borderRadius: '10px',
                  background: colors.accent.gradient,
                  boxShadow: shadows.glow.primary,
                  '&:hover': { boxShadow: shadows.glow.primaryLg },
                }}
              >
                Update Item
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Menu Item Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
            boxShadow: shadows.glow.accent,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, fontFamily: 'Space Grotesk' }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '8px', background: `${colors.error.main}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.error.main }}>
            <Delete />
          </Box>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: isDark ? colors.dark.textPrimary : colors.light.textPrimary, mb: 1, fontWeight: 500 }}>
            Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? colors.dark.textMuted : colors.light.textMuted }}>
            This action is permanent and cannot be undone. All references in order history, combos, or categories will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }} 
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              if (itemToDelete) {
                await handleDeleteMenuItem(itemToDelete.id);
              }
              setDeleteConfirmOpen(false);
              setItemToDelete(null);
            }} 
            variant="contained" 
            color="error"
            sx={{ 
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(135deg, ${colors.error.main} 0%, ${colors.error.light} 100%)`,
              boxShadow: '0 4px 12px rgba(224,42,42,0.3)',
            }}
          >
            Delete Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configure Global Payment Gateway Dialog */}
      <Dialog open={globalGatewayDialogOpen} onClose={() => setGlobalGatewayDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
          },
        }}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Configure {globalGatewayForm.displayName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Display Name"
              fullWidth
              value={globalGatewayForm.displayName}
              onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, displayName: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            {globalGatewayForm.gatewayName === 'Razorpay' ? (
              <>
                <TextField
                  label="Key ID"
                  fullWidth
                  value={globalGatewayForm.apiKey}
                  onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, apiKey: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <TextField
                  label="Key Secret"
                  type="password"
                  fullWidth
                  value={globalGatewayForm.apiSecret}
                  onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, apiSecret: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </>
            ) : (
              <>
                <TextField
                  label="Merchant ID"
                  fullWidth
                  value={globalGatewayForm.merchantId}
                  onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, merchantId: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <TextField
                  label="Salt Key"
                  type="password"
                  fullWidth
                  value={globalGatewayForm.apiSecret}
                  onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, apiSecret: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
                <TextField
                  label="Salt Index"
                  fullWidth
                  value={globalGatewayForm.saltIndex}
                  onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, saltIndex: e.target.value })}
                  placeholder="e.g. 1"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                />
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={globalGatewayForm.isTestMode}
                  onChange={(e) => setGlobalGatewayForm({ ...globalGatewayForm, isTestMode: e.target.checked })}
                  color="warning"
                />
              }
              label="Test / Sandbox Mode"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setGlobalGatewayDialogOpen(false)} sx={{ borderRadius: '10px' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGlobalGatewayManual} sx={{ borderRadius: '10px', background: colors.accent.gradient }}>Save Configuration</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            borderRadius: '12px',
            background: isDark ? colors.dark.surface : colors.light.surface,
            border: `1px solid ${isDark ? colors.dark.borderLight : colors.light.borderLight}`,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
